import {initWorkspaceUI} from './workspace-ui.js';
import {compareRevisions,revisionParts} from '../lib/revision-order.js';
import {libraryItemsChanged} from '../lib/library-list-sync.mjs';
import {OverviewRfisUI} from './overview-rfis.js';
import {DocumentRfiSurface} from './document-rfi.js';
import {DocumentActivityUI} from './document-activity.js';
import {openProjectAudit} from './project-settings.js';
import {confirmLeaveMembers, openProjectMembers, releaseMembersGuard} from './project-members.js';
import {openProjectGroups} from './rfi-workflow.js';
import {initAuth,setUploadAuthHeaders} from './auth-ui.js';
await initAuth();
window.addEventListener('unhandledrejection',event=>{if(event.reason?.status){event.preventDefault();flashHint(event.reason.message);}});
import {LocationNavigation,catalogItems,folderPath,folderSubtree,renderLibraryTree} from './location-navigation.js';
import {CONVERTIBLE_KINDS,KIND_LABEL,isOfficePreviewable,isShareable,kindOfName,officePreviewLabel,opensInViewer} from '../lib/document-kinds.js';
import {renderSheetSet} from './sheet-set-preview.js';
import {openReferencePanel} from './reference-panel.js';
import {displayUrl} from '../lib/url-link.mjs';
import {LinkedViewer} from './linked-viewer.js';
import {mesh3dToBinary} from '../mesh3d-binary.mjs';
import {ReviewToolsUI} from './review-tools.js';
import {ArchivePanel} from './archive-panel.js';
import {initUploadReviewUI} from './upload-review-ui.js';
import {workflowFolderRestriction, allowsWorkflowFolder} from '../lib/upload-workflow-policy.js';
/**
 * v3 前端 —— 伺服器端解析（強制 ODA）＋ 圖庫。
 *
 * 與舊版的差別
 * ────────────
 * 1. **上傳的檔案會留在伺服器（圖庫）**，重新整理頁面或重啟服務都還在，
 *    不需要重傳、也不需要重轉 —— 轉檔結果也存在磁碟上。
 * 2. 上傳後**立刻開始背景轉檔**。文件工作區會持續核對清冊，轉檔狀態與他人新增的檔案會更新到清單上。
 * 3. 解析完全在伺服器端，瀏覽器只負責顯示：2D 是 SVG，3D 是 three.js（WebGL）。
 *
 * 重用的模組：lib/svg-viewer.js（SVG 縮放/平移）與 lib/viewer3d.js（three.js），
 * 兩者都與伺服器回傳的既有契約相容。
 *
 * 已知限制：ODA 的 SVG 依「顏色」分組、不含圖層/handle 資訊，所以圖層面板是
 * 「圖層資訊 + 依顏色切換」，不是逐圖層開關。面板上有明確標示。
 */
import { SvgViewer } from '../lib/svg-viewer.js';
import { AnnotationLayer, TYPE_LABEL } from '../lib/annotations.js';
import { NAV_KEYS, editableTarget } from '../lib/navigation-input.js';

/* ── 常數 ───────────────────────────────────────────────────── */
// 上傳／開檔等待：配合 10 GiB 上傳（相對舊 30 分放大 20 倍）。
const WAIT_TIMEOUT_MS = 10 * 60 * 60 * 1000;
const POLL_MS = 2500;
let libraryFetchedAt = 0;
let libraryJob = null;
let libraryAgain = null;

/* ── 狀態 ───────────────────────────────────────────────────── */
const state = {
  workspace: 'overview',
  libraryFilter: 'all',
  librarySearch: '',
  libraryShare: 'all',
  libraryKind: 'all',
  librarySort: { key: null, dir: 1 },
  convertFilter: 'queue',        // queue | ready | error | all
  adminPane: 'engine',           // engine | archive | accounts | mine | members | groups | projectAudit | systemAudit
  settingsEpoch: 0,
  renderedSettingsEpoch: 0,
  conversion: { queue: [], running: [] },
  selected: new Set(),           // 勾選的檔案 id（批次操作）
  selectedFolders: new Set(),    // 勾選的子目錄 id
  selectedProjects: new Set(),   // 勾選的頂層目錄（專案）id
  projects: [],
  selectedProject: 'all',
  folders: [],
  selectedFolder: null,
  zipMode: localStorage.getItem('dwgv3.zipMode') === 'package' ? 'package' : 'folders',   // 目前所在資料夾；null＝專案根目錄。搜尋時涵蓋其子樹
  docMode: null,          // 'pdf'｜'image'＝目前 Viewer 顯示文件預覽，不是 2D／3D
  reviewPanel: 'tools',
  items: [],
  stats: null,
  activeId: null,
  activeDetail: null,            // 歷史版不在現行清單，仍保留檢視中的版次。
  engine: 'oda',         // 正式路徑強制 ODA
  engines: null,         // /api/engines
  mode3d: false,
  has2d: false,           // 目前這個檔案有沒有可顯示的 2D（決定「回 2D」能不能按）
  viewer3d: null,
  viewer3dMod: null,
  hiddenColors: new Set(),
  // 畫布是否翻轉（黑底白線）。由「引擎底色 + 主題」決定，可被 ◑ 手動覆寫。
  viewerBackground: localStorage.getItem('dcms.viewerBackground') === 'dark' ? 'dark' : 'light',
  invert: false,
  invertOverride: null,   // null = 跟隨檢視背景；true/false = 使用者手動反白
  cadLayouts: [],
  cadLayout: null,
  cadSvg: null,
  cadLayoutChoices: new Map(),
  currentEngine: null,    // 目前畫面上是哪顆引擎（決定底色基準）
  pollTimer: null,
  // 這個檔案的提醒資訊（單一出口，見 addNotice）
  notices: [],
  // 標註（圖形溝通）
  ann: null,          // AnnotationLayer 實例（只有一個，換宿主不重建）
  annOther: [],       // 另一個座標系（3D 畫面 vs 2D 圖面）的標註，存檔時要一起送
  annMode: false,
  annTool: null,
  annDirty: false,
  annSaveTimer: null,
  annViewObs: null,
  annStaleKey: '',
  viewpoints: [],
};

/**
 * 開啟請求的序號。
 *
 * openItem() 是 async，中間會 await 兩次 fetch。如果使用者連續開兩個檔案
 * （或快速切換引擎），**先發出的慢請求可能後回來**，把已經畫好的新內容
 * 覆蓋成舊的 —— 症狀就是「標題是新的、畫面是上一個檔案的」。
 * 每次開啟遞增序號，回應回來時比對，過期就整包丟棄。
 */
let openSeq = 0;

/** enter3D() 的序號，用途同 openSeq：丟棄過期的 3D 回應。 */
let meshSeq = 0;

/**
 * 進行中請求的取消器。
 *
 * **為什麼不能只靠序號**：序號只丟棄「回應」，請求本身還掛在那裡。檔案還沒轉好
 * 時，伺服器的 `/svg` 會一直等到轉完（最多 20 分鐘），而瀏覽器對同一個 origin
 * 只開約 6 條連線 —— 使用者開了 A 再開 B，A 那兩條請求就會繼續佔著連線，B 的
 * `/svg`、`/drawing` 反而排不到，症狀就是「開 B 一直卡在載入中」。
 *
 * 這跟縮圖佔滿連線池是同一類問題（見 renderList 的 THUMB_CONCURRENCY），
 * 所以換檔案／關視窗／離開 3D 時要主動 abort，而不是放著等逾時。
 * 逾時也一併由這裡管，取代原本每個 fetch 各自掛的 AbortSignal.timeout。
 */
let openCtrl = null;
let meshCtrl = null;

/** 開一個可取消、有逾時的請求。同一類的上一個會先被取消。 */
function beginRequest(kind) {
  abortRequest(kind);
  const ac = new AbortController();
  const timer = setTimeout(
    () => ac.abort(new DOMException('request timeout', 'TimeoutError')), WAIT_TIMEOUT_MS);
  const ctrl = { ac, timer };
  if (kind === 'mesh') meshCtrl = ctrl; else openCtrl = ctrl;
  return ctrl;
}

/** 請求結束（成功或失敗）：只清計時器。傳 ctrl 是為了不誤清後繼請求的計時器。 */
function endRequest(kind, ctrl) {
  if ((kind === 'mesh' ? meshCtrl : openCtrl) !== ctrl) return;
  clearTimeout(ctrl.timer);
  if (kind === 'mesh') meshCtrl = null; else openCtrl = null;
}

/** 主動取消進行中的請求。 */
function abortRequest(kind) {
  const ctrl = kind === 'mesh' ? meshCtrl : openCtrl;
  if (kind === 'mesh') meshCtrl = null; else openCtrl = null;
  if (!ctrl) return;
  clearTimeout(ctrl.timer);
  try { ctrl.ac.abort(); } catch { /* 已經結束 */ }
}

const $ = (sel) => document.querySelector(sel);
const $$ = (sel) => [...document.querySelectorAll(sel)];
const documentActivityUI=new DocumentActivityUI();
const overviewRfisUI=new OverviewRfisUI({openFile:async id=>{
  const response=await fetch(`/api/library/${encodeURIComponent(id)}`,{cache:'no-store'}),data=await response.json();
  if(!response.ok||!data.item)throw new Error(data.error||'無法取得檔案');
  await openItem(data.item);
}});

const viewer = new SvgViewer($('#canvas'));
let linkedViewer=null;
let archivePanel=null;
let locationNavigation=null;
let pdfViewer=null;
let docPreviewLabel='PDF';
let imageViewer=null;
let documentRfi=null;
const isDocPreview = () => state.docMode === 'pdf' || state.docMode === 'image';
const isPageRfi = () => isDocPreview() || (!state.mode3d && state.has2d);
/** 目錄樹展開狀態；空集合＝預設全部收攏。 */
const folderExpanded = new Set();
let revealedAssigned = false;
/** 只有一個授權專案時直接選取，目錄樹維持收合。 */
function revealAssignedDirectories() {
  if (window.dcmsAuth?.admin || revealedAssigned || !state.projects.length) return;
  if (state.projects.length === 1 && ['all', 'unassigned'].includes(state.selectedProject)) {
    state.selectedProject = state.projects[0].id;
    state.selectedFolder = null;
  }
  revealedAssigned = true;
}
function expandFolderPath(projectId, folderId) {
  if (projectId && !['all', 'unassigned'].includes(projectId)) folderExpanded.add(`p:${projectId}`);
  for (let f = state.folders.find(x => x.id === folderId); f; f = state.folders.find(x => x.id === f.parentId)) {
    folderExpanded.add(f.id);
    if (f.projectId) folderExpanded.add(`p:${f.projectId}`);
  }
}

/** 上傳改為 popup：文件庫清冊留在背景，對話框預設帶目前目錄。 */
function openUploadDialog() {
  renderProjects();
  const dialog = $('#uploadDialog');
  if (dialog && !dialog.open) dialog.showModal();
  window.dcmsAuth?.refresh?.().then(async()=>{await refreshLibrary({silent:true});if(dialog?.open)renderProjects();}).catch(error=>flashHint(error.message));
}
function closeUploadDialog() {
  const dialog = $('#uploadDialog');
  if (dialog?.open) dialog.close();
}

/* Persistent workspace navigation; drawings live in the right content area. */
function showWorkspace(name) {
  if (name !== 'viewer') exitViewerMax();
  // 專案選擇改由目錄樹操作，側欄不再顯示「專案」下拉。
  $('#sidebarProjectContext').hidden = true;
  if (linkedViewer && name !== 'viewer') linkedViewer.cancel();
  state.workspace = name;
  document.querySelector('.app-shell').dataset.workspace = name;
  documentActivityUI.setActive(name==='overview');
  overviewRfisUI.setActive(name==='overview');
  // 上傳以對話框呈現，內容頁仍顯示文件庫。
  const page = name === 'upload' ? 'library' : name;
  $('.page').hidden = page === 'viewer';
  $('#modal').hidden = page !== 'viewer';
  const admin = !!window.dcmsAuth?.admin;
  const adminPane = state.adminPane || 'engine';
  if (name === 'engine' && admin && adminPane === 'engine') void loadEngines();
  if (name === 'engine' && state.renderedSettingsEpoch !== state.settingsEpoch) {
    state.renderedSettingsEpoch = state.settingsEpoch;
    const projectId = $('#settingsProjectSelect')?.value;
    const fail = (error) => flashHint(error.message);
    if (adminPane === 'mine') void window.dcmsAuth?.openAccount?.();
    else if (adminPane === 'accounts') void window.dcmsAuth?.openAccounts?.();
    else if (adminPane === 'systemAudit') void window.dcmsAuth?.openAudit?.();
    else if (adminPane === 'members') void openProjectMembers(projectId).catch(fail);
    else if (adminPane === 'groups') void openProjectGroups(projectId, async () => {}, {page: true}).catch(fail);
    else if (adminPane === 'projectAudit') void openProjectAudit(projectId).catch(fail);
  }
  for (const section of $$('[data-page]')) {
    const onPage = section.dataset.page === page;
    const adminOnly = section.hasAttribute('data-admin-only');
    const selfOnly = section.hasAttribute('data-self-only');
    const pane = section.dataset.adminPane;
    section.hidden = !onPage || (adminOnly && !admin) || (selfOnly && (admin || adminPane !== 'engine')) || (Boolean(pane) && pane !== adminPane);
  }
  if (name === 'engine' && admin && adminPane === 'uploadReview') document.dispatchEvent(new Event('dcms-upload-review-open'));
  if (name === 'engine' && admin && adminPane === 'uploadWorkflows') document.dispatchEvent(new Event('dcms-upload-workflows-open'));
  if (name === 'uploadRequests') document.dispatchEvent(new Event('dcms-upload-requests-open'));
  const secondary = name === 'upload' ? 'library' : name;
  const primary = name === 'upload' ? 'library' : name;
  for (const pane of $$('[data-secondary]')) pane.hidden = pane.dataset.secondary !== secondary;
  for (const button of $$('.primary-nav [data-workspace]')) {
    const active = button.dataset.workspace === primary;
    button.classList.toggle('is-active', active);
    button.setAttribute('aria-pressed', String(active));
  }
  for (const button of $$('.secondary-nav [data-workspace]')) {
    const active = button.dataset.workspace === name;
    button.classList.toggle('is-active', active);
    button.setAttribute('aria-pressed', String(active));
  }
  $('#workspaceContext').textContent = {
    overview: '總覽', library: '文件', upload: '文件 · 上傳', uploadRequests: '審核單', viewer: '檢視', convert: '轉檔', engine: '工作區設定',
  }[name];
  $('#mTitle').textContent = name === 'viewer' ? docTitle(activeItem())
    : { overview: '專案協作總覽', library: projectTitle(), upload: '上傳圖面、模型、PDF 與文件', uploadRequests: '我的上傳審核單', convert: '轉檔排程', engine: settingsTitle() }[name];
  $('#mBadges').hidden = name !== 'viewer';
  $('#mClose').hidden = name !== 'viewer';
  $('[data-workspace="viewer"]').disabled = false;
  if (name === 'upload') openUploadDialog();
  else closeUploadDialog();
  if (name === 'viewer') locationNavigation?.ensureProject();
  if (name === 'engine' && window.dcmsAuth?.admin && state.adminPane === 'archive') archivePanel?.load();
  const settingsButtons = {
    engine: 'engineJump', archive: 'archiveJump', accounts: 'accountsJump', mine: 'myAccountJump',
    members: 'settingsProjectMembers', groups: 'settingsProjectGroups', projectAudit: 'settingsProjectAudit', systemAudit: 'auditJump',
    projectFolders: 'projectFoldersJump', uploadReview: 'uploadReviewJump', uploadWorkflows: 'uploadWorkflowsJump',
  };
  const activeSettings = settingsButtons[state.adminPane || 'engine'];
  for (const id of Object.values(settingsButtons)) {
    const button = $(`#${id}`);
    if (!button) continue;
    const on = id === activeSettings;
    button.classList.toggle('is-active', on);
    button.setAttribute('aria-pressed', String(on));
  }
  locationNavigation?.sync();
  if (state.viewer3d) {
    if (name === 'viewer' && state.mode3d) { state.viewer3d.resize(); state.viewer3d.start(); }
    else state.viewer3d.stop();
  }
  if (name === 'viewer' && !state.mode3d && !state.docMode && viewer.svg) viewer.fitContent();
  if (name === 'convert') {
    if (!state.convertFilterChosen) state.convertFilter = defaultConvertFilter();
    renderConvertBoard();
  }
  if (libraryLiveWorkspace() || libraryBusy()) ensureLibraryLive();
  else clearTimeout(state.pollTimer);
}

function settingsTitle() {
  const admin = !!window.dcmsAuth?.admin;
  return {
    engine: admin ? '轉檔引擎與圖庫' : '設定',
    archive: '封存匣',
    uploadReview: '上傳審核',
    uploadWorkflows: '上傳流程',
    accounts: '帳號與權限',
    mine: '我的帳號',
    members: admin ? '成員管理' : '成員清單',
    groups: '專案釋疑群組',
    projectAudit: '操作紀錄',
    systemAudit: '稽核紀錄',
    projectFolders: '建立專案資料夾',
  }[state.adminPane || 'engine'] || '設定';
}

function showSettingsPane(pane) {
  if ((state.adminPane || 'engine') === 'members' && !confirmLeaveMembers()) return;
  if ((state.adminPane || 'engine') === 'members') releaseMembersGuard();
  state.adminPane = pane;
  state.settingsEpoch += 1;
  showWorkspace('engine');
}

/* 次功能列寬度：預設 15%，拖曳 15%–40%；「<」縮回 15%、「>」放到 40%。 */
const SIDEBAR_PCT_DEFAULT = 15, SIDEBAR_PCT_MIN = 15, SIDEBAR_PCT_MAX = 40, SIDEBAR_PCT_KEY = 'dwgv3.sidebarPct';
function sidebarPct() {
  const raw = getComputedStyle(document.querySelector('.app-shell')).getPropertyValue('--sidebar-pct').trim();
  const n = parseFloat(raw);
  return Number.isFinite(n) ? n : SIDEBAR_PCT_DEFAULT;
}
function setSidebarPct(pct, { persist = true } = {}) {
  const n = Math.min(SIDEBAR_PCT_MAX, Math.max(SIDEBAR_PCT_MIN, Math.round(Number(pct) * 10) / 10));
  document.querySelector('.app-shell').style.setProperty('--sidebar-pct', `${n}%`);
  if (persist) localStorage.setItem(SIDEBAR_PCT_KEY, String(n));
  const atMin = Math.abs(n - SIDEBAR_PCT_MIN) < 0.05;
  const atMax = Math.abs(n - SIDEBAR_PCT_MAX) < 0.05;
  const shrink = $('#sidebarShrink');
  if (shrink) {
    shrink.disabled = atMin;
    shrink.title = atMin ? '已是最小寬度（15%）' : '縮至預設寬度（15%）';
  }
  const expand = $('#sidebarExpand');
  if (expand) {
    expand.disabled = atMax;
    expand.title = atMax ? '已是最大寬度（40%）' : '放至最大寬度（40%）';
  }
  requestAnimationFrame(() => { state.viewer3d?.resize(); linkedViewer?.draw?.(); });
  return n;
}
{
  const saved = parseFloat(localStorage.getItem(SIDEBAR_PCT_KEY) || '');
  setSidebarPct(Number.isFinite(saved) ? saved : SIDEBAR_PCT_DEFAULT, { persist: false });
  $('#sidebarShrink')?.addEventListener('click', () => setSidebarPct(SIDEBAR_PCT_MIN));
  $('#sidebarExpand')?.addEventListener('click', () => setSidebarPct(SIDEBAR_PCT_MAX));
  const handle = $('#sidebarResize');
  handle?.addEventListener('pointerdown', e => {
    if (e.button !== 0) return;
    e.preventDefault();
    const shell = document.querySelector('.app-shell');
    if (shell.classList.contains('compact-sidebar')) return;
    handle.setPointerCapture(e.pointerId);
    shell.classList.add('is-resizing-sidebar');
    const onMove = ev => {
      const rect = shell.getBoundingClientRect();
      const rail = document.querySelector('.primary-nav').getBoundingClientRect().width;
      const pct = ((ev.clientX - rect.left - rail) / rect.width) * 100;
      setSidebarPct(pct);
    };
    const onUp = () => {
      shell.classList.remove('is-resizing-sidebar');
      handle.releasePointerCapture(e.pointerId);
      handle.removeEventListener('pointermove', onMove);
      handle.removeEventListener('pointerup', onUp);
      handle.removeEventListener('pointercancel', onUp);
    };
    handle.addEventListener('pointermove', onMove);
    handle.addEventListener('pointerup', onUp);
    handle.addEventListener('pointercancel', onUp);
  });
}

/* 次功能列收合。1024px 以下改為覆蓋在操作區上的抽屜：進入窄版時先收起，點操作區或開檔後自動收起。 */
const narrowLayout = window.matchMedia('(max-width: 1024px)');
function setSidebarCollapsed(collapsed) {
  const shell = document.querySelector('.app-shell');
  shell.classList.toggle('compact-sidebar', collapsed);
  const button = $('#compactSidebar');
  button.setAttribute('aria-expanded', String(!collapsed));
  const action = collapsed ? '展開側欄' : '收合側欄';
  button.setAttribute('aria-label', action);
  button.title = action;
  button.querySelector('span').textContent = collapsed ? '展開' : '收合';
  button.querySelector('path').setAttribute('d', collapsed
    ? 'M3 4h18v16H3zM9 4v16M12 9l3 3-3 3'
    : 'M3 4h18v16H3zM9 4v16M15 9l-3 3 3 3');
  requestAnimationFrame(() => { state.viewer3d?.resize(); linkedViewer?.draw?.(); });
}
const collapseOverlaySidebar = () => {
  if (narrowLayout.matches && !document.querySelector('.app-shell').classList.contains('compact-sidebar')) setSidebarCollapsed(true);
};
$('#compactSidebar').addEventListener('click', () => {
  setSidebarCollapsed(!document.querySelector('.app-shell').classList.contains('compact-sidebar'));
});
setSidebarCollapsed(narrowLayout.matches);
narrowLayout.addEventListener('change', e => setSidebarCollapsed(e.matches));
$('.operation-area').addEventListener('pointerdown', collapseOverlaySidebar);
$('#locationItems').addEventListener('click', e => { if (e.target.closest('[data-location-item]')) collapseOverlaySidebar(); });

const STATUS_LABEL = { wip: '工作中', review: '審查中', approved: '核准', issued: '發行', void: '作廢', estimate_added: '估算新增', estimate_changed: '估算變更', construction_execution: '執行', construction_change_added: '設變新增', construction_change: '設變', b_approved: '核定版', b_unapproved: '未核定版' };

/** 檢視器標題：有圖號／圖名就用，否則用檔名。 */
function docTitle(item) {
  if (!item) return '檢視';
  const parts = [item.meta?.drawingNo, item.meta?.title].filter(Boolean);
  return parts.length ? parts.join(' ') : item.name;
}

/** 標題旁的圖說資料（版次、狀態、專業、樓層）；技術數字放在「資訊」彈出框。 */
function renderDocBadges(item) {
  const m = item?.meta ?? {};
  const bits = [];
  if (m.revision) bits.push(`<span class="badge">版次 ${revisionHtml(m.revision)}</span>`);
  if (item && item.isCurrent === false) bits.push('<span class="badge warn">非現行版</span>');
  else if (item?.isCurrent !== false && (item?.revisionCount ?? 1) > 1) bits.push('<span class="badge">現行版</span>');
  if (m.status) bits.push(`<span class="badge status-${m.status}">${STATUS_LABEL[m.status] ?? m.status}</span>`);
  if (m.discipline) bits.push(`<span class="badge">${escapeHtml(m.discipline)}</span>`);
  if (m.floor) bits.push(`<span class="badge">${escapeHtml(m.floor)}</span>`);
  if (item && (m.drawingNo || m.title)) bits.push(`<span class="badge badge-file" title="${escapeHtml(item.name)}">${escapeHtml(item.name)}</span>`);
  $('#mBadges').innerHTML = bits.join('');
}

const REVIEW_TITLES = { layers: '圖層／顏色', annotations: '標註／議題', scene: '顯示／導覽', viewpoints: '視點書籤', linked: '2D／3D 對位', measure2d: '2D 距離量測' };
const GROUP_TITLES = { v3dViewGroup: '3D 視角', v3dSectionGroup: '3D 剖切', v3dMeasureGroup: '3D 量測', v3dObjectsGroup: '構件／樓層', v3dNotesGroup: '釋疑' };
/** 會佔用畫面點選的 3D 工具，各自屬於哪個工具群組。 */
const PICK_MODE_GROUP = { pivot: 'v3dViewGroup', 'walk-start': 'v3dViewGroup', select: 'v3dObjectsGroup', note: 'v3dNotesGroup', measure: 'v3dMeasureGroup', angle: 'v3dMeasureGroup', calibrate: 'v3dMeasureGroup' };

const openGroupTab = () => $$('.v3d-group-tab').find(tab => tab.getAttribute('aria-expanded') === 'true') ?? null;

/** 展開一個 3D 工具群組（null＝全部收起）。群組與檢閱面板共用右側抽屜，一次只開一個。 */
function setGroupTab(tab) {
  const tools = isPageRfi()?documentRfi?.surfaceTools:state.viewer3d?.surfaceTools;
  if (isPageRfi()&&tools?.mode&&tab?.id!=='v3dNotesGroup')tools.setMode(null);
  if (tools?.mode && PICK_MODE_GROUP[tools.mode] && PICK_MODE_GROUP[tools.mode] !== tab?.id) {
    tools.setMode(null); $('#v3dPickStatus').textContent = '已結束點選';
  }
  for (const other of $$('.v3d-group-tab')) {
    const active = other === tab;
    other.setAttribute('aria-expanded', String(active));
    other.classList.toggle('on', active);
    $(`#${other.getAttribute('aria-controls')}`).hidden = !active;
  }
}

function syncDrawer() {
  const tab = openGroupTab();
  const open = state.reviewPanel !== 'tools' || Boolean(tab);
  const changed = $('#toolDrawer').hidden === open;
  $('#toolDrawer').hidden = !open;
  const rfi = state.reviewPanel === 'tools' && tab?.id === 'v3dNotesGroup';
  $('#toolDrawer').classList.toggle('is-rfi', rfi);
  $('#rfiResize').hidden = !rfi;
  if (rfi) setRfiWidth(parseFloat($('#toolDrawer').style.getPropertyValue('--rfi-width')) || 360);
  $('#reviewPanelTitle').textContent = state.reviewPanel !== 'tools' ? REVIEW_TITLES[state.reviewPanel] : tab ? GROUP_TITLES[tab.id] : '工具';
  $('#v3dMeasureGroup').classList.toggle('is-active', state.reviewPanel === 'measure2d');
  if (changed) requestAnimationFrame(() => state.viewer3d?.resize());
}

/** 開啟檢閱面板（'tools'＝不開面板）。 */
function showReviewPanel(name) {
  if (state.cadLayout && !state.mode3d && ['measure2d','linked','annotations'].includes(name)) { flashHint('請切回模型空間使用此工具'); return; }
  state.reviewPanel = name;
  if (name !== 'tools') setGroupTab(null);
  $('#modal').dataset.review = name;
  $('#layerPanel').hidden = !['layers', 'annotations'].includes(name);
  $('#sceneTools').hidden = name !== 'scene';
  $('#viewpointPanel').hidden = name !== 'viewpoints';
  $('#linkedTools').hidden = name !== 'linked';
  $('#drawingMeasurePanel').hidden = name !== 'measure2d';
  if(name==='linked')linkedViewer?.refreshChoices();
  for (const button of $$('button[data-review]')) {
    const active = button.dataset.review === name;
    button.classList.toggle('is-active', active);
    button.setAttribute('aria-pressed', String(active));
  }
  syncDrawer();
  syncToolbarState();
  sync3DTools();
}

for (const button of $$('[data-workspace]')) button.addEventListener('click', () => {
  showWorkspace(button.dataset.workspace);
  if (button.dataset.workspace === 'engine') {
    setSidebarCollapsed(false);
    $('#accountSettings button')?.focus();
  }
});
for (const button of $$('button[data-review]')) button.addEventListener('click', () => {
  showReviewPanel(state.reviewPanel === button.dataset.review ? 'tools' : button.dataset.review);
  if (state.reviewPanel === 'annotations') renderAnnList();
});
// The RFI drawer keeps its full height; resize its width from the left edge.
const rfiDrawer = $('#toolDrawer'), rfiResize = $('#rfiResize');
function setRfiWidth(width) {
  const available = rfiDrawer.parentElement.clientWidth;
  const max = Math.max(200, Math.min(800, available - 120));
  const value = Math.round(Math.max(Math.min(280, max), Math.min(max, width)));
  rfiDrawer.style.setProperty('--rfi-width', `${value}px`);
  rfiResize.setAttribute('aria-valuemin', String(Math.min(280, max)));
  rfiResize.setAttribute('aria-valuemax', String(max));
  rfiResize.setAttribute('aria-valuenow', String(value));
}
let rfiDrag = null;
rfiResize.addEventListener('pointerdown', e => {
  if (e.button !== 0) return;
  e.preventDefault();
  rfiDrag = { id: e.pointerId, x: e.clientX, width: rfiDrawer.getBoundingClientRect().width };
  rfiResize.setPointerCapture(e.pointerId);
});
rfiResize.addEventListener('pointermove', e => {
  if (rfiDrag?.id === e.pointerId) setRfiWidth(rfiDrag.width + rfiDrag.x - e.clientX);
});
rfiResize.addEventListener('lostpointercapture', () => { rfiDrag = null; });
for (const event of ['pointerup', 'pointercancel']) rfiResize.addEventListener(event, e => {
  if (rfiResize.hasPointerCapture(e.pointerId)) rfiResize.releasePointerCapture(e.pointerId);
  rfiDrag = null;
});
rfiResize.addEventListener('keydown', e => {
  if (!['ArrowLeft', 'ArrowRight', 'Home'].includes(e.key)) return;
  e.preventDefault();
  setRfiWidth(e.key === 'Home' ? 360 : rfiDrawer.getBoundingClientRect().width + (e.key === 'ArrowLeft' ? 20 : -20));
});
new ResizeObserver(() => state.viewer3d?.resize()).observe(rfiDrawer);
$('#drawerClose').addEventListener('click', () => { setGroupTab(null); showReviewPanel('tools'); });
$('#linkedMeasure2D').addEventListener('click', () => showReviewPanel('measure2d'));
$('#libraryFilterSelect')?.addEventListener('change', (e) => {
  state.libraryFilter = e.target.value || 'all';
  renderList();
});
$('#libraryKindSelect')?.addEventListener('change', (e) => {
  state.libraryKind = e.target.value || 'all';
  renderList();
});
if ($('#libraryFilterSelect')) $('#libraryFilterSelect').value = state.libraryFilter;
if ($('#libraryKindSelect')) $('#libraryKindSelect').value = state.libraryKind;
$('#librarySearch').addEventListener('input', e => { state.librarySearch = e.target.value.trim().toLowerCase(); renderList(); });
initWorkspaceUI({state, renderList, fitColumns: () => fitDirSummarySpan()});
showWorkspace('overview');

function projectTitle() {
  const project = state.projects.find(p => p.id === state.selectedProject);
  if (project) return state.selectedFolder ? `${project.name} / ${folderPath(state.folders, state.selectedFolder)}` : project.name;
  return state.selectedProject === 'unassigned' ? '未分類' : '全部';
}

/** 目前選取的頂層目錄（後端仍叫專案；「全部／未分類」時為 null）。 */
const currentProject = () => state.projects.find(p => p.id === state.selectedProject) ?? null;

function clearLibrarySelection() {
  state.selected.clear();
  state.selectedFolders.clear();
  state.selectedProjects.clear();
}
function librarySelectionCount() {
  return state.selected.size + state.selectedFolders.size + state.selectedProjects.size;
}
function selectBrowse({ project, folder = null } = {}) {
  expandFolderPath(project, folder);
  state.selectedProject = project;
  state.selectedFolder = folder;
  clearLibrarySelection();
  renderProjects();
  renderList();
}

function selectFolder(id) {
  if (!currentProject()) return;
  selectBrowse({ project: state.selectedProject, folder: id });
}

/** 目錄選單項目（含縮排），exclude 內的資料夾不列出（搬移時排除自己的子樹）。 */
function folderOptions(projectId, exclude = new Set()) {
  const out = [];
  const walk = (parentId, depth) => {
    for (const f of state.folders.filter(x => x.projectId === projectId && (x.parentId ?? null) === parentId)
      .sort((a, b) => a.name.localeCompare(b.name, 'zh-Hant', { numeric: true }))) {
      if (exclude.has(f.id)) continue;
      out.push(new Option(`${'　'.repeat(depth)}${f.name}`, f.id));
      walk(f.id, depth + 1);
    }
  };
  walk(null, 0);
  return out;
}

/** 跨頂層目錄的搬移目標：值為 `projectId|folderId`（`|`＝未分類；目錄搬移不提供未分類）。 */
function destinationOptions({ excludeFolders = new Set(), allowUnassigned = true } = {}) {
  const out = allowUnassigned ? [new Option('未分類', '|')] : [];
  for (const project of [...state.projects].sort((a, b) => a.name.localeCompare(b.name, 'zh-Hant', { numeric: true }))) {
    out.push(new Option(`${project.name}（專案第一層）`, `${project.id}|`));
    const walk = (parentId, depth, prefix) => {
      for (const f of state.folders.filter(x => x.projectId === project.id && (x.parentId ?? null) === parentId)
        .sort((a, b) => a.name.localeCompare(b.name, 'zh-Hant', { numeric: true }))) {
        if (excludeFolders.has(f.id)) continue;
        out.push(new Option(`${'　'.repeat(depth)}${prefix} / ${f.name}`, `${project.id}|${f.id}`));
        walk(f.id, depth + 1, `${prefix} / ${f.name}`);
      }
    };
    walk(null, 1, project.name);
  }
  return out;
}

function parseDestination(value) {
  const [projectId = '', folderId = ''] = String(value ?? '').split('|');
  return { projectId: projectId || null, folderId: folderId || null };
}

function renderFolders() {
  const project = currentProject();
  $('#folderHint').hidden = state.selectedProject !== 'all';
  $('#folderActions').hidden = false;
  $('#folderTree').hidden = false;
  const tree = $('#folderTree');
  const treeScroll = tree.scrollTop;
  renderLibraryTree(tree, {
    projects: state.projects, folders: state.folders,
    selectedProject: state.selectedProject, selectedFolder: state.selectedFolder,
    onSelect: selectBrowse, expanded: folderExpanded,
  });
  tree.scrollTop = treeScroll;
  const atFolder = Boolean(state.selectedFolder);
  const atTopDir = Boolean(project) && !atFolder;
  $('#folderRename').disabled = !(atFolder || atTopDir) || (atTopDir && !window.dcmsAuth?.admin);
  $('#folderMove').disabled = !atFolder;
  $('#folderDelete').disabled = !(atFolder || atTopDir) || (atTopDir && !window.dcmsAuth?.admin);
  const upload = $('#uploadFolder');
  const uploadProject = state.projects.find(p => p.id === $('#uploadProject').value);
  const uploadFolder = state.selectedProject === uploadProject?.id ? state.selectedFolder : upload.value;
  const workflowId = $('#uploadWorkflow').value;
  const isRestricted = Boolean(workflowFolderRestriction(workflowId));
  const options = uploadProject ? folderOptions(uploadProject.id).filter(option =>
    allowsWorkflowFolder(workflowId,uploadProject.id,option.value,state.folders)
    && (!isRestricted || window.dcmsAuth?.can('upload.request.create',{projectId:uploadProject.id,folderId:option.value}))) : [];
  const placeholder = new Option(isRestricted ? (options.length ? '請選擇目錄' : '沒有可選目錄') : '根目錄', '');
  if (isRestricted) placeholder.disabled = true;
  upload.replaceChildren(placeholder, ...options);
  upload.disabled = !uploadProject || (isRestricted && !options.length);
  // 依目錄展開需要頂層目錄；未分類／全部時 ZIP 只能當外參套件。
  const zip = $('#uploadZipMode');
  zip.disabled = !uploadProject;
  zip.value = uploadProject ? state.zipMode : 'package';
  upload.value = options.some(option => option.value === uploadFolder) ? uploadFolder : '';
}

function renderProjectSettingsMenu() {
  const select=$('#settingsProjectSelect'),previous=select.value;
  $('#settingsProjectMembers').textContent=window.dcmsAuth?.admin?'成員管理':'成員清單';
  const projects=state.projects.filter(p=>window.dcmsAuth?.can('project.members.read',{projectId:p.id,folderId:null}));
  $('#projectAdminSettings').hidden=!projects.length&&!window.dcmsAuth?.admin;
  for(const id of ['settingsProjectMembers','settingsProjectGroups','settingsProjectAudit'])$('#'+id).disabled=!projects.length;
  select.replaceChildren(...projects.map(p=>new Option(p.name,p.id)));
  if(!projects.length)select.add(new Option('尚無可管理的專案',''));
  select.value=projects.some(p=>p.id===previous)?previous:projects.some(p=>p.id===state.selectedProject)?state.selectedProject:projects[0]?.id||'';
}

function renderProjects() {
  renderProjectSettingsMenu();
  revealAssignedDirectories();
  if (!['all', 'unassigned'].includes(state.selectedProject) && !state.projects.some(p => p.id === state.selectedProject)) state.selectedProject = 'all';
  if (state.selectedFolder && state.folders.find(f => f.id === state.selectedFolder)?.projectId !== state.selectedProject) state.selectedFolder = null;
  const select = $('#projectSelect');
  select.replaceChildren(new Option('全部', 'all'), new Option('未分類', 'unassigned'));
  const uploadSelect = $('#uploadProject');
  const lockedUploadProject = uploadSelect.disabled ? uploadSelect.value : null;
  uploadSelect.replaceChildren(new Option('未分類', ''));
  for (const project of state.projects) {
    select.add(new Option(`${project.name} (${project.itemCount})`, project.id));
    uploadSelect.add(new Option(project.name, project.id));
  }
  select.value = state.selectedProject;
  $('#projectSettings').hidden=!state.projects.some(p=>p.id===state.selectedProject)||!window.dcmsAuth?.can('project.members.read',{projectId:state.selectedProject,folderId:null});
  uploadSelect.value = lockedUploadProject && state.projects.some(p => p.id === lockedUploadProject)
    ? lockedUploadProject : ['all', 'unassigned'].includes(state.selectedProject) ? '' : state.selectedProject;
  const specific = state.projects.some(p => p.id === state.selectedProject);
  $('#projectDelete').disabled = !specific;
  $('#projectRename').disabled = !specific;
  renderLibraryPath(currentProject());
  if (state.workspace === 'library') $('#mTitle').textContent = projectTitle();
  renderFolders();
}

/** 選擇目標目錄。回傳 {projectId, folderId} 或 undefined（取消）。 */
function pickDestination(title, { excludeFolders = new Set(), currentProjectId = null, currentFolderId = null, allowUnassigned = true } = {}) {
  return new Promise(resolve => {
    const dialog = document.createElement('dialog');
    dialog.className = 'meta-dialog folder-dialog';
    dialog.innerHTML = `<form method="dialog"><h3></h3><label>目標目錄<select name="folder"></select></label>
      <div class="meta-actions"><button class="btn btn-sm" value="cancel">取消</button><button class="btn btn-sm btn-primary" value="ok">移動</button></div></form>`;
    dialog.querySelector('h3').textContent = title;
    const select = dialog.querySelector('select');
    select.append(...destinationOptions({ excludeFolders, allowUnassigned }));
    const currentValue = `${currentProjectId || ''}|${currentFolderId || ''}`;
    if ([...select.options].some(o => o.value === currentValue)) select.value = currentValue;
    dialog.addEventListener('close', () => {
      resolve(dialog.returnValue === 'ok' ? parseDestination(select.value) : undefined);
      dialog.remove();
    });
    document.body.append(dialog);
    dialog.showModal();
  });
}

async function addFolder() {
  // 「全部」時新增＝建立頂層目錄；其下則建立子目錄。
  if (state.selectedProject === 'all' || state.selectedProject === 'unassigned') {
    const name = prompt('新目錄名稱（最多 80 字）：');
    if (name === null) return;
    try {
      const result = await projectRequest('/api/library/projects', 'POST', { name });
      selectBrowse({ project: result.project.id, folder: null });
      await refreshLibrary();
      flashHint(`已新增目錄「${result.project.name}」`);
    } catch (e) { flashHint(e.message); }
    return;
  }
  const project = currentProject();
  if (!project) return;
  const parent = state.selectedFolder;
  const name = prompt(`在「${parent ? folderPath(state.folders, parent) : project.name}」下新增目錄（最多 80 字）：`);
  if (name === null) return;
  try {
    const result = await projectRequest('/api/library/folders', 'POST', { projectId: project.id, parentId: parent, name });
    if (parent) folderExpanded.add(parent);
    else folderExpanded.add(`p:${project.id}`);
    await refreshLibrary();
    flashHint(`已新增目錄「${result.folder.name}」`);
  } catch (e) { flashHint(e.message); }
}
async function renameFolder(folder) {
  const name = prompt('目錄新名稱（最多 80 字）：', folder.name);
  if (name === null) return;
  try {
    await projectRequest(`/api/library/folders/${folder.id}`, 'PATCH', { name });
    await refreshLibrary();
    flashHint('目錄名稱已更新');
  } catch (e) { flashHint(e.message); }
}
async function renameCurrentTopDir() {
  const project = currentProject();
  if (!project) return;
  const name = prompt('目錄新名稱（最多 80 字）：', project.name);
  if (name === null) return;
  try {
    await projectRequest(`/api/library/projects/${project.id}`, 'PATCH', { name });
    await refreshLibrary();
    flashHint('目錄名稱已更新');
  } catch (e) { flashHint(e.message); }
}
async function moveFolder(folder) {
  const target = await pickDestination(`搬移「${folder.name}」`, { excludeFolders: folderSubtree(state.folders, folder.id), currentProjectId: folder.projectId, currentFolderId: folder.parentId ?? null, allowUnassigned: false });
  if (target === undefined) return;
  if (target.projectId === folder.projectId && target.folderId === (folder.parentId ?? null)) { flashHint('目錄已在所選位置'); return; }
  try {
    await projectRequest(`/api/library/folders/${folder.id}`, 'PATCH', { projectId: target.projectId, parentId: target.folderId });
    await refreshLibrary();
    flashHint('目錄已搬移');
  } catch (e) { flashHint(e.message); }
}
async function deleteFolder(folder) {
  const subtree = folderSubtree(state.folders, folder.id);
  const files = state.items.filter((i) => subtree.has(i.folderId)).length;
  const children = Math.max(0, subtree.size - 1);
  const detail = [
    files ? `${files} 個檔案` : null,
    children ? `${children} 個子目錄` : null,
  ].filter(Boolean).join('、') || '（目錄為空）';
  if (!confirm(`永久刪除目錄「${folder.name}」？\n將一併刪除其中的 ${detail}，無法復原。`)) return;
  try {
    const result = await projectRequest(`/api/library/folders/${folder.id}?purge=1`, 'DELETE');
    if (state.selectedFolder === folder.id || subtree.has(state.selectedFolder)) {
      state.selectedFolder = folder.parentId ?? null;
    }
    await refreshLibrary();
    const bits = [];
    if (result.purged) bits.push(`${result.purged} 個檔案`);
    if (result.folders) bits.push(`${result.folders} 個目錄`);
    flashHint(`已永久刪除「${folder.name}」${bits.length ? `（${bits.join('、')}）` : ''}`);
  } catch (e) { flashHint(e.message); }
}
async function deleteTopDir(project) {
  if (!project) return;
  const files = state.items.filter((i) => i.projectId === project.id).length;
  const folders = state.folders.filter((f) => f.projectId === project.id).length;
  const detail = [
    files ? `${files} 個檔案` : null,
    folders ? `${folders} 個子目錄` : null,
  ].filter(Boolean).join('、') || '（目錄為空）';
  if (!confirm(`永久刪除目錄「${project.name}」？\n將一併刪除其中的 ${detail}，無法復原。`)) return;
  try {
    const result = await projectRequest(`/api/library/projects/${project.id}?purge=1`, 'DELETE');
    if (state.selectedProject === project.id) selectBrowse({ project: 'all', folder: null });
    await refreshLibrary();
    const bits = [];
    if (result.purged) bits.push(`${result.purged} 個檔案`);
    if (result.folders) bits.push(`${result.folders} 個目錄`);
    flashHint(`已永久刪除「${project.name}」${bits.length ? `（${bits.join('、')}）` : ''}`);
  } catch (e) { flashHint(e.message); }
}
async function deleteCurrentTopDir() {
  return deleteTopDir(currentProject());
}
async function moveItem(item) {
  const dest = await pickDestination(`搬移「${item.name}」`, {
    currentProjectId: item.projectId, currentFolderId: item.folderId,
  });
  if (dest === undefined) return;
  try {
    if ((item.projectId ?? null) !== dest.projectId) {
      await projectRequest(`/api/library/${item.id}/project`, 'PATCH', { projectId: dest.projectId });
    }
    if (dest.projectId) {
      await projectRequest(`/api/library/${item.id}/folder`, 'PATCH', { folderId: dest.folderId });
    }
    await refreshLibrary();
    flashHint('已搬移');
  } catch (e) { flashHint(e.message); }
}
const selectedFolderObject = () => state.folders.find(f => f.id === state.selectedFolder);
$('#folderAdd').addEventListener('click', addFolder);
$('#crumbFolderAdd').addEventListener('click', addFolder);
$('#crumbUpload')?.addEventListener('click', () => showWorkspace('upload'));
$('#crumbConvert')?.addEventListener('click', () => convertCurrentFolder());
for (const btn of $$('[data-convert-filter]')) {
  btn.addEventListener('click', () => {
    state.convertFilter = btn.dataset.convertFilter;
    state.convertFilterChosen = true;
    renderConvertBoard();
  });
}
$('#convertSummary')?.addEventListener('click', (e) => {
  const btn = e.target.closest('[data-summary-filter]');
  if (!btn) return;
  state.convertFilter = btn.dataset.summaryFilter;
  state.convertFilterChosen = true;
  renderConvertBoard();
});
$('#convertRetryAllFailed')?.addEventListener('click', () => retryAllFailedConverts());
$('#uploadDialogClose')?.addEventListener('click', () => showWorkspace('library'));
$('#uploadDialog')?.addEventListener('close', () => {
  if (state.workspace === 'upload') {
    state.workspace = 'library';
    for (const button of $$('.secondary-nav [data-workspace]')) {
      const active = button.dataset.workspace === 'library';
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-pressed', String(active));
    }
    $('#workspaceContext').textContent = '文件';
    $('#mTitle').textContent = projectTitle();
  }
});
$('#folderRename').addEventListener('click', () => {
  const f = selectedFolderObject();
  if (f) renameFolder(f);
  else if (currentProject()) renameCurrentTopDir();
});
$('#folderMove').addEventListener('click', () => { const f = selectedFolderObject(); if (f) moveFolder(f); });
$('#folderDelete').addEventListener('click', () => {
  const f = selectedFolderObject();
  if (f) deleteFolder(f);
  else if (currentProject()) deleteCurrentTopDir();
});

async function projectRequest(url, method, data) {
  const response = await fetch(url, { method, headers: { 'Content-Type': 'application/json' },
    ...(data ? { body: JSON.stringify(data) } : {}) });
  const result = await response.json();
  if (!response.ok) throw new Error(result.error || '專案操作失敗');
  return result;
}

$('#settingsProjectMembers').addEventListener('click',()=>showSettingsPane('members'));
$('#settingsProjectGroups').addEventListener('click',()=>showSettingsPane('groups'));
$('#settingsProjectAudit').addEventListener('click',()=>showSettingsPane('projectAudit'));
$('#settingsProjectSelect').addEventListener('change',()=>{if(['members','groups','projectAudit'].includes(state.adminPane))showSettingsPane(state.adminPane);});
function openManagedProject(projectId){if(!projectId)return;const select=$('#settingsProjectSelect');if(select&&[...select.options].some(o=>o.value===projectId))select.value=projectId;setSidebarCollapsed(false);showSettingsPane('members');}
$('#locationProjectSettings').addEventListener('click',()=>openManagedProject(state.selectedProject));
$('#projectSettings').addEventListener('click',()=>openManagedProject(state.selectedProject));
document.addEventListener('dcms-settings-pane',event=>showSettingsPane(event.detail));
window.addEventListener('dcms-project-settings-changed',()=>refreshLibrary({silent:true}).catch(e=>flashHint(e.message)));
$('#projectSelect').addEventListener('change', e => { state.selectedProject = e.target.value; state.selectedFolder = null; renderProjects(); renderList(); });
$('#uploadProject').addEventListener('change', e => { state.selectedProject = e.target.value || 'unassigned'; state.selectedFolder = null; renderProjects(); renderList(); });
$('#uploadWorkflow').addEventListener('change', () => renderFolders());
document.addEventListener('dcms-upload-workflow-set', () => renderFolders());
$('#uploadZipMode').addEventListener('change', e => { state.zipMode = e.target.value; localStorage.setItem('dwgv3.zipMode', state.zipMode); });
$('#uploadFolder').addEventListener('change', e => {
  if (state.selectedProject !== $('#uploadProject').value) return;
  state.selectedFolder = e.target.value || null;
  renderProjects(); renderList();
});
$('#projectAdd').addEventListener('click', () => { state.selectedProject = 'all'; state.selectedFolder = null; addFolder(); });
$('#projectRename').addEventListener('click', () => renameCurrentTopDir());
$('#projectDelete').addEventListener('click', () => deleteCurrentTopDir());

/* ── 小工具 ─────────────────────────────────────────────────── */
const escapeHtml = (s) => String(s).replace(/[&<>"]/g, (c) =>
  ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
function revisionHtml(value) {
  const {number}=revisionParts(value);
  if(!number)return '<span class="muted">—</span>';
  return escapeHtml(number);
}

function fmtBytes(n) {
  if (!Number.isFinite(n)) return '—';
  if (n < 1024) return `${n} B`;
  if (n < 1048576) return `${(n / 1024).toFixed(1)} KiB`;
  if (n < 1073741824) return `${(n / 1048576).toFixed(1)} MiB`;
  return `${(n / 1073741824).toFixed(2)} GiB`;
}

function fmtDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString('sv-SE');
}

function fmtAddedTime(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const parts = Object.fromEntries(new Intl.DateTimeFormat('en-GB', {timeZone:'Asia/Taipei',year:'numeric',month:'2-digit',day:'2-digit',hour:'2-digit',minute:'2-digit',second:'2-digit',hourCycle:'h23'}).formatToParts(d).map(p => [p.type,p.value]));
  return `${parts.year}/${parts.month}/${parts.day} ${parts.hour}:${parts.minute}:${parts.second}`;
}

function fmtAgo(iso) {
  if (!iso) return '';
  const s = Math.max(0, (Date.now() - Date.parse(iso)) / 1000);
  if (s < 60) return '剛剛';
  if (s < 3600) return `${Math.floor(s / 60)} 分前`;
  if (s < 86400) return `${Math.floor(s / 3600)} 小時前`;
  return `${Math.floor(s / 86400)} 天前`;
}

const engineLabel = (e) => (e === 'oda' ? 'ODA' : (e === 'libredwg' || e === 'server') ? 'LibreDWG' : (e || '?'));

let _flashTimer;
/** 在上傳區塊顯示一則提示（一段時間後自動消失）。 */
function flashHint(text) {
  const el = $('#workspaceToast');
  el.textContent = text;
  el.hidden = false;
  clearTimeout(_flashTimer);
  _flashTimer = setTimeout(() => { el.hidden = true; }, 12000);
}

/* ── 引擎資訊 ───────────────────────────────────────────────── */
async function loadEngines() {
  try {
    if(!window.dcmsAuth.can('system.status.read')||!window.dcmsAuth.can('library.usage.read')){state.engines=null;renderEngineUI();return;}
    const response=await fetch('/api/engines', { cache: 'no-store' });
    state.engines=response.ok?await response.json():null;
  } catch {
    state.engines = null;
  }
  renderEngineUI();
  renderDirectoryUsage();
}

function renderDirectoryUsage() {
  const body = $('#usageRows');
  const usage = state.engines?.library?.usage;
  body.replaceChildren();
  if (!usage) {
    const row = body.insertRow(); const cell = row.insertCell();
    cell.colSpan = 5; cell.textContent = '無法取得圖庫用量，請按「更新用量」重試。';
    $('#libCount').textContent = ''; return;
  }
  $('#libCount').textContent = `${usage.total.documents} 份文件 · ${fmtBytes(usage.total.bytes)}`;
  const append = (entry, depth = 0) => {
    const row = body.insertRow();
    for (const value of [entry.name, entry.documents, entry.revisions, entry.archived, fmtBytes(entry.bytes)]) row.insertCell().textContent = value;
    row.cells[0].style.paddingLeft = `${12 + depth * 20}px`;
    if (entry.type !== 'folder') row.classList.add('usage-summary');
  };
  const projects = usage.rows.filter(r => r.type === 'project').sort((a,b) => a.name.localeCompare(b.name, 'zh-Hant', {numeric:true}));
  for (const project of projects) append(project);
  if (!projects.length) {
    const cell = body.insertRow().insertCell();
    cell.colSpan = 5; cell.textContent = '尚無專案目錄。';
  }
}

$('#usageRefresh').addEventListener('click', () => loadEngines());

function renderEngineUI() {
  const e = state.engines;
  const oda = e?.engines?.oda;
  const statusEl = $('#engineStatus');
  const note = $('#engineNote');

  if (!e) {
    statusEl.textContent = '無法取得引擎資訊';
    statusEl.className = 'engine-status bad';
    return;
  }

  if (oda?.available) {
    // 真正的授權到期日在 license.trial（ODA API 查到）。
    // license.sessionToken 只是短命登入 token，會自動輪替 —— 別拿它當授權效期。
    //
    // 注意：available 只代表「工具鏈檔案齊全」，**不代表 token 可用**。
    // token 過期或失效時轉檔會失敗，所以這裡要老實說是什麼狀態。
    const trial = oda.license?.trial;
    const tk = oda.license?.sessionToken;
    if (trial?.active) {
      statusEl.textContent = `ODA 可用 · 試用授權剩 ${trial.daysLeft} 天（至 ${fmtDate(trial.expires)}）`;
      statusEl.className = 'engine-status ok';
    } else if (trial && trial.active === false) {
      statusEl.textContent = `ODA 試用已到期（${fmtDate(trial.expires)}）`;
      statusEl.className = 'engine-status bad';
    } else if (tk?.expired) {
      statusEl.textContent = 'ODA 登入 token 已過期，轉檔可能會失敗';
      statusEl.className = 'engine-status bad';
    } else if (trial?.error) {
      statusEl.textContent = `ODA 授權狀態不明：${trial.error}`;
      statusEl.className = 'engine-status warn';
    } else {
      statusEl.textContent = 'ODA 工具鏈就緒（尚未確認授權狀態）';
      statusEl.className = 'engine-status warn';
    }
  } else {
    statusEl.textContent = 'ODA 不可用，無法轉檔／開圖（PDF／圖片／Office 預覽仍可用）';
    statusEl.className = 'engine-status bad';
  }

  const summary = $('#engineSummary');
  if (summary) {
    summary.textContent = oda?.available
      ? `DWG／DXF／RVT／RFA 由 ODA 轉檔${oda.concurrency ? `，背景同時處理 ${oda.concurrency} 個檔案` : ''}。PDF、圖片與 Office 預覽不經 ODA。`
      : 'ODA 未就緒，DWG／DXF／RVT／RFA 暫時無法轉檔或開啟。PDF、圖片與 Office 預覽仍可使用。';
  }
  // 授權有問題時給出「該做什麼」，而不只是說狀態不明
  const trialHint = oda?.license?.trial?.hint;
  if (oda?.available && trialHint && !oda?.license?.trial?.active && summary) summary.textContent += ` ${trialHint}`;

  const bits = [];
  if (oda?.available) {
    const tk = oda.license?.sessionToken;
    if (tk?.expiresInMinutes != null) bits.push(`登入 token 剩 ${tk.expiresInMinutes} 分（會自動輪替，非授權效期）`);
    if (oda.python) bits.push(`Python：${oda.python}`);
    if (oda.odaRoot) bits.push(`ODA 路徑：${oda.odaRoot}`);
  } else {
    bits.push(oda?.reason || 'ODA 未就緒');
  }
  if (e.library) bits.push(`圖庫 ${e.library.items} 項 / ${fmtBytes(e.library.bytes)}`);
  note.textContent = bits.join(' · ');

  state.engine = 'oda';
}

/** 僅接受 ODA；保留給既有呼叫端。 */
function setEngine(next) {
  if (next !== 'oda' || state.engine === 'oda') return;
  state.engine = 'oda';
  const item = activeItem();
  if (item) openItem(item, { force: true });
}

/* ── 圖庫清單 ───────────────────────────────────────────────── */

function applyLibraryPayload(data, { silent = false, previousScan = null, schedule = true } = {}) {
  const itemsChanged = Array.isArray(data.items) && libraryItemsChanged(data.items, state.items);
  const projectsChanged = Array.isArray(data.projects) && JSON.stringify(data.projects) !== JSON.stringify(state.projects);
  const foldersChanged = Array.isArray(data.folders) && JSON.stringify(data.folders) !== JSON.stringify(state.folders);
  if (data.items) state.items = data.items;
  if (data.stats) state.stats = data.stats;
  if (data.conversion) state.conversion = data.conversion;
  else if (!state.conversion) state.conversion = { queue: [], running: [] };
  if (data.projects) state.projects = data.projects;
  if (data.folders) state.folders = data.folders;
  const archived = state.stats?.deleted ?? 0;
  const archiveCount = $('#archiveNavCount');
  if (archiveCount) archiveCount.textContent = archived ? `（${archived}）` : '';
  if (!silent || projectsChanged || foldersChanged) renderProjects();
  if (!silent || itemsChanged || projectsChanged || foldersChanged) renderList();
  if (state.workspace === 'convert') renderConvertBoard();
  const current = activeItem();
  if (current && previousScan != null && JSON.stringify(current.package?.scan) !== previousScan) {
    showPackageNotice(current);
  }
  if (!schedule) return;
  libraryFetchedAt = Date.now();
  schedulePoll();
}

function refreshLibrary({ silent = false } = {}) {
  if (libraryJob) {
    libraryAgain = { silent: libraryAgain?.silent === false ? false : Boolean(silent) };
    return libraryJob;
  }
  const run = (async () => {
    try {
      await loadLibrary({ silent });
    } finally {
      libraryJob = null;
      const next = libraryAgain;
      libraryAgain = null;
      if (next) return refreshLibrary(next);
    }
  })();
  libraryJob = run;
  return run;
}

async function loadLibrary({ silent = false } = {}) {
  const previousScan = JSON.stringify(activeItem()?.package?.scan);
  // 開頁：先畫目錄樹，再載入清冊，避免長清單卡住空白畫面。
  if (!silent && !state.items.length && !state.projects.length && !libraryAgain) {
    try {
      const tree = await (await fetch('/api/library?tree=1', { cache: 'no-store' })).json();
      if (!libraryAgain) applyLibraryPayload(tree, { silent: false, previousScan, schedule: false });
    } catch { /* 繼續抓完整清單 */ }
  }
  let data;
  try {
    const response = await fetch('/api/library', { cache: 'no-store' });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    data = await response.json();
  } catch {
    if (!libraryAgain) schedulePoll();
    return;
  }
  // 這次請求期間已有更新的重新整理在排隊，略過這份快照，避免慢回應蓋掉新狀態。
  if (libraryAgain) return;
  applyLibraryPayload(data, { silent, previousScan });
}

/** 開啟檢視前補齊單檔明細（meshVersion 等清單精簡欄位）。 */
async function hydrateItemDetail(item) {
  if (!item?.id) return item;
  const eng = item.engines || {};
  const needsMesh = Object.values(eng).some((s) => s?.hasMesh3d && s.meshVersion == null);
  const needsScan = Boolean(item.package?.scan) && !Array.isArray(item.package.scan.references);
  if (!needsMesh && !needsScan) return item;
  try {
    const r = await fetch(`/api/library/${item.id}`, { cache: 'no-store' });
    const j = await r.json();
    if (!j?.ok || !j.item) return item;
    const idx = state.items.findIndex((i) => i.id === item.id);
    if (idx >= 0) state.items[idx] = j.item;
    if (state.activeId === j.item.id) state.activeDetail = j.item;
    return j.item;
  } catch {
    return item;
  }
}

function libraryBusy() {
  return state.items.some((it) => {
    const status = it.engines?.oda?.status;
    return status === 'pending' || status === 'converting';
  });
}

/** 文件、上傳與轉檔工作區要持續核對清冊；其他工作區只在仍有轉檔時更新。 */
function libraryLiveWorkspace() {
  return state.workspace === 'library' || state.workspace === 'upload' || state.workspace === 'convert';
}

function schedulePoll() {
  clearTimeout(state.pollTimer);
  if (document.hidden) return;
  if (!libraryLiveWorkspace() && !libraryBusy()) return;
  state.pollTimer = setTimeout(() => refreshLibrary({ silent: true }), POLL_MS);
}

function ensureLibraryLive() {
  if (document.hidden) return;
  if (!libraryLiveWorkspace() && !libraryBusy()) return;
  if (Date.now() - libraryFetchedAt >= POLL_MS) refreshLibrary({ silent: true });
  else schedulePoll();
}

document.addEventListener('visibilitychange', () => {
  if (document.hidden) clearTimeout(state.pollTimer);
  else if (libraryLiveWorkspace() || libraryBusy()) ensureLibraryLive();
});

function activeItem() {
  return state.items.find((i) => i.id === state.activeId)
    ?? (state.activeDetail?.id === state.activeId ? state.activeDetail : null);
}

const isDownloadOnly = (item) => !opensInViewer(item);
const canOpenInViewer = (item) => Boolean(item) && !isDownloadOnly(item);

function downloadItem(item) {
  const a = document.createElement('a');
  a.href = `/api/library/${item.id}/file?download=1`;
  a.download = item.name;
  a.click();
}

function statusChip(item) {
  if (isDownloadOnly(item)) return '<span class="tag" title="此格式目前提供下載，不支援線上檢視">下載</span>';
  if (CONVERTIBLE_KINDS.has(item?.kind)) {
    const status = item.engines?.oda?.status;
    if (status === 'error') return '<span class="tag err" title="轉檔未成功，請至轉檔頁查看原因">轉檔失敗</span>';
    if (status === 'ready' && item.engines?.oda?.referencesDirty) return '<span class="tag warn" title="外參關聯已變更，檢視時會更新轉檔結果">外參待更新</span>';
    if (status !== 'ready') return '<span class="tag warn" title="正在排隊或轉檔，完成後即可檢視">轉檔中</span>';
  }
  if (item?.kind === 'office') return '<span class="tag ok" title="開啟時轉成 PDF 檢視；原始檔仍可下載">可檢視</span>';
  if (item?.kind === 'web') return '<span class="tag ok" title="https 開頭，或 Windows [InternetShortcut] 的 URL=">可檢視</span>';
  return '<span class="tag ok" title="此格式支援檢視；CAD／模型需完成轉檔">可檢視</span>';
}

/** 轉檔工作區用：只追蹤正式引擎（ODA），忽略歷史 LibreDWG 狀態。 */
function convertTrack(item) {
  const pick = 'oda';
  const st = item.engines?.[pick] ?? {};
  const status = st.status ?? 'pending';
  return { engine: pick, status, st };
}

function convertStatusLabel(status, st = {}) {
  if (status === 'converting') return '<span class="tag warn">轉檔中</span>';
  if (status === 'pending') return '<span class="tag warn">排隊中</span>';
  if (status === 'error') return `<span class="tag err" title="${escapeHtml(st.error || '')}">失敗</span>`;
  if (status === 'ready') {
    const bits = [];
    if (st.hasSvg) bits.push('2D');
    if (st.hasMesh3d) bits.push('3D');
    return `<span class="tag ok">已完成${bits.length ? ' · ' + bits.join('+') : ''}</span>`;
  }
  return `<span class="tag">${escapeHtml(status || '未知')}</span>`;
}

function convertJobs() {
  const queuePos = new Map((state.conversion?.queue || []).map((j) => [`${j.id}|${j.engine}`, j.position]));
  const runningKeys = new Set((state.conversion?.running || []).map((j) => `${j.id}|${j.engine}`));
  const jobs = [];
  for (const item of state.items) {
    if (item.missing || !CONVERTIBLE_KINDS.has(item.kind)) continue;
    const track = convertTrack(item);
    const key = `${item.id}|${track.engine}`;
    jobs.push({
      item,
      ...track,
      position: queuePos.get(key) ?? null,
      isRunning: runningKeys.has(key) || track.status === 'converting',
    });
  }
  const rank = (j) => {
    if (j.isRunning || j.status === 'converting') return 0;
    if (j.status === 'pending') return 1;
    if (j.status === 'error') return 2;
    if (j.status === 'ready') return 3;
    return 4;
  };
  jobs.sort((a, b) => {
    const ra = rank(a), rb = rank(b);
    if (ra !== rb) return ra - rb;
    if (a.position != null || b.position != null) return (a.position ?? 1e9) - (b.position ?? 1e9);
    const atA = a.st.at || a.item.addedAt || '';
    const atB = b.st.at || b.item.addedAt || '';
    if (a.status === 'ready' || b.status === 'ready') return atB.localeCompare(atA);
    return (a.item.name || '').localeCompare(b.item.name || '', 'zh-Hant');
  });
  return jobs;
}

async function retryAllFailedConverts() {
  const btn = $('#convertRetryAllFailed');
  const failed = convertJobs().filter((j) => j.status === 'error' && !j.item.missing);
  if (!failed.length) {
    flashHint('沒有可重試的失敗項目');
    return;
  }
  if (btn) { btn.disabled = true; btn.textContent = `排入中… 0/${failed.length}`; }
  try {
    const response=await fetch('/api/library/batch-convert',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({ids:failed.map(job=>job.item.id)})});
    const data=await response.json();flashHint(`已重新排入 ${data.queued} 個失敗項目`);
    state.convertFilter='queue';await refreshLibrary();
  } catch(e) {flashHint(e.message);
  } finally {
    if (btn) {
      btn.disabled = false;
      btn.textContent = '全部重新轉檔';
    }
    renderConvertBoard();
  }
}

function convertCounts(jobs = convertJobs()) {
  const counts = { queue: 0, ready: 0, error: 0, all: jobs.length };
  for (const j of jobs) {
    if (j.status === 'pending' || j.status === 'converting') counts.queue++;
    else if (j.status === 'ready') counts.ready++;
    else if (j.status === 'error') counts.error++;
  }
  return counts;
}

/** 使用者還沒自己選分頁時，先顯示最需要處理的狀態。 */
function defaultConvertFilter() {
  const counts = convertCounts();
  return counts.queue ? 'queue' : counts.error ? 'error' : 'all';
}

function renderConvertBoard() {
  const jobs = convertJobs();
  const counts = convertCounts(jobs);
  const summary = $('#convertSummary');
  if (summary) {
    summary.innerHTML = [
      ['queue', '排隊／轉檔中', counts.queue],
      ['ready', '已完成', counts.ready],
      ['error', '失敗', counts.error],
      ['all', '可轉檔合計', counts.all],
    ].map(([key, label, n]) => `<li><button type="button" data-summary-filter="${key}" class="${key === state.convertFilter ? 'is-active' : ''}${key === 'error' && n ? ' has-error' : ''}" aria-pressed="${key === state.convertFilter}"><span>${label}</span><strong>${n}</strong></button></li>`).join('');
  }
  for (const btn of $$('[data-convert-filter]')) {
    const on = btn.dataset.convertFilter === state.convertFilter;
    btn.classList.toggle('is-active', on);
    btn.setAttribute('aria-pressed', String(on));
  }
  const meta = $('#convertHeadMeta');
  if (meta) {
    const q = state.conversion?.queue?.length ?? 0;
    const r = state.conversion?.running?.length ?? 0;
    meta.textContent = r || q ? `進行中 ${r} · 佇列 ${q}` : (counts.queue ? `待處理 ${counts.queue}` : '目前沒有排程');
  }
  const retryAll = $('#convertRetryAllFailed');
  if (retryAll) {
    const show = state.convertFilter === 'error' && counts.error > 0;
    retryAll.hidden = !show;
    if (!retryAll.disabled) retryAll.textContent = show ? `全部重新轉檔（${counts.error}）` : '全部重新轉檔';
  }
  const list = $('#convertList');
  if (!list) return;
  const filter = state.convertFilter;
  const visible = jobs.filter((j) => {
    if (filter === 'queue') return j.status === 'pending' || j.status === 'converting';
    if (filter === 'ready') return j.status === 'ready';
    if (filter === 'error') return j.status === 'error';
    return true;
  });
  if (!visible.length) {
    const empty = filter === 'queue' ? '目前沒有排隊或轉檔中的項目。'
      : filter === 'ready' ? '尚無已完成轉檔的檔案。'
      : filter === 'error' ? '沒有轉檔失敗的項目。'
      : '尚無可追蹤的轉檔項目。';
    list.innerHTML = `<p class="empty">${empty}</p>`;
    return;
  }
  const whereOf = (item) => {
    const proj = item.projectId ? state.projects.find((p) => p.id === item.projectId)?.name : '';
    const folder = item.projectId ? folderPath(state.folders, item.folderId) : '';
    return [proj, folder].filter(Boolean).join(' / ') || (item.projectId ? '根目錄' : '未分類');
  };
  const table = document.createElement('table');
  table.className = 'file-table convert-table';
  table.innerHTML = `<thead><tr>
    <th scope="col">狀態</th><th scope="col">檔案</th><th scope="col">目錄</th>
    <th scope="col">引擎</th><th scope="col">排程</th><th scope="col">時間</th><th scope="col">操作</th>
  </tr></thead><tbody></tbody>`;
  const body = table.querySelector('tbody');
  for (const job of visible) {
    const { item, engine, status, st, position, isRunning } = job;
    const row = document.createElement('tr');
    row.className = 'convert-row' + (isRunning ? ' is-running' : '');
    const schedule = isRunning || status === 'converting' ? '進行中'
      : position != null ? `第 ${position} 位`
      : status === 'pending' ? '等候中' : '—';
    const when = st.at ? fmtDate(st.at) : (item.addedAt ? fmtDate(item.addedAt) : '—');
    const title = item.meta?.title || item.name;
    row.innerHTML = `
      <td>${convertStatusLabel(status, st)}</td>
      <td class="convert-file">
        <div class="fr-name" title="${escapeHtml(item.name)}">${escapeHtml(title)}</div>
        ${item.meta?.title ? `<div class="fr-file">${escapeHtml(item.name)}</div>` : ''}
        ${st.error ? `<div class="convert-error" title="${escapeHtml(st.error)}">${escapeHtml(st.error)}</div>` : ''}
      </td>
      <td title="${escapeHtml(whereOf(item))}">${escapeHtml(whereOf(item))}</td>
      <td>${escapeHtml(engineLabel(engine))}</td>
      <td class="mono">${schedule}</td>
      <td title="${escapeHtml(st.at || item.addedAt || '')}">${when}${st.ms != null ? ` · ${(st.ms / 1000).toFixed(1)}s` : ''}</td>
      <td class="col-actions">
        <button type="button" class="btn btn-sm" data-act="open" ${item.missing ? 'disabled' : ''}>開啟</button>
        ${status === 'error' ? '<button type="button" class="btn btn-sm btn-ghost" data-act="retry">重試</button>' : ''}
        <button type="button" class="btn btn-sm btn-ghost" data-act="library">文件</button>
      </td>`;
    row.querySelector('[data-act="open"]').addEventListener('click', () => openItem(item));
    row.querySelector('[data-act="retry"]')?.addEventListener('click', async () => {
      await fetch(`/api/library/${item.id}/convert?engine=${encodeURIComponent(engine)}`, { method: 'POST' });
      flashHint(`已重新排入「${item.name}」`);
      await refreshLibrary();
    });
    row.querySelector('[data-act="library"]').addEventListener('click', () => {
      selectBrowse({
        project: item.projectId || 'unassigned',
        folder: item.folderId || null,
      });
      showWorkspace('library');
    });
    body.appendChild(row);
  }
  list.replaceChildren(table);
}

function renderList() {
  const scroller = document.querySelector('.operation-area > .page');
  const scrollTop = scroller?.scrollTop || 0;
  const list = $('#fileList');
  const scrollLeft = list.scrollLeft;
  const openMenuId = list.querySelector('details.row-more[open]')?.closest('tr')?.dataset.id || '';
  const restoreView = () => {
    if (scroller) scroller.scrollTop = scrollTop;
    list.scrollLeft = scrollLeft;
    if (!openMenuId) return;
    const more = list.querySelector(`tr[data-id="${CSS.escape(openMenuId)}"] details.row-more`);
    if (more && !more.open) more.open = true;
  };
  try {
  locationNavigation?.renderCatalog();
  list.innerHTML = '';
  const n = state.items.length;
  $('#libraryResultCount').textContent = '0 份文件';
  $('#libraryReset').hidden = !state.librarySearch && state.libraryFilter === 'all' && state.libraryKind === 'all' && state.libraryShare === 'all';
  $('#clearBtn').hidden = n === 0;
  const cnt = $('#libCount');
  if (cnt && state.engines?.library?.usage) cnt.textContent = `${state.engines.library.usage.total.documents} 份文件 · ${fmtBytes(state.engines.library.usage.total.bytes)}`;
  const archived = state.stats?.deleted ?? 0;
  $('#archiveNavCount').textContent = archived ? `（${archived}）` : '';

  const project = currentProject();
  // 選定頂層目錄且未搜尋時像檔案總管；在「全部」則列頂層目錄。搜尋時涵蓋目前位置的整個子樹。
  const atRoot = state.selectedProject === 'all' && !state.librarySearch;
  const browsing = Boolean(project) && !state.librarySearch;
  renderFolderBar(project, browsing || atRoot);
  const subfolders = browsing ? state.folders.filter(f => f.projectId === project.id && (f.parentId ?? null) === state.selectedFolder)
    .sort((a, b) => a.name.localeCompare(b.name, 'zh-Hant', { numeric: true })) : [];
  const topDirs = atRoot ? [...state.projects].sort((a, b) => a.name.localeCompare(b.name, 'zh-Hant', { numeric: true })) : [];

  if (!n && !browsing && !atRoot) {
    clearLibrarySelection(); syncBulkBar();
    list.innerHTML = '<p class="empty" id="emptyHint">尚無文件。上傳 DWG／RVT 會自動背景轉檔；PDF 與 Office 文件也可放入目錄。</p>';
    return;
  }
  const visible = catalogItems(state.items,{formatOnly:true,kind:state.libraryKind,folder:browsing?null:state.selectedFolder,folders:state.folders}).filter(item => {
    if (atRoot) return false;   // 「全部」只列頂層目錄，檔案進各目錄後再看
    if (browsing && (item.folderId ?? null) !== state.selectedFolder) return false;
    if (state.selectedProject === 'unassigned' && item.projectId) return false;
    if (!['all', 'unassigned'].includes(state.selectedProject) && item.projectId !== state.selectedProject) return false;
    if (state.librarySearch && !`${item.name} ${item.package?.path || ''} ${item.meta?.drawingNo || ''} ${item.meta?.title || ''} ${item.meta?.discipline || ''}`.toLowerCase().includes(state.librarySearch)) return false;
    if (state.libraryShare === 'shared' && item.shareActive !== true) return false;
    if (state.libraryShare === 'private' && item.shareActive === true) return false;
    const status = item.engines?.[resolvedEngine()]?.status;
    const converted = !Object.keys(item.engines || {}).length;   // PDF／Office 不轉檔，視為已完成
    if (state.libraryFilter === 'ready') return (converted || status === 'ready') && !item.missing;
    if (state.libraryFilter === 'busy') return ['pending', 'converting'].includes(status) && !item.missing;
    if (state.libraryFilter === 'error') return status === 'error' || item.missing || item.package?.scan.status === 'error';
    return true;
  });
  for (const id of [...state.selected]) if (!visible.some(item => item.id === id)) state.selected.delete(id);
  for (const id of [...state.selectedFolders]) if (!subfolders.some(f => f.id === id)) state.selectedFolders.delete(id);
  for (const id of [...state.selectedProjects]) if (!topDirs.some(p => p.id === id)) state.selectedProjects.delete(id);
  syncBulkBar();
  if (!visible.length && !subfolders.length && !topDirs.length) {
    const filtered = state.librarySearch || state.libraryKind !== 'all' || state.libraryFilter !== 'all' || state.libraryShare !== 'all';
    list.innerHTML = `<p class="empty">${!filtered && (browsing || atRoot) ? '這個目錄是空的。可上傳檔案或按「＋ 目錄」建立子目錄。'
      : `${state.selectedFolder ? '這個目錄沒有符合條件的文件。' : '沒有符合條件的文件。'}請調整搜尋或篩選。`}</p>`;
    return;
  }
  $('#libraryResultCount').textContent = `${visible.length} 份文件 · ${subfolders.length + topDirs.length} 個目錄`;
  const sorted = sortItems(visible);
  const selectable = sorted.length + subfolders.length + topDirs.length;
  const allChecked = selectable > 0
    && sorted.every(i => state.selected.has(i.id))
    && subfolders.every(f => state.selectedFolders.has(f.id))
    && topDirs.every(p => state.selectedProjects.has(p.id));
  const table = document.createElement('table');
  table.className = 'file-table';
  const head = (key, label, cls = '') => `<th class="${cls}" scope="col">${key
    ? `<button class="th-sort" data-sort="${key}" aria-sort="${state.librarySort.key === key ? (state.librarySort.dir > 0 ? 'ascending' : 'descending') : 'none'}">${label}${state.librarySort.key === key ? (state.librarySort.dir > 0 ? ' ▲' : ' ▼') : ''}</button>` : label}</th>`;
  table.innerHTML = `<thead><tr>
      <th class="col-check" scope="col"><input type="checkbox" class="check-all" aria-label="全選" ${!selectable ? 'disabled' : allChecked ? 'checked' : ''}></th>
      ${head('drawingNo', '圖號', 'col-no')}${head('title', '圖名／檔名', 'col-title')}
      <th class="col-actions" scope="col"><span class="sr-only">操作</span></th>
      ${head('folder', '位置', 'col-folder')}${head('discipline', '專業', 'col-disc')}
      ${head('revision', '版次', 'col-rev')}${head('status', '狀態', 'col-status')}<th class="col-conv" scope="col">檢視狀態</th>
      ${head('addedAt', '上傳時間', 'col-date')}
    </tr></thead><tbody></tbody>`;
  table.querySelector('.check-all').addEventListener('change', e => {
    for (const item of sorted) e.target.checked ? state.selected.add(item.id) : state.selected.delete(item.id);
    for (const folder of subfolders) e.target.checked ? state.selectedFolders.add(folder.id) : state.selectedFolders.delete(folder.id);
    for (const dir of topDirs) e.target.checked ? state.selectedProjects.add(dir.id) : state.selectedProjects.delete(dir.id);
    renderList();
  });
  for (const button of table.querySelectorAll('[data-sort]')) button.addEventListener('click', () => {
    const key = button.dataset.sort;
    state.librarySort = { key, dir: state.librarySort.key === key ? -state.librarySort.dir : 1 };
    renderList();
  });
  list.append(table);
  const body = table.querySelector('tbody');
  for (const dir of topDirs) body.append(topDirListRow(dir));
  for (const folder of subfolders) body.append(folderListRow(folder));
  fitDirSummarySpan(table);
  for (const item of sorted) {
    const m = item.meta ?? {};
    const download = isDownloadOnly(item);
    const projName = item.projectId ? state.projects.find(p => p.id === item.projectId)?.name : '';
    const folderName = item.projectId ? folderPath(state.folders, item.folderId) : '';
    const where = [projName, folderName].filter(Boolean).join(' / ');
    const row = document.createElement('tr');
    row.className = 'file-row' + (item.id === state.activeId ? ' is-active' : '');
    row.dataset.id = item.id;
    row.innerHTML = `
      <td class="col-check"><input type="checkbox" class="row-check" aria-label="選取 ${escapeHtml(item.name)}" ${state.selected.has(item.id) ? 'checked' : ''}></td>
      <td class="col-no mono">${m.drawingNo ? escapeHtml(m.drawingNo) : '<span class="muted">—</span>'}</td>
      <td class="col-title">
        <div class="fr-title-cell">
          <div class="fr-thumb">${item.thumb ? `<img src="${item.thumb}" alt="">` : `<span class="fr-thumb-ph">${escapeHtml((item.ext || '.dwg').slice(1).toUpperCase())}</span>`}</div>
          <div class="fr-main">
            <button type="button" class="fr-name" title="${escapeHtml(item.name)}" aria-label="${download ? '下載' : '開啟'} ${escapeHtml(item.name)}">${escapeHtml(m.title || item.name)}</button>
            <div class="fr-meta">
              ${m.title ? `<span class="fr-file" title="${escapeHtml(item.name)}">${escapeHtml(item.name)}</span>` : ''}
              <span class="fr-share-status${item.shareActive ? ' is-shared' : ''}" title="此版次${item.shareActive ? '有有效的外包分享連結' : '沒有有效的外包分享連結'}">${item.shareActive ? '已分享' : '未分享'}</span>
              <span>${fmtBytes(item.size)}</span>
              <span class="fr-upload-time">上傳：${fmtAddedTime(item.addedAt).replace(' ', '<br>')}</span>
              ${m.floor ? `<span class="tag">${escapeHtml(m.floor)}</span>` : ''}
              ${item.package ? `<span class="tag" title="${escapeHtml(item.package.path)}">ZIP · ${item.package.fileCount} 檔</span>` : ''}
              ${item.package?.scan.role ? `<span class="tag">${item.package.scan.role === 'root' ? '主圖候選' : item.package.scan.role === 'reference' ? '外參圖面' : '用途待確認'}</span>` : ''}
              ${item.package?.scan.status === 'error' ? '<span class="tag warn">外參掃描失敗</span>' : ''}
              ${(item.referencePending ?? (item.package?.scan?.xrefPending || item.package?.scan?.references?.some(r => r.status !== 'found' && r.kind !== 'font'))) ? '<span class="tag warn">外參待確認</span>' : ''}
            </div>
          </div>
        </div>
      </td>
      <td class="col-actions">
        ${rowMoreMenu([
          ...(isShareable(item) && window.dcmsAuth?.can('document.share', item) ? [{ act: 'share', label: '分享', title: '產生外包檢視連結。對方不需登入，只能看這一版並留下自己的釋疑' }] : []),
          ...(window.dcmsAuth?.can('document.original.download', item) ? [{ act: 'download', label: '下載', title: '下載此版次的原始檔案' }] : []),
          { act: 'history', label: '歷程', title: '查看同一文件的各版次' },
          { act: 'move', label: '搬移', title: '搬到其他目錄' },
          { act: 'meta', label: '資料', title: '編輯圖號、圖名、版次與狀態' },
          ...((CONVERTIBLE_KINDS.has(item.kind) || item.package) && window.dcmsAuth?.can('document.preview',item) ? [{ act: 'package', label: '外參', title: '本檔外參關聯與上傳來源' }] : []),
          { act: 'rm', label: '封存', title: '封存文件及所有歷史版次', danger: true },
        ])}
      </td>
      <td class="col-folder" title="${escapeHtml(where)}">${where ? escapeHtml(where) : `<span class="muted">${item.projectId ? '根目錄' : '—'}</span>`}</td>
      <td class="col-disc">${m.discipline ? escapeHtml(m.discipline) : '<span class="muted">—</span>'}</td>
      <td class="col-rev mono">${revisionHtml(m.revision)}${item.isCurrent === false ? ' <span class="tag warn">舊版</span>' : (item.revisionCount > 1 ? ' <span class="tag">現行</span>' : '')}</td>
      <td class="col-status">${m.status ? `<span class="badge status-${m.status}">${STATUS_LABEL[m.status]}</span>` : '<span class="muted">未設定</span>'}</td>
      <td class="col-conv">${statusChip(item)}</td>
      <td class="col-date" title="${escapeHtml(item.addedAt || '')}">${fmtAddedTime(item.addedAt).replace(' ', '<br>')}</td>
`;
    if (item.isCurrent === false) row.classList.add('is-superseded');
    const check = row.querySelector('.row-check');
    check.addEventListener('click', e => e.stopPropagation());
    check.addEventListener('change', () => {
      check.checked ? state.selected.add(item.id) : state.selected.delete(item.id);
      syncCheckAll(table);
      syncBulkBar();
    });
    row.querySelector('[data-act="share"]')?.addEventListener('click', (e) => { e.stopPropagation(); shareItem(item); });
    row.querySelector('[data-act="download"]')?.addEventListener('click', (e) => { e.stopPropagation(); downloadItem(item); });
    row.querySelector('[data-act="move"]').addEventListener('click', (e) => { e.stopPropagation(); moveItem(item); });
    row.querySelector('[data-act="meta"]').addEventListener('click', (e) => { e.stopPropagation(); editMeta(item); });
    row.querySelector('[data-act="history"]').addEventListener('click', (e) => { e.stopPropagation(); showRevisions(item); });
    row.querySelector('[data-act="package"]')?.addEventListener('click', e => { e.stopPropagation(); showPackageReport(item); });
    row.querySelector('[data-act="rm"]').addEventListener('click', async (e) => {
      e.stopPropagation();
      if (!confirm(`封存「${item.name}」及其所有歷史版次？整份文件將不再顯示於清單，原始檔與轉檔結果仍保留，可在「設定 → 管理者設定 → 封存匣」還原。`)) return;
      await fetch(`/api/library/${item.id}`, { method: 'DELETE' });
      if (state.activeId === item.id) closeModal();
      refreshLibrary();
    });
    row.querySelector('button.fr-name')?.addEventListener('click', e => {
      e.stopPropagation();
      if (item.missing) return;
      if (download) downloadItem(item); else openItem(item);
    });
    row.querySelector('button.fr-name')?.addEventListener('dblclick', e => e.stopPropagation());
    wireRowMore(row);
    // 雙擊開啟可檢視檔案；只能下載的格式改為下載，避免單擊誤觸。
    row.addEventListener('dblclick', () => {
      if (item.missing) return;
      if (download) downloadItem(item);
      else openItem(item);
    });
    body.appendChild(row);
  }
  } finally {
    restoreView();
    requestAnimationFrame(restoreView);
  }
}

/** 清冊標題兼作路徑導覽，保留目前位置的 heading 語意。 */
function renderLibraryPath(project) {
  const crumbs = $('#folderCrumbs');
  const chain = [];
  for (let f = state.folders.find(x => x.id === state.selectedFolder); f && !chain.includes(f); f = state.folders.find(x => x.id === f.parentId)) chain.unshift(f);
  const rootName = project ? project.name : state.selectedProject === 'unassigned' ? '未分類' : '全部';
  const steps = [{ project: state.selectedProject, folder: null, name: rootName },
    ...chain.map(f => ({ project: state.selectedProject, folder: f.id, name: f.name }))];
  // 進入頂層目錄後，路徑最前可回到「全部」
  if (project) steps.unshift({ project: 'all', folder: null, name: '全部' });
  crumbs.replaceChildren(...steps.flatMap((step, i) => {
    const last = i === steps.length - 1;
    const el = document.createElement(last ? 'span' : 'button');
    el.className = last ? 'crumb is-current' : 'crumb';
    el.textContent = step.name;
    el.dataset.folder = step.folder ?? '';
    if (last) el.setAttribute('aria-current', 'location');
    else { el.type = 'button'; el.addEventListener('click', () => selectBrowse({ project: step.project, folder: step.folder })); }
    if (!i) return [el];
    const sep = document.createElement('span');
    sep.className = 'crumb-sep'; sep.textContent = '/'; sep.setAttribute('aria-hidden', 'true');
    return [sep, el];
  }));
}

/** 清冊上方的路徑列：瀏覽時可點上層回去；搜尋時說明搜尋範圍。 */
function renderFolderBar(project, browsing) {
  const bar = $('#folderBar');
  const atRoot = state.selectedProject === 'all';
  bar.hidden = !(project || atRoot || state.selectedProject === 'unassigned');
  if (bar.hidden) return;
  renderLibraryPath(project);
  $('#folderScope').textContent = browsing || atRoot ? '' : '含子目錄';
  $('#crumbFolderAdd').hidden = state.selectedProject === 'unassigned';
  // 「全部」也可上傳（進未分類）；路徑列有顯示時就提供快捷入口。
  if ($('#crumbUpload')) $('#crumbUpload').hidden = false;
  if ($('#crumbConvert')) $('#crumbConvert').hidden = false;
}

/** 目前路徑列範圍內、可送轉檔引擎的項目（含子目錄）。 */
function convertibleInCurrentFolder() {
  return state.items.filter((item) => {
    if (item.missing || !CONVERTIBLE_KINDS.has(item.kind)) return false;
    if (state.selectedProject === 'all') return true;
    if (state.selectedProject === 'unassigned') return !item.projectId;
    if (item.projectId !== state.selectedProject) return false;
    if (!state.selectedFolder) return true;
    return folderSubtree(state.folders, state.selectedFolder).has(item.folderId);
  });
}

/** 批次轉檔用引擎：正式路徑強制 ODA。 */
function convertEngineFor(_item) {
  return 'oda';
}

function needsFolderConvert(item) {
  const eng = convertEngineFor(item);
  const st = item.engines?.[eng] ?? {};
  return st.status !== 'ready' && st.status !== 'converting';
}

async function convertCurrentFolder() {
  const btn = $('#crumbConvert');
  const all = convertibleInCurrentFolder();
  if (!all.length) {
    flashHint('目前目錄沒有可轉檔的 DWG／DXF／RVT／RFA');
    return;
  }
  const targets = all.filter(needsFolderConvert);
  const skipped = all.length - targets.length;
  if (!targets.length) {
    flashHint(`目前目錄 ${all.length} 個可轉檔檔案皆已完成或轉檔中，略過不再轉`);
    return;
  }
  const scope = state.selectedProject === 'all' ? '全部範圍'
    : state.selectedFolder ? '此目錄及子目錄' : '此目錄';
  if (!confirm(`將為${scope} ${targets.length} 個尚未完成的檔案排入轉檔${skipped ? `（已完成／轉檔中 ${skipped} 個略過）` : ''}？`)) return;
  if (btn) btn.disabled = true;
  try {
    const response=await fetch('/api/library/batch-convert',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({ids:targets.map(item=>item.id)})});
    const data=await response.json();flashHint(`已排入 ${data.queued} 個轉檔${skipped ? `；略過已完成 ${skipped} 個` : ''}`);
  } catch(e) {flashHint(e.message);} finally {if(btn)btn.disabled=false;}
  await refreshLibrary();
}

function shareItem(item) {
  const dialog = document.createElement('dialog');
  dialog.className = 'meta-dialog share-dialog';
  dialog.setAttribute('aria-labelledby', 'shareDialogTitle');
  dialog.innerHTML = `<form>
    <header class="share-dialog-header"><div><span class="share-dialog-eyebrow">DCMS · 文件協作</span><h3 id="shareDialogTitle">檔案分享</h3></div><span class="share-state-badge">尚未分享</span></header>
    <div class="share-file-card"><span class="share-file-icon" aria-hidden="true">↗</span><div><p class="share-file-name"></p><span class="share-file-revision"></span></div></div>
    <p class="side-help">對方不需登入，輸入密碼即可檢視此版文件及留下釋疑。僅分享此版，不提供原檔下載。</p>
    <div class="share-content-grid">
      <section class="share-link-panel" aria-label="分享連結與密碼">
        <label class="share-url-row"><span>分享連結</span><div class="share-url-control"><input name="url" readonly placeholder="建立連結後顯示" aria-label="分享連結"><button class="btn btn-sm" type="button" data-act="copy">複製連結</button></div></label>
        <div class="share-access-details" hidden><div class="share-password-row"><label>6 位數字密碼<input name="password" readonly inputmode="numeric"></label><button class="btn btn-sm" type="button" data-act="copy-password">複製密碼</button></div><p class="share-field-hint">將密碼提供給收件人，開啟連結或掃碼時使用。</p></div>
      </section>
      <section class="share-qr-panel" aria-label="分享 QR Code" hidden><span class="share-qr-title">掃碼開啟文件</span><img class="share-qr" alt="分享連結 QR Code" width="256" height="256"><div class="share-qr-actions"><button class="btn btn-sm" type="button" data-act="copy-qr">複製 QR Code</button><a class="btn btn-sm" data-act="download-qr" download="DCMS-share-QR.png">下載 QR Code</a></div></section>
    </div>
    <p class="share-dialog-status" role="status" aria-live="polite"></p>
    <footer class="share-dialog-footer"><button class="btn btn-sm share-revoke" type="button" data-act="revoke">停止分享</button><div class="meta-actions"><button class="btn btn-sm" type="button" data-act="close">關閉</button><button class="btn btn-sm btn-primary" type="button" data-act="create">建立連結</button></div></footer>
  </form>`;
  dialog.querySelector('.share-file-name').textContent = item.name;
  dialog.querySelector('.share-file-revision').textContent = `版號 ${item.meta?.revision || '未標示'}`;
  const input = dialog.querySelector('input[name="url"]');
  const status = dialog.querySelector('.share-dialog-status');
  const syncShareState = active => {
    item.shareActive = active;
    const listed = state.items.find(entry => entry.id === item.id);
    if (listed) listed.shareActive = active;
    renderList();
  };
  const paint = (active, href = '', data = {}) => {
    input.value = href;
    dialog.querySelector('.share-state-badge').textContent = active ? '分享中' : '尚未分享';
    dialog.querySelector('.share-state-badge').classList.toggle('is-active', active);
    dialog.querySelector('[data-act="create"]').textContent = active ? '重新建立連結' : '建立連結';
    dialog.querySelector('.share-qr-panel').hidden = !data.qrCode;
    dialog.querySelector('.share-content-grid').classList.toggle('has-qr', Boolean(data.qrCode));
    dialog.querySelector('.share-access-details').hidden = !href;
    dialog.querySelector('input[name="password"]').value = data.password || '舊連結未設密碼';
    const qr = dialog.querySelector('.share-qr');
    qr.hidden = !data.qrCode;
    if (data.qrCode) qr.src = data.qrCode; else qr.removeAttribute('src');
    const download = dialog.querySelector('[data-act="download-qr"]');
    download.hidden = !data.qrCode;
    dialog.querySelector('[data-act="copy-qr"]').disabled = !data.qrCode;
    if (data.qrCode) download.href = data.qrCode; else download.removeAttribute('href');
    dialog.querySelector('[data-act="copy-password"]').disabled = !data.password;
    status.textContent = href ? '連結與密碼已儲存，可隨時回來查看。重新建立連結後，舊連結將失效。' : active ? '已有舊分享連結；再次開啟舊連結後即可補記網址，或重新建立（舊連結將失效）。' : '目前沒有有效的分享連結。';
    dialog.querySelector('[data-act="revoke"]').disabled = !active;
    dialog.querySelector('[data-act="copy"]').disabled = !href;
  };
  dialog.querySelector('[data-act="create"]').onclick = async () => {
    const response = await fetch(`/api/library/${item.id}/share`, {method: 'POST', headers: {'Content-Type': 'application/json'}, body: '{}'});
    const data = await response.json();
    if (!response.ok) { status.textContent = data.error || '無法建立連結'; return; }
    syncShareState(true);
    paint(true, data.url || new URL(data.path, location.origin).href, data);
  };
  dialog.querySelector('[data-act="revoke"]').onclick = async () => {
    const response = await fetch(`/api/library/${item.id}/share`, {method: 'DELETE'});
    const data = await response.json();
    if (!response.ok) { status.textContent = data.error || '無法停止分享'; return; }
    syncShareState(false);
    paint(false);
  };
  dialog.querySelector('form').addEventListener('submit', (event) => event.preventDefault());
  dialog.querySelector('[data-act="close"]').onclick = () => dialog.close();
  dialog.querySelector('[data-act="copy-qr"]').onclick = async () => {
    const qr = dialog.querySelector('.share-qr');
    const source = qr.getAttribute('src');
    if (!source) return;
    try {
      const bytes = Uint8Array.from(atob(source.split(',')[1]), character => character.charCodeAt(0));
      const blob = new Blob([bytes], {type: 'image/png'});
      if (!navigator.clipboard?.write || typeof ClipboardItem === 'undefined') throw new Error('圖片剪貼簿不可用');
      await navigator.clipboard.write([new ClipboardItem({'image/png': blob})]);
      status.textContent = '已複製 QR Code 圖片，可直接貼上';
    } catch {
      // Image selection supports copying on LAN HTTP where Clipboard API is unavailable.
      const selection = window.getSelection();
      const previous = selection?.rangeCount ? selection.getRangeAt(0).cloneRange() : null;
      const range = document.createRange();
      range.selectNode(qr);
      selection?.removeAllRanges();
      selection?.addRange(range);
      let copied = false;
      try { copied = document.execCommand('copy'); } catch { /* Show the available alternative below. */ }
      finally {
        selection?.removeAllRanges();
        if (previous) selection?.addRange(previous);
      }
      status.textContent = copied ? '已複製 QR Code 圖片，可直接貼上' : '瀏覽器未允許複製圖片，請在 QR Code 上按右鍵選擇「複製圖片」，或下載後貼上';
    }
  };
  dialog.querySelector('[data-act="copy-password"]').onclick = async () => {
    const passwordInput = dialog.querySelector('input[name="password"]');
    try { await navigator.clipboard.writeText(passwordInput.value); status.textContent = '已複製密碼'; }
    catch { passwordInput.select(); status.textContent = '請手動複製密碼'; }
  };
  dialog.querySelector('[data-act="copy"]').onclick = async () => {
    try { await navigator.clipboard.writeText(input.value); status.textContent = '已複製連結'; }
    catch { input.select(); status.textContent = '請手動複製連結'; }
  };
  dialog.addEventListener('close', () => dialog.remove());
  document.body.append(dialog);
  dialog.showModal();
  paint(false);
  fetch(`/api/library/${item.id}/share`).then(r => r.json()).then(data => { if (dialog.isConnected && data?.active && !input.value) paint(true, data.url || '', data); }).catch(() => {});
}

function syncCheckAll(table) {
  const box = table?.querySelector('.check-all');
  if (!box || box.disabled) return;
  const rows = [...table.querySelectorAll('tbody .row-check')];
  box.checked = rows.length > 0 && rows.every(c => c.checked);
}

/** 清冊列次要操作收進「⋯」選單；主操作（開啟／下載）仍外露。 */
function rowMoreMenu(items) {
  const entries = items.map((it) =>
    `<button type="button" class="row-menu-item${it.danger ? ' is-danger' : ''}" data-act="${it.act}" role="menuitem"${it.title ? ` title="${escapeHtml(it.title)}"` : ''}>${escapeHtml(it.label)}</button>`).join('');
  return `<details class="row-more">
    <summary class="btn btn-sm btn-ghost row-more-btn" title="更多操作" aria-label="更多操作">⋯</summary>
    <div class="row-menu" role="menu" aria-label="更多操作">${entries}</div>
  </details>`;
}

/** 選單固定定位：先隱藏量尺寸再顯示，避免 absolute 撐出捲軸導致短清單抖動；下方不夠則往上開。 */
function placeRowMenu(more) {
  const menu = more.querySelector('.row-menu');
  const btn = more.querySelector('summary');
  if (!menu || !btn) return;
  menu.style.visibility = 'hidden';
  menu.classList.add('is-fixed');
  menu.style.top = '0';
  menu.style.left = '0';
  const br = btn.getBoundingClientRect();
  const mr = menu.getBoundingClientRect();
  const gap = 4, pad = 8;
  let top = br.bottom + gap;
  if (top + mr.height > window.innerHeight - pad) top = Math.max(pad, br.top - gap - mr.height);
  let left = br.right - mr.width;
  if (left < pad) left = pad;
  if (left + mr.width > window.innerWidth - pad) left = Math.max(pad, window.innerWidth - pad - mr.width);
  menu.style.top = `${Math.round(top)}px`;
  menu.style.left = `${Math.round(left)}px`;
  menu.style.visibility = '';
}

function clearRowMenuPlace(more) {
  const menu = more.querySelector('.row-menu');
  if (!menu) return;
  menu.classList.remove('is-fixed');
  menu.style.top = menu.style.left = menu.style.visibility = '';
}

function closeOpenRowMores() {
  for (const pop of document.querySelectorAll('#fileList details.row-more[open]')) pop.open = false;
}

/** 點「⋯」不要觸發整列開檔；同時只開一個列選單。 */
function wireRowMore(row) {
  const more = row.querySelector('details.row-more');
  if (!more) return;
  more.addEventListener('click', (e) => e.stopPropagation());
  more.querySelector('.row-menu')?.addEventListener('click', (e) => {
    if (e.target.closest('[data-act]')) more.open = false;
  });
  more.addEventListener('toggle', () => {
    if (!more.open) {
      clearRowMenuPlace(more);
      return;
    }
    for (const other of document.querySelectorAll('#fileList details.row-more[open]')) {
      if (other !== more) other.open = false;
    }
    requestAnimationFrame(() => placeRowMenu(more));
  });
}

/** 目錄列摘要格要橫跨的欄數＝目前可見的資訊欄（窄版會用 CSS 隱藏部分欄位）。 */
const DIR_SUMMARY_COLS = ['col-folder', 'col-disc', 'col-rev', 'col-status', 'col-conv', 'col-date'];
function fitDirSummarySpan(table = $('#fileList .file-table')) {
  if (!table) return;
  const span = [...table.querySelectorAll('thead th')]
    .filter(th => DIR_SUMMARY_COLS.some(c => th.classList.contains(c)) && getComputedStyle(th).display !== 'none').length;
  for (const cell of table.querySelectorAll('.col-dir-summary')) cell.colSpan = Math.max(1, span);
}
for (const query of ['(max-width: 1100px)', '(max-width: 900px)']) window.matchMedia(query).addEventListener('change', () => fitDirSummarySpan());
new ResizeObserver(() => fitDirSummarySpan()).observe($('#fileList'));

/** 目錄列的轉檔摘要：圖面／模型已轉幾個、轉檔中、失敗，以及不需轉檔的文件數。 */
function dirConvertSummary(items) {
  let convertible = 0, ready = 0, busy = 0, error = 0, other = 0;
  for (const item of items) {
    if (!CONVERTIBLE_KINDS.has(item.kind)) { other++; continue; }
    convertible++;
    const status = item.engines?.[resolvedEngine()]?.status;
    if (item.missing || status === 'error') error++;
    else if (status === 'ready') ready++;
    else if (status === 'pending' || status === 'converting') busy++;
  }
  const bits = [];
  if (convertible) bits.push(`<span>已轉 <b>${ready}</b>／${convertible}</span>`);
  if (busy) bits.push(`<span class="tag">轉檔中 ${busy}</span>`);
  if (error) bits.push(`<span class="tag warn">失敗 ${error}</span>`);
  if (other) bits.push(`<span>其他文件 ${other}</span>`);
  return bits.length ? `<div class="dir-summary">${convertible ? '<div class="dir-summary-label">圖面／模型</div>' : ''}<div class="dir-summary-stats">${bits.join('')}</div></div>` : '<span class="muted">空目錄</span>';
}

/** 清冊中的頂層目錄列（後端專案）：操作與子目錄相同。 */
function topDirListRow(project) {
  const folders = state.folders.filter(f => f.projectId === project.id && !f.parentId).length;
  const files = state.items.filter(i => i.projectId === project.id).length;
  const row = document.createElement('tr');
  row.className = 'dir-row';
  row.dataset.projectId = project.id;
  row.innerHTML = `
    <td class="col-check"><input type="checkbox" class="row-check" aria-label="選取目錄 ${escapeHtml(project.name)}" ${state.selectedProjects.has(project.id) ? 'checked' : ''}></td>
    <td class="col-no"></td>
    <td class="col-title"><div class="fr-title-cell">
      <div class="fr-thumb dir-thumb"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M3 6h6l2 2h10v11H3z"/></svg></div>
      <div class="fr-main"><div class="fr-name"></div>
        <div class="fr-meta"><span>目錄</span><span>${files} 個檔案</span>${folders ? `<span>${folders} 個子目錄</span>` : ''}</div></div>
    </div></td>
    <td class="col-actions">
      ${rowMoreMenu([
        { act: 'rename', label: '改名', title: '目錄改名' },
        { act: 'rm', label: '刪除', title: '永久刪除目錄及其檔案', danger: true },
      ])}
    </td>
    <td class="col-dir-summary" colspan="6">${dirConvertSummary(state.items.filter(i => i.projectId === project.id))}</td>`;
  row.querySelector('.fr-name').textContent = project.name;
  const check = row.querySelector('.row-check');
  check.addEventListener('click', e => e.stopPropagation());
  check.addEventListener('change', () => {
    check.checked ? state.selectedProjects.add(project.id) : state.selectedProjects.delete(project.id);
    syncCheckAll(row.closest('table'));
    syncBulkBar();
  });
  row.addEventListener('dblclick', () => selectBrowse({ project: project.id, folder: null }));
  row.querySelector('[data-act="rename"]').addEventListener('click', async e => {
    e.stopPropagation();
    const name = prompt('目錄新名稱（最多 80 字）：', project.name);
    if (name === null) return;
    try {
      await projectRequest(`/api/library/projects/${project.id}`, 'PATCH', { name });
      await refreshLibrary();
      flashHint('目錄名稱已更新');
    } catch (err) { flashHint(err.message); }
  });
  row.querySelector('[data-act="rm"]').addEventListener('click', async e => {
    e.stopPropagation();
    await deleteTopDir(project);
  });
  if(!window.dcmsAuth?.admin)row.querySelector('.row-more').hidden=true;
  wireRowMore(row);
  return row;
}

/** 清冊中的子目錄列：雙擊進入。 */
function folderListRow(folder) {
  const subtree = folderSubtree(state.folders, folder.id);
  const files = state.items.filter(i => subtree.has(i.folderId)).length;
  const children = state.folders.filter(f => f.parentId === folder.id).length;
  const row = document.createElement('tr');
  row.className = 'dir-row';
  row.dataset.folderId = folder.id;
  row.innerHTML = `
    <td class="col-check"><input type="checkbox" class="row-check" aria-label="選取目錄 ${escapeHtml(folder.name)}" ${state.selectedFolders.has(folder.id) ? 'checked' : ''}></td>
    <td class="col-no"></td>
    <td class="col-title"><div class="fr-title-cell">
      <div class="fr-thumb dir-thumb"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M3 6h6l2 2h10v11H3z"/></svg></div>
      <div class="fr-main"><div class="fr-name"></div>
        <div class="fr-meta"><span>目錄</span><span>${files} 個檔案</span>${children ? `<span>${children} 個子目錄</span>` : ''}</div></div>
    </div></td>
    <td class="col-actions">
      ${rowMoreMenu([
        { act: 'rename', label: '改名', title: '目錄改名' },
        { act: 'move', label: '搬移', title: '搬到其他目錄' },
        { act: 'rm', label: '刪除', title: '永久刪除目錄及其檔案', danger: true },
      ])}
    </td>
    <td class="col-dir-summary" colspan="6">${dirConvertSummary(state.items.filter(i => subtree.has(i.folderId)))}</td>`;
  const name = row.querySelector('.fr-name');
  name.textContent = folder.name;
  name.title = folderPath(state.folders, folder.id);
  const check = row.querySelector('.row-check');
  check.addEventListener('click', e => e.stopPropagation());
  check.addEventListener('change', () => {
    check.checked ? state.selectedFolders.add(folder.id) : state.selectedFolders.delete(folder.id);
    syncCheckAll(row.closest('table'));
    syncBulkBar();
  });
  row.addEventListener('dblclick', () => selectFolder(folder.id));
  const act = (key, fn) => row.querySelector(`[data-act="${key}"]`).addEventListener('click', e => { e.stopPropagation(); fn(folder); });
  act('rename', renameFolder);
  act('move', moveFolder);
  act('rm', deleteFolder);
  wireRowMore(row);
  return row;
}

function sortItems(items) {
  const { key, dir } = state.librarySort;
  if (!key) return items;
  const value = item => key === 'addedAt' ? item.addedAt || '' : key === 'title' ? item.meta?.title || item.name
    : key === 'folder' ? (item.projectId ? [state.projects.find(p => p.id === item.projectId)?.name, folderPath(state.folders, item.folderId)].filter(Boolean).join(' / ') : '')
    : key === 'status' ? String(['wip', 'review', 'approved', 'issued', 'void'].indexOf(item.meta?.status ?? '') + 1 || '') : item.meta?.[key] || '';
  // 空值一律排在最後，不受升降冪影響
  return [...items].sort((a, b) => {
    const va = value(a), vb = value(b);
    if (!va || !vb) return !va - !vb;
    return va.localeCompare(vb, 'zh-Hant', { numeric: true }) * dir;
  });
}

function syncBulkBar() {
  const n = librarySelectionCount();
  $('#bulkBar').hidden = n === 0;
  $('#bulkCount').textContent = `已選 ${n} 項`;
  // 頂層目錄無法搬移；僅選頂層時停用搬移。
  const onlyTops = state.selectedProjects.size && !state.selected.size && !state.selectedFolders.size;
  $('#bulkMove').disabled = onlyTops;
  $('#bulkMove').title = onlyTops ? '頂層目錄無法搬移，請改用刪除或改名' : '搬到其他目錄';
}
$('#bulkClear').addEventListener('click', () => { clearLibrarySelection(); renderList(); });
$('#bulkMove').addEventListener('click', async () => {
  const ids = [...state.selected];
  const folderIds = [...state.selectedFolders];
  const projectIds = [...state.selectedProjects];
  const n = ids.length + folderIds.length + projectIds.length;
  if (!n || (!ids.length && !folderIds.length)) return;
  const exclude = new Set();
  for (const id of folderIds) for (const x of folderSubtree(state.folders, id)) exclude.add(x);
  const dest = await pickDestination(`搬移已選 ${ids.length + folderIds.length} 項`, { excludeFolders: exclude, allowUnassigned: !folderIds.length });
  if (dest === undefined) return;
  const failed = [];
  let done = 0;
  for (const id of ids) {
    try {
      const item = state.items.find(i => i.id === id);
      if ((item?.projectId ?? null) !== dest.projectId) {
        await projectRequest(`/api/library/${id}/project`, 'PATCH', { projectId: dest.projectId });
      }
      if (dest.projectId) await projectRequest(`/api/library/${id}/folder`, 'PATCH', { folderId: dest.folderId });
      done++;
    } catch (e) { failed.push(e.message); }
  }
  for (const id of folderIds) {
    try {
      const folder = state.folders.find(f => f.id === id);
      if (!folder) continue;
      if (!dest.projectId) {
        failed.push(`目錄「${folder.name}」必須放在專案內`);
        continue;
      }
      if (exclude.has(dest.folderId)) {
        failed.push(`不能搬進「${folder.name}」自己的子目錄`);
        continue;
      }
      await projectRequest(`/api/library/folders/${id}`, 'PATCH', { projectId: dest.projectId, parentId: dest.folderId });
      done++;
    } catch (e) { failed.push(e.message); }
  }
  if (projectIds.length) failed.push(`${projectIds.length} 個頂層目錄無法搬移`);
  clearLibrarySelection();
  await refreshLibrary();
  flashHint(failed.length ? `${done} 項已搬移，${failed.length} 項未完成：${failed[0]}` : `已搬移 ${done} 項`);
});

function extraToText(meta) {
  const extra = meta?.extra || {};
  return Object.entries(extra).map(([k, v]) => `${k}=${v}`).join('\n');
}
function textToExtra(text) {
  const extra = {};
  for (const line of String(text || '').split('\n')) {
    const trimmed = line.trim();
    if (!trimmed) continue;
    const at = trimmed.indexOf('=');
    if (at < 1) throw new Error(`額外欄位請用「名稱=值」：${trimmed}`);
    extra[trimmed.slice(0, at).trim()] = trimmed.slice(at + 1).trim();
  }
  return extra;
}

/** 編輯圖說資料（文件 meta 與本版版次／狀態）。 */
function editMeta(item) {
  const dialog = $('#metaDialog'), form = $('#metaForm');
  $('#metaFile').textContent = item.name;
  const systemRevision=Boolean(revisionParts(item.meta?.revision).description);
  $('#metaSystemStatusHint').hidden=!systemRevision;
  for (const field of form.querySelectorAll('[name]')) {
    if(field.name==='revision'||field.name==='status')field.disabled=systemRevision;
    if (field.name === 'extra') field.value = extraToText(item.meta);
    else if(field.name==='revision')field.value=revisionParts(item.meta?.revision).number;
    else field.value = item.meta?.[field.name] ?? '';
  }
  dialog.returnValue = '';
  dialog.onclose = async () => {
    if (dialog.returnValue !== 'save') return;
    try {
      const patch = {};
      for (const field of form.querySelectorAll('[name]')) {
        if(field.disabled)continue;
        if (field.name === 'extra') patch.extra = textToExtra(field.value);
        else patch[field.name] = field.value.trim() || null;
      }
      const result = await projectRequest(`/api/library/${item.id}/meta`, 'PATCH', patch);
      await refreshLibrary();
      if (state.activeId === item.id) { $('#mTitle').textContent = docTitle(result.item); renderDocBadges(result.item); }
      flashHint('圖說資料已更新');
    } catch (e) { flashHint(e.message); }
  };
  dialog.showModal();
  form.querySelector('[name="drawingNo"]').focus();
}

async function showRevisions(item) {
  let data;
  try {
    const response = await fetch(`/api/library/${item.id}/revisions`, { cache: 'no-store' });
    data = await response.json();
    if (!response.ok || !data.ok) throw new Error(data.error || '無法讀取版次');
  } catch (e) { flashHint(e.message); return; }
  const dialog = $('#revisionsDialog');
  $('#revisionsFile').textContent = data.document?.name || item.name;
  const current = data.items.find(rev => rev.isCurrent);
  $('#revisionsSummary').innerHTML = `${data.items.length} 個版次 · 現行版 ${current?.meta?.revision?revisionHtml(current.meta.revision):'未標版次'}`;
  const status = $('#revisionsStatus');
  status.hidden = true;
  const list = $('#revisionsList');
  list.replaceChildren();
  for (const rev of data.items) {
    const label = rev.isCurrent ? '現行版' : current && compareRevisions(rev, current) < 0 ? '新版' : '舊版';
    const row = document.createElement('div');
    row.className = 'revision-row' + (rev.isCurrent ? ' is-current' : '');
    const when = rev.addedAt ? new Date(rev.addedAt).toLocaleString('sv-SE', { dateStyle: 'short', timeStyle: 'short' }) : '—';
    const viewing = rev.id === state.activeId && state.workspace === 'viewer';
    row.innerHTML = `<div class="revision-detail">
      <div class="revision-heading"><span class="revision-number">${rev.meta?.revision?revisionHtml(rev.meta.revision):'未標版次'}</span>
        <span class="revision-label ${rev.isCurrent ? 'is-current' : ''}">${label}</span>
        ${viewing ? '<span class="revision-viewing">檢視中</span>' : ''}</div>
      <div class="revision-info">上傳 ${escapeHtml(when)} · ${escapeHtml(fmtBytes(rev.size))}${rev.meta?.status ? ` · ${escapeHtml(STATUS_LABEL[rev.meta.status] || rev.meta.status)}` : ''}</div>
      <div class="revision-filename">${escapeHtml(rev.name)}</div></div>`;
    const actions = document.createElement('div');
    actions.className = 'revision-actions';
    const open = document.createElement('button');
    open.type = 'button';
    open.className = 'btn btn-sm';
    open.textContent = viewing ? '檢視中' : '開啟';
    open.disabled = viewing;
    open.addEventListener('click', async () => {
      dialog.close();
      await openItem(rev);
    });
    actions.append(open);
    if (!rev.isCurrent) {
      const select = document.createElement('button');
      select.type = 'button';
      select.className = 'btn btn-sm btn-primary';
      select.textContent = '設為現行版';
      select.addEventListener('click', async () => {
        for (const button of list.querySelectorAll('button')) button.disabled = true;
        status.hidden = false;
        status.textContent = '正在設定現行版…';
        try {
          const result = await projectRequest(`/api/library/${rev.id}/current`, 'POST', {});
          const active = activeItem();
          if (active?.documentId === rev.documentId) {
            const detail = await (await fetch(`/api/library/${active.id}`, { cache: 'no-store' })).json();
            if (detail.ok) {
              state.activeDetail = detail.item;
              renderDocBadges(detail.item);
              state.notices = state.notices.filter(notice => notice.scope !== 'revision');
              if (detail.item.isCurrent === false) addNotice('warn', oldRevisionNotice(detail.item, result.item), 'revision');
              else renderNotices();
            }
          }
          await refreshLibrary({ silent: true });
          await showRevisions(result.item);
          status.hidden = false;
          status.textContent = `已將版次 ${revisionParts(rev.meta?.revision).number || '未標版次'} 設為現行版`;
        } catch (e) {
          status.textContent = `設定失敗：${e.message}`;
          for (const button of list.querySelectorAll('button')) button.disabled = false;
          open.disabled = viewing;
        }
      });
      actions.append(select);
    }
    row.append(actions);
    list.append(row);
  }
  if (!dialog.open) dialog.showModal();
}

let catalogCsv = '';
$('#catalogExport').addEventListener('click', () => { window.location.href = '/api/library/catalog.csv'; });
$('#catalogImport').addEventListener('click', () => $('#catalogFile').click());
$('#catalogFile').addEventListener('change', async (e) => {
  const file = e.target.files?.[0];
  e.target.value = '';
  if (!file) return;
  catalogCsv = await file.text();
  try {
    const preview = await projectRequest('/api/library/catalog', 'POST', { csv: catalogCsv });
    const box = $('#catalogPreview');
    box.replaceChildren();
    const lines = [];
    if (preview.changes?.length) lines.push(`將更新 ${preview.changes.length} 筆`);
    else lines.push('沒有需要寫入的差異');
    if (preview.missing?.length) lines.push(`${preview.missing.length} 筆對不到檔案（路徑＋檔名）`);
    $('#catalogSummary').textContent = lines.join('。') + '。';
    for (const change of (preview.changes || []).slice(0, 30)) {
      const row = document.createElement('div');
      row.className = 'revision-row';
      row.textContent = `${change.path} / ${change.filename}：${Object.keys(change.patch).join('、')}`;
      box.append(row);
    }
    for (const miss of (preview.missing || []).slice(0, 20)) {
      const row = document.createElement('div');
      row.className = 'revision-row';
      row.textContent = `找不到：${miss.path} / ${miss.filename}（${miss.reason}）`;
      box.append(row);
    }
    $('#catalogApply').disabled = !preview.changes?.length;
    $('#catalogDialog').showModal();
  } catch (err) { flashHint(err.message); }
});
$('#catalogDialog').addEventListener('close', async () => {
  if ($('#catalogDialog').returnValue !== 'apply' || !catalogCsv) return;
  try {
    const result = await projectRequest('/api/library/catalog', 'POST', { csv: catalogCsv, apply: true });
    await refreshLibrary();
    flashHint(`已寫入 ${result.changes?.length || 0} 筆圖說資料`);
  } catch (err) { flashHint(err.message); }
  catalogCsv = '';
});


/* ── 上傳 ───────────────────────────────────────────────────── */
const reviewUi=await initUploadReviewUI({getState:()=>state,flashHint,refreshLibrary});
/**
 * 進行中的上傳。瀏覽器的上傳進度只代表已寫入 socket，中止後緩衝內的資料仍可能送達，
 * 所以取消除了中止 XHR，也通知伺服器（uploadId）：未收齊不入庫，已入庫的新項目移到封存。
 */
const uploadRun = { xhr: null, id: '', cancelled: false, cancelResult: null };
const newUploadId = () => `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
function trackUploadXhr(xhr, id) {
  uploadRun.xhr = xhr;
  uploadRun.id = id;
  xhr.addEventListener('loadend', () => { if (uploadRun.xhr === xhr) uploadRun.xhr = null; });
}
function cancelUpload() {
  if(reviewUi.enabled&&reviewUi.active){reviewUi.cancel();return;}
  if (uploadRun.cancelled) return;
  uploadRun.cancelled = true;
  if (uploadRun.xhr) {
    const id = uploadRun.id;
    uploadRun.xhr.abort();
    uploadRun.cancelResult = fetch(`/api/library/upload/cancel?uploadId=${encodeURIComponent(id)}`, { method: 'POST' })
      .then(r => r.json()).catch(() => null);
  }
  syncUploadCancel();
}
function syncUploadCancel() {
  const button = $('#uploadCancel');
  button.disabled = uploadRun.cancelled;
  button.textContent = uploadRun.cancelled ? '取消中…' : '取消上傳';
  button.title = '中止目前檔案並停止後續檔案；ZIP 若已在伺服器解壓，會完成匯入';
}
$('#uploadCancel').addEventListener('click', cancelUpload);

/** 用 XHR 才能回報真實上傳進度（fetch 沒有 upload progress）。body 可為 File／Blob／ArrayBuffer。 */
function upload(name, body, source, projectId = ['all', 'unassigned'].includes(state.selectedProject) ? null : state.selectedProject, folderId = projectId ? state.selectedFolder : null, zipMode = 'package', onProgress = null) {
  const zip = /\.zip$/i.test(name) && projectId && zipMode === 'folders' ? '&zip=folders' : '';
  const uploadId = newUploadId();
  const url = `/api/library/upload?name=${encodeURIComponent(name)}&source=${source || 'upload'}&project=${encodeURIComponent(projectId || '')}&folder=${encodeURIComponent(folderId || '')}${zip}&uploadId=${uploadId}`;
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', url);
    setUploadAuthHeaders(xhr);
    xhr.timeout = WAIT_TIMEOUT_MS;
    xhr.responseType = 'text';
    xhr.setRequestHeader('Content-Type', 'application/octet-stream');
    xhr.upload.onprogress = (e) => {
      if (!onProgress) return;
      if (e.lengthComputable) onProgress(e.loaded, e.total, 'upload');
      else onProgress(e.loaded, 0, 'upload');
    };
    xhr.upload.onload = () => { if (onProgress) onProgress(1, 1, 'processing'); };
    xhr.onload = () => {
      let j = null;
      try { j = JSON.parse(xhr.responseText); } catch { /* ignore */ }
      if (xhr.status >= 200 && xhr.status < 300 && j?.ok) resolve(j);
      else reject(new Error(j?.error || `上傳失敗（HTTP ${xhr.status}）`));
    };
    xhr.onerror = () => reject(new Error('網路錯誤，上傳中斷'));
    xhr.ontimeout = () => reject(new Error('上傳逾時'));
    xhr.onabort = () => reject(new Error('上傳已取消'));
    trackUploadXhr(xhr, uploadId);
    xhr.send(body);
  });
}

function setUploadProgress({ label, detail, loaded = 0, total = 0, phase = 'upload' } = {}) {
  const panel = $('#uploadProgress');
  panel.hidden = false;
  $('#uploadProgressLabel').textContent = label || '上傳中…';
  $('#uploadProgressDetail').textContent = detail || '';
  const bar = $('#uploadProgressBar');
  const pctEl = $('#uploadProgressPct');
  const busy = phase === 'processing' || !total;
  panel.classList.toggle('is-busy', busy);
  syncUploadCancel();
  if (busy) {
    pctEl.textContent = phase === 'processing' ? '處理中' : '…';
    bar.style.width = '';
  } else {
    const pct = Math.max(0, Math.min(100, Math.round((loaded / total) * 100)));
    pctEl.textContent = `${pct}%`;
    bar.style.width = `${pct}%`;
  }
  const busyText = detail || label || '上傳中…';
  $('#workspaceBusy').textContent = busyText;
  $('#workspaceBusy').hidden = false;
}

function hideUploadProgress() {
  $('#uploadProgress').hidden = true;
  $('#uploadProgress').classList.remove('is-busy');
  $('#uploadProgressBar').style.width = '0%';
  $('#workspaceBusy').hidden = true;
}

async function ingest(files) {
  const projectId = ['all', 'unassigned'].includes(state.selectedProject) ? null : state.selectedProject;
  const folderId = projectId ? state.selectedFolder : null;
  const all = [...files];
  // 圖說管理：可辨識與不認識的檔案都入庫；只略過不安全格式（執行檔／網頁／腳本等）。
  const accepted = all.filter(f => /\.zip$/i.test(f.name) || kindOfName(f.name));
  const msgs = [];
  if (all.length > accepted.length) {
    msgs.push(`已忽略 ${all.length - accepted.length} 個不安全或不允許的檔案（執行檔、網頁、腳本等）；其餘格式會保存，不認識的僅可下載`);
  }
  if (!accepted.length) { if (msgs.length) flashHint(msgs.join('；')); return; }
  if (state.workspace !== 'upload') showWorkspace('upload');
  let dup = 0, done = 0, notSent = 0;
  const reports = [], landedNames = [];
  Object.assign(uploadRun, { cancelled: false, cancelResult: null });
  $('#dropzone').setAttribute('aria-busy', 'true');
  try {
    for (let i = 0; i < accepted.length; i++) {
      if (uploadRun.cancelled) { notSent += accepted.length - i; break; }
      const file = accepted[i];
      const zip = /\.zip$/i.test(file.name);
      const indexLabel = `檔案 ${i + 1} / ${accepted.length} · `;
      setUploadProgress({
        label: `${indexLabel}上傳 ${file.name}`,
        detail: file.size ? `0 / ${fmtBytes(file.size)}` : '準備中…',
        loaded: 0, total: file.size || 0, phase: 'upload',
      });
      try {
        const j = await upload(file.name, file, zip ? 'zip' : 'upload', projectId, folderId, state.zipMode, (loaded, total, phase) => {
          if (phase === 'processing') {
            setUploadProgress({
              label: `${indexLabel}${zip ? '解壓／匯入' : '伺服器處理中'} ${file.name}`,
              detail: zip ? '請稍候，大型 ZIP 可能需要較久' : '寫入圖庫中…',
              phase: 'processing',
            });
            return;
          }
          const known = total > 0 ? total : file.size;
          setUploadProgress({
            label: `${indexLabel}上傳 ${file.name}`,
            detail: known ? `${fmtBytes(loaded)} / ${fmtBytes(known)}` : fmtBytes(loaded),
            loaded, total: known, phase: 'upload',
          });
        });
        done++;
        const results = j.items || [{ duplicate: j.duplicate, item: j.item }];
        if (j.report) { reports.push({ name: file.name, ...j.report }); continue; }
        for (const result of results) {
          if (result.duplicate) dup++;
          if (result.item?.name) landedNames.push(result.item.name);
        }
        if (j.package) msgs.push(`${file.name}：共 ${j.package.fileCount} 個檔案、${results.length} 個圖面，背景掃描外參後轉檔`);
      } catch (e) {
        if (uploadRun.cancelled) notSent++;
        else msgs.push(`${file.name}：${String(e?.message ?? e)}`);
      }
    }
  } finally {
    $('#dropzone').removeAttribute('aria-busy');
    hideUploadProgress();
  }
  const cancelled = uploadRun.cancelled;
  const outcome = cancelled ? await uploadRun.cancelResult : null;
  await refreshLibrary();
  // 上傳後留在文件清冊，方便確認新檔已出現；需看圖時再從清冊開啟。
  showWorkspace('library');
  if (cancelled) {
    msgs.unshift(`已取消上傳：${done} 個已入庫，${notSent} 個未入庫`);
    if (outcome?.archived) msgs.push(`取消前已寫入的 ${outcome.archived} 個項目已移到封存`);
    if (outcome?.kept === 'zip') msgs.push('ZIP 在取消前已傳完，伺服器會完成匯入，未回復');
  } else if (done && !reports.length) {
    const unique = [...new Set(landedNames)];
    msgs.unshift(unique.length === 1 ? `已上傳「${unique[0]}」` : `已上傳 ${done} 個檔案`);
  }
  if (dup) msgs.push(`${dup} 個項目與同一資料夾的既有內容相同，已沿用`);
  if (msgs.length) flashHint(msgs.join('；'));
  if (reports.length) showImportReport(reports);
}

/** ZIP 依目錄匯入的結果：新增／取代／未變更與略過清單。 */
function showImportReport(reports) {
  const dialog = document.createElement('dialog');
  dialog.className = 'meta-dialog import-report';
  const MAX_LIST = 50;
  dialog.innerHTML = `<form method="dialog"><h3>ZIP 目錄匯入完成</h3>${reports.map(r => `
    <section><h4>${escapeHtml(r.name)}</h4>
      <p>新建 ${r.foldersCreated} 個資料夾；新增 ${r.added}、取代 ${r.replaced}、內容相同未變更 ${r.unchanged} 個檔案${r.skipped.length ? `；略過 ${r.skipped.length} 個` : ''}。</p>
      ${r.truncated.length ? `<p class="warn">超過 ${20} 層的目錄已放在第 20 層：${r.truncated.slice(0, 5).map(escapeHtml).join('、')}${r.truncated.length > 5 ? '…' : ''}</p>` : ''}
      ${r.skipped.length ? `<ul class="import-skipped">${r.skipped.slice(0, MAX_LIST).map(s => `<li><span>${escapeHtml(s.path)}</span><small>${escapeHtml(s.reason)}</small></li>`).join('')}${r.skipped.length > MAX_LIST ? `<li>…另 ${r.skipped.length - MAX_LIST} 個</li>` : ''}</ul>` : ''}
    </section>`).join('')}
    <p class="muted">DWG／RVT 會在背景掃描外參後轉檔。被取代的檔案沿用圖說資料，但舊版的標註與書籤不保留。</p>
    <div class="meta-actions"><button class="btn btn-sm btn-primary" value="ok">關閉</button></div></form>`;
  dialog.addEventListener('close', () => dialog.remove());
  document.body.append(dialog);
  dialog.showModal();
}

/* ── 開啟 / 顯示 ────────────────────────────────────────────── */

async function showPackageReport(item) {
  await openReferencePanel(item, {fmtBytes, onChange: async () => {
    await refreshLibrary({silent: true});
    const current = activeItem();
    if (current?.id === item.id) await openItem(current, {layout: state.cadLayout?.name || undefined});
  }});
}

async function showPackageNotice(item) {
  state.notices = state.notices.filter(n => n.scope !== 'package');
  if (!CONVERTIBLE_KINDS.has(item.kind)) return;
  const seq = openSeq;
  try {
    const response = await fetch(`/api/library/${item.id}/references`, {cache:'no-store'});
    if (!response.ok) return;
    const report = await response.json();
    if (seq !== openSeq || state.activeId !== item.id) return;
    for (const node of report.nodes || []) {
      if (node.status !== 'complete') addNotice('warn', `外參掃描尚未完成：${node.name}；請開啟「外參」查看明細。`, 'package');
      for (const ref of node.references || []) {
        if (ref.font) continue;
        if (ref.status !== 'found') addNotice('warn', `外參待確認：${node.name} → ${ref.path}；請開啟「外參」指定檔案。`, 'package');
        else if (ref.updateAvailable) addNotice('info', `外參 ${ref.path} 已有其他現行版，目前保留原關聯版次。`, 'package');
      }
    }
  } catch { /* Reference details remain available from the document menu. */ }
}

function resolvedEngine() {
  return 'oda';
}

function showModal(item) {
  if(locationNavigation){
    locationNavigation.kind=item.kind==='pdf'?'pdf':item.kind==='image'?'images':['office','web','sheetset'].includes(item.kind)?'documents':/\.r(vt|fa)$/i.test(item.name)?'models':'drawings';
    locationNavigation.renderCatalog();
  }
  showWorkspace('viewer');
  setGroupTab(null);
  showReviewPanel('tools');
  $('#mTitle').textContent = docTitle(item);
  renderDocBadges(item);
  const revBtn = $('#btnRevisions');
  if (revBtn) revBtn.onclick = () => showRevisions(item);
  hideError();
}

function closeModal() {
  exitViewerMax();
  linkedViewer.reset();
  reviewTools.token++;
  reviewTools.stopWatch();
  reviewTools.updateRfiCount(0);
  flushAnnSave();         // 關掉前把標註寫回去（不 await：關視窗不該被網路卡住）
  if (state.ann) state.ann.setTool(null);
  state.annMode = false;
  state.annViewObs?.disconnect();
  state.annViewObs = null;
  if ($('#annTools')) $('#annTools').hidden = true;
  openSeq++;              // 讓還在飛的載入請求失效，避免它稍後把內容畫進已關閉的視窗
  meshSeq++;              // 3D 請求同理
  abortRequest('open');   // 而且真的把它取消掉，不要讓它繼續佔著連線
  abortRequest('mesh');
  $('#modal').hidden = true;
  closeDocPreview();
  state.activeId = null;
  state.activeDetail = null;
  state.mode3d = false;
  state.has2d = false;
  syncToolbarState();
  state.viewer3d?.stop?.();
  state.viewer3d?.clearGeometry();
  viewer.clear();
  renderList();
  showWorkspace('library');
  state.viewpoints = []; renderViewpoints(); sync3DTools();
}

/**
 * 換檔案時，把「上一個檔案留下的東西」全部清掉。
 *
 * 這是「不要吃到其他檔案的渲染」的關鍵：2D 畫布、3D 幾何、圖層面板、工具列按鈕
 * 都是共用的，只要有一個沒清到，新檔案就會露出舊檔案的內容。3D-only 的檔案
 * 不會走到 displayReady()，所以這裡必須自己清乾淨，不能依賴後續流程。
 */
function resetViewerForOpen() {
  linkedViewer.reset();
  state.cadLayouts = []; state.cadLayout = null; state.cadSvg = null;
  syncCadLayoutControl();
  closeDocPreview();
  state.mode3d = false;
  state.has2d = false;
  meshSeq++;              // 進行中的 3D 回應失效
  abortRequest('mesh');   // 同時取消，避免它繼續佔連線
  state.viewer3d?.stop?.();
  state.viewer3d?.setMesh?.(null);
  state.viewpoints = []; renderViewpoints();
  $('#view3d').hidden = true;
  $('#canvas').hidden = false;
  viewer.clear();
  $('#infoBadges').innerHTML = '';
  $('#statusLeft').textContent = '—';
  $('#v3dInfo').textContent = '';
  // 圖層面板：3D-only 的檔案不會呼叫 renderLayers()，不清就會留著上一個檔的圖層
  $('#layerCount').textContent = '0';
  $('#layerList').innerHTML = '';
  $('#layerNote').hidden = true;
  // 工具列按鈕**一律顯示**，不隨檔案／是否只有 3D 而變動（使用者要求一致）。
  // 「內嵌預覽圖」點下去才知道有沒有（伺服器會回 404 → 提示）；「3D」
  // 沒有 3D 時會顯示取得失敗的原因，比按鈕消失更好懂。
  // 只有「2D」是情境式的：這個檔沒有 2D 就沒有地方可回（見 LocationNavigation.sync）。
  if (state.ann) { state.ann.setItems([]); state.ann.setTool(null); state.ann.setActive(false); }
  state.annOther = [];
  state.annMode = false;
  state.annTool = null;
  state.annStaleKey = '';
  if ($('#annTools')) $('#annTools').hidden = true;
  syncAnnToolButtons();
  renderAnnList();
  clearNotices();
  hideLoading();
  hideError();
  syncToolbarState();
}

/**
 * 這個項目在指定引擎上「有沒有」某種產物 —— **只讀圖庫狀態，不發任何請求**。
 *
 * 回傳 'yes' | 'no' | 'unknown'（pending／converting 時還沒定案，必須問伺服器）。
 * 「沒有 2D 就直接開 3D」的判斷完全靠這個，所以不能為此先打一次 /svg：
 * 那正是會白等十幾分鐘、佔住連線的行為。
 */
function artifactState(item, engine, kind) {
  const st = item?.engines?.[engine];
  if (!st) return 'unknown';
  // ready / error 都是「已定案」；pending / converting 還沒有結果
  if (st.status !== 'ready' && st.status !== 'error') return 'unknown';
  return (kind === 'mesh' ? st.hasMesh3d : st.hasSvg) ? 'yes' : 'no';
}

/** 這個項目兩邊都失敗時，把 2D／3D 的原因一起寫出來（見 library.mjs 的 error2d／error3d）。 */
function conversionError(item, engine) {
  const st = item?.engines?.[engine] ?? {};
  const bits = [];
  if (st.error2d) bits.push(`2D：${st.error2d}`);
  if (st.error3d) bits.push(`3D：${st.error3d}`);
  if (!bits.length && st.error) bits.push(st.error);
  return bits.join('；') || '這個檔案沒有可顯示的結果';
}

function closeDocPreview() {
  documentRfi?.dispose();documentRfi=null;
  reviewTools.token++;reviewTools.stopWatch();reviewTools.context=null;reviewTools.notesSignature=null;reviewTools.updateRfiCount(0);$('#v3dNotesList').replaceChildren();
  pdfViewer?.close();
  imageViewer?.close();
  const frame = $('#webFrame');
  if (frame) { frame.src = 'about:blank'; frame.hidden = false; }
  docPreviewLabel = 'PDF';
  state.docMode = null;
  $('#pdfView').hidden = true;
  $('#imageView').hidden = true;
  $('#webView').hidden = true;
  $('#sheetSetView').hidden = true;
  $('#sheetSetView').replaceChildren();
}

async function loadPdfModule() {
  return import(new URL('assets/pdf-viewer.js', document.baseURI).href);
}

async function loadImageModule() {
  return import(new URL('assets/image-viewer.js', document.baseURI).href);
}

function rotationLabel(rotation) {
  return rotation ? ` · 旋轉 ${rotation}°` : '';
}

function onPdfChange({ page, pages, scale, rotation = 0 }) {
  documentRfi?.render();
  if (state.docMode !== 'pdf') return;
  const pct = Math.round(scale * 100);
  $('#zoomLabel').textContent = `${page}/${pages} · ${pct}%`;
  $('#zoomLabel').title = `第 ${page} 頁，共 ${pages} 頁；縮放 ${pct}%（以 PDF 原始尺寸為 100%）${rotationLabel(rotation)}`;
  $('#statusLeft').textContent = `${docPreviewLabel} · 第 ${page} / ${pages} 頁 · ${pct}%${rotationLabel(rotation)}`;
}

function onImageChange({ page, pages, scale, rotation = 0 }) {
  documentRfi?.render();
  if (state.docMode !== 'image') return;
  const pct = Math.round(scale * 100);
  $('#zoomLabel').textContent = pages > 1 ? `${page}/${pages} · ${pct}%` : `${pct}%`;
  $('#zoomLabel').title = pages > 1
    ? `第 ${page} 頁，共 ${pages} 頁；縮放 ${pct}%（以原始像素為 100%）${rotationLabel(rotation)}`
    : `縮放 ${pct}%（以原始像素為 100%）${rotationLabel(rotation)}`;
  $('#statusLeft').textContent = (pages > 1 ? `圖片 · 第 ${page} / ${pages} 頁 · ${pct}%` : `圖片 · ${pct}%`) + rotationLabel(rotation);
}

async function loadDrawingRfi(item) {
  if(!item||!viewer.svg||!state.has2d||state.mode3d||state.cadLayout||window.dcmsAuth&&!window.dcmsAuth.can('rfi.read',item))return;
  if(!documentRfi?.drawingRfi){
    documentRfi?.dispose();
    const preview={host:viewer.host,pages:[{n:1,el:viewer.host}],rotation:0};
    documentRfi=new DocumentRfiSurface(preview,{drawingViewer:viewer,notify:text=>{const hint=$('#documentRfiStatus');hint.textContent=text;hint.hidden=!text;},onModeChange:syncSurfaceTools});
  }
  await reviewTools.load(documentRfi,item,'oda');sync3DTools();
}

async function loadDocumentRfi(preview,item) {
  if(window.dcmsAuth&&!window.dcmsAuth.can('rfi.read',item))return;
  documentRfi?.dispose();
  documentRfi=new DocumentRfiSurface(preview,{notify:text=>{const hint=$('#documentRfiStatus');hint.textContent=text;hint.hidden=!text;},onModeChange:syncSurfaceTools});
  await reviewTools.load(documentRfi,item,'oda');
  sync3DTools();
}

/** PDF 用 pdf.js 顯示。Office 先向 /preview 取得轉好的 PDF，再走同一條路徑。 */
async function openPdf(item, seq, ctrl, { url, loading, label } = {}) {
  docPreviewLabel = label || 'PDF';
  state.docMode = 'pdf';
  $('#canvas').hidden = true;
  $('#view3d').hidden = true;
  $('#imageView').hidden = true;
  $('#pdfView').hidden = false;
  syncToolbarState();
  sync3DTools();
  showLoading(loading || '載入 PDF…');
  try {
    const [mod, res] = await Promise.all([loadPdfModule(), fetch(url || `/api/library/${item.id}/file`, { signal: ctrl.ac.signal })]);
    if (seq !== openSeq) return;
    if (!res.ok) {
      const j = await res.json().catch(() => null);
      throw new Error(j?.error || `HTTP ${res.status}`);
    }
    const data = await res.arrayBuffer();
    if (seq !== openSeq) return;
    pdfViewer ??= new mod.PdfViewer($('#pdfView'), { onChange: onPdfChange });
    const result = await pdfViewer.open(data);
    if (seq !== openSeq || !result) return;
    hideLoading();
    $('#infoBadges').innerHTML = `<span class="badge">${escapeHtml(docPreviewLabel)}</span><span class="badge">${result.pages} 頁</span><span class="badge">${fmtBytes(item.size)}</span>`;
    pdfViewer.emit();
    await loadDocumentRfi(pdfViewer,item);
    if(seq!==openSeq)return;
    $('#pdfView').focus({ preventScroll: true });
  } catch (e) {
    if (seq !== openSeq) return;
    hideLoading();
    showError(`${docPreviewLabel} 無法顯示：${String(e?.message ?? e)}`);
  } finally {
    endRequest('open', ctrl);
  }
}

async function openSheetSet(item, seq, ctrl) {
  state.docMode = 'sheetset';
  $('#canvas').hidden = true;
  $('#view3d').hidden = true;
  $('#sheetSetView').hidden = false;
  syncToolbarState();
  sync3DTools();
  showLoading('讀取 DST 圖紙集…');
  try {
    const response = await fetch(`/api/library/${item.id}/sheetset`, {signal: ctrl.ac.signal});
    const data = await response.json();
    if (seq !== openSeq) return;
    if (!response.ok) throw new Error(data.error || `HTTP ${response.status}`);
    renderSheetSet($('#sheetSetView'), data);
    hideLoading();
    $('#infoBadges').innerHTML = `<span class="badge">DST 圖紙集</span><span class="badge">${data.sheets.length} 張</span><span class="badge">${fmtBytes(item.size)}</span>`;
    $('#statusLeft').textContent = `圖紙集 · ${data.sheets.length} 張圖紙`;
    if (item.isCurrent === false) addNotice('warn', oldRevisionNotice(item), 'revision');
    $('#sheetSetView').focus({preventScroll: true});
  } catch (error) {
    if (seq !== openSeq) return;
    hideLoading();
    showError(`DST 無法預覽：${error.message}`);
  } finally {
    endRequest('open', ctrl);
  }
}

/** .url：https 開頭的第一行，或 Windows InternetShortcut 的 URL=。跨網域頁面放在隔離框架。 */
async function openWeb(item, seq, ctrl) {
  state.docMode = 'web';
  $('#canvas').hidden = true;
  $('#view3d').hidden = true;
  $('#pdfView').hidden = true;
  $('#imageView').hidden = true;
  $('#webView').hidden = false;
  syncToolbarState();
  sync3DTools();
  showLoading('開啟網頁…');
  const frame = $('#webFrame');
  frame.src = 'about:blank';
  frame.hidden = false;
  try {
    const res = await fetch(`/api/library/${item.id}/link`, { signal: ctrl.ac.signal });
    if (seq !== openSeq) return;
    const data = await res.json().catch(() => null);
    if (!res.ok) throw new Error(data?.error || `HTTP ${res.status}`);
    const target = new URL(data.url);
    if (target.protocol !== 'http:' && target.protocol !== 'https:') throw new Error('只接受 http 或 https 網址');
    if (seq !== openSeq) return;
    const shown = displayUrl(target.href);
    $('#webViewUrl').textContent = shown;
    $('#webViewUrl').title = shown;
    const open = $('#webViewOpen');
    open.href = target.href;
    const sameOrigin = target.origin === location.origin;
    $('#webViewNote').textContent = sameOrigin
      ? '此網址指向本系統，請用新分頁開啟。'
      : '若頁面空白，代表該網站不允許被嵌入，請改以新分頁開啟。';
    if (!sameOrigin) {
      frame.setAttribute('sandbox', 'allow-scripts allow-forms allow-popups allow-popups-to-escape-sandbox allow-downloads allow-same-origin');
      frame.src = target.href;
    } else {
      frame.hidden = true;
    }
    hideLoading();
    $('#infoBadges').innerHTML = `<span class="badge">網頁</span><span class="badge">${escapeHtml(target.hostname)}</span>`;
    $('#statusLeft').textContent = `網頁 · ${shown}`;
    $('#zoomLabel').textContent = '網頁';
    $('#webView').focus({ preventScroll: true });
  } catch (e) {
    if (seq !== openSeq) return;
    hideLoading();
    showError(`網頁無法開啟：${String(e?.message ?? e)}`);
  } finally {
    endRequest('open', ctrl);
  }
}

/** 點陣圖預覽（JPG／PNG／WebP／BMP／GIF／TIFF）；TIFF 以 UTIF 解碼。 */
async function openImage(item, seq, ctrl) {
  state.docMode = 'image';
  $('#canvas').hidden = true;
  $('#view3d').hidden = true;
  $('#pdfView').hidden = true;
  $('#imageView').hidden = false;
  syncToolbarState();
  sync3DTools();
  showLoading('載入圖片…');
  try {
    const [mod, res] = await Promise.all([loadImageModule(), fetch(`/api/library/${item.id}/file`, { signal: ctrl.ac.signal })]);
    if (seq !== openSeq) return;
    if (!res.ok) {
      const j = await res.json().catch(() => null);
      throw new Error(j?.error || `HTTP ${res.status}`);
    }
    const data = await res.arrayBuffer();
    if (seq !== openSeq) return;
    imageViewer ??= new mod.ImageViewer($('#imageView'), { onChange: onImageChange });
    const mime = res.headers.get('content-type') || '';
    const result = await imageViewer.open(data, { name: item.name, mime });
    if (seq !== openSeq || !result) return;
    hideLoading();
    const ext = (item.ext || '').slice(1).toUpperCase() || '圖片';
    const sizeBadge = result.width && result.height ? `${result.width}×${result.height}` : null;
    $('#infoBadges').innerHTML = [
      `<span class="badge">${escapeHtml(ext)}</span>`,
      result.pages > 1 ? `<span class="badge">${result.pages} 頁</span>` : '',
      sizeBadge ? `<span class="badge">${sizeBadge}</span>` : '',
      `<span class="badge">${fmtBytes(item.size)}</span>`,
    ].join('');
    imageViewer.emit();
    await loadDocumentRfi(imageViewer,item);
    if(seq!==openSeq)return;
    $('#imageView').focus({ preventScroll: true });
  } catch (e) {
    if (seq !== openSeq) return;
    hideLoading();
    showError(`圖片無法顯示：${String(e?.message ?? e)}`);
  } finally {
    endRequest('open', ctrl);
  }
}

function oldRevisionNotice(item, current = state.items.find(it => it.id === item.currentId)) {
  const prefix = '此為舊版圖說（非現行有效版）。';
  return current ? `${prefix}現行版次：${revisionParts(current.meta?.revision).number || '未標版次'}。` : prefix;
}

/** 紙空間有圖框、文字或圖塊才當預設圖紙；只剩視埠的預設版面改開模型空間。 */
function defaultPaperLayout(layouts) {
  return layouts.find(row => {
    if (row.model || !(row.paperWidthMm > 0) || !(row.paperHeightMm > 0)) return false;
    if (Number.isFinite(row.nonViewportCount)) return row.nonViewportCount > 0;
    return row.entityCount > 2;
  }) || null;
}

async function openItem(item, { layout } = {}) {
  if (isDownloadOnly(item)) { downloadItem(item); return; }
  item = await hydrateItemDetail(item);
  const seq = ++openSeq;              // 這一個請求的序號
  const ctrl = beginRequest('open');  // 同時取消上一個檔案還在飛的請求
  state.activeId = item.id;
  state.activeDetail = item;
  state.hiddenColors.clear();
  renderList();
  showModal(item);

  // 先把上一個檔案的圖清掉。即使後面的請求失敗，也絕不會看到舊內容
  // 掛在畫面上（那是「開新檔卻顯示舊檔」最直接的來源）。
  resetViewerForOpen();
  if (item.kind === 'sheetset') { await openSheetSet(item, seq, ctrl); return; }
  if (item.kind === 'pdf') { await openPdf(item, seq, ctrl); return; }
  if (item.kind === 'office' && isOfficePreviewable(item.name)) {
    await openPdf(item, seq, ctrl, {
      url: `/api/library/${item.id}/preview`,
      loading: '正在轉成 PDF…',
      label: officePreviewLabel(item.name),
    });
    return;
  }
  if (item.kind === 'image') { await openImage(item, seq, ctrl); return; }
  if (item.kind === 'web') { await openWeb(item, seq, ctrl); return; }
  showPackageNotice(item);
  if (item.isCurrent === false) {
    addNotice('warn', oldRevisionNotice(item), 'revision');
  }

  const engine = resolvedEngine();
  linkedViewer.setContext(item,engine);
  const st = item.engines?.[engine] ?? {};
  const has2d = artifactState(item, engine, 'svg');

  showLoading('讀取 CAD 版面…');
  if (/\.(dwg|dxf)$/i.test(item.ext || item.name)) {
    try {
      const response = await fetch(`/api/library/${item.id}/layouts`, { signal: ctrl.ac.signal });
      const result = await response.json();
      if (!response.ok) throw new Error(result.error || '無法讀取版面');
      if (seq !== openSeq) return;
      state.cadLayouts = result.layouts || [];
      const choice = layout ?? state.cadLayoutChoices.get(item.id);
      state.cadLayout = state.cadLayouts.find(row => !row.model && row.name === choice)
        || (choice === undefined ? defaultPaperLayout(state.cadLayouts) : null) || null;
      state.cadLayoutChoices.set(item.id, state.cadLayout?.name || '');
      syncCadLayoutControl();
    } catch (error) {
      if (seq !== openSeq) return;
      if (layout) { hideLoading(); showError(error.message); endRequest('open', ctrl); return; }
      addNotice('warn', `版面清單無法讀取，先顯示模型空間：${error.message}`);
    }
  }
  // 這個檔在這一顆引擎上已經確定沒有 2D 了 → 不要發 /svg（會白等一輪），直接開 3D。
  // 實例：38 MB 的 Revit「3D 視圖」，ODA 的 2D 匯出會跑滿 15 分鐘後逾時，
  // 但 3D 只要 74 秒 —— 舊版會在這裡留下「這個檔案沒有 2D 結果」的死路。
  if (!state.cadLayout && has2d === 'no') {
    hideLoading();
    endRequest('open', ctrl);
    if (artifactState(item, engine, 'mesh') === 'no') {
      showError(conversionError(item, engine));
      return;
    }
    addNotice('info', `此檔沒有可顯示的 2D 圖面（${st.skipped2d || st.error2d || st.error || '未提供 2D'}），已直接以 3D 顯示。`);
    await enter3D();
    return;
  }

  const layoutQuery = state.cadLayout ? `&layout=${encodeURIComponent(state.cadLayout.name)}` : '';
  const cached = st.status === 'ready' && st.hasSvg;
  showLoading(cached
    ? '載入中…（已轉好，讀取快取）'
    : `轉檔中…（引擎 ${engineLabel(engine)}${st.status === 'converting' ? '，背景處理中' : ''}）`);
  try {
    const [drawingRes, svgRes] = await Promise.all([
      fetch(`/api/library/${item.id}/drawing?engine=${engine}${layoutQuery}`, { signal: ctrl.ac.signal }),
      fetch(`/api/library/${item.id}/svg?engine=${engine}${layoutQuery}`, { signal: ctrl.ac.signal }),
    ]);
    if (seq !== openSeq) return;      // 已經被更新的開啟請求取代 → 丟棄
    if (!drawingRes.ok) {
      const j = await drawingRes.json().catch(() => null);
      throw new Error(j?.error || `HTTP ${drawingRes.status}`);
    }
    if (!svgRes.ok) {
      const j = await svgRes.json().catch(() => null);
      throw new Error(j?.error || `HTTP ${svgRes.status}`);
    }
    const drawing = await drawingRes.json();
    const svg = await svgRes.text();
    if (seq !== openSeq) return;      // 解 body 也要再檢查一次（body 很大時耗時明顯）
    hideLoading();
    displayReady(item, { drawing, svg, engine });
    refreshLibrary({ silent: true });
  } catch (e) {
    if (seq !== openSeq) return;      // 過期（或被取消）的請求：安靜丟棄，不要蓋掉新畫面
    hideLoading();
    if (state.cadLayout) { showError(`版面無法顯示：${String(e?.message ?? e)}`); return; }
    // 2D 這條路失敗了，但這個檔可能還是有 3D → 直接改開 3D，而不是留下一條死路。
    // 重新讀一次圖庫狀態：剛才那個請求可能順手把轉檔推到了定案。
    const fresh = state.items.find((x) => x.id === item.id) ?? item;
    if (artifactState(fresh, engine, 'mesh') !== 'no') {
      addNotice('info', `2D 無法顯示（${String(e?.message ?? e)}），已改用 3D。`);
      await enter3D();
      return;
    }
    showError(String(e?.message ?? e));
  } finally {
    endRequest('open', ctrl);
  }
}

function displayReady(item, { drawing, svg, engine }) {
  state.cadSvg = svg;
  state.cadLayout = drawing.meta?.selectedLayout || null;
  syncCadLayoutControl();
  const meta = { ...(drawing.meta ?? {}), engine };
  state.mode3d = false;
  state.has2d = true;          // 有 2D 才讓「2D」可用
  $('#view3d').hidden = true;
  $('#canvas').hidden = false;
  state.currentEngine = engine;
  applyCanvasAppearance();

  try {
    viewer.setContent(svg, { handleToLayer: meta.handleToLayer ?? [] });
    state.cadLayout ? viewer.fit() : viewer.fitContent();
  } catch (e) {
    showError(`圖面無法顯示：${String(e?.message ?? e)}`);
    return;
  }
  applyColorVisibility();
  applyCanvasAppearance();

  const warns = drawing.warnings ?? [];
  if (warns.length) addNotice('warn', warningSummary(warns));
  const index = meta.geometry?.indexLimitations;
  if (index?.count) {
    const summary = index.types.slice(0, 2).map(row => `${row.label} ×${row.count.toLocaleString()}`).join('、');
    addNotice('info', `幾何索引限制：${summary}${index.types.length > 2 ? `等 ${index.types.length} 類` : ''}，共 ${index.count.toLocaleString()} 個未納入輔助索引；圖面由 ODA 原生繪製，此數量不代表漏畫。`);
  }
  for(const ref of meta.xrefLoads||[])if(!ref.loaded&&!ref.unloaded)addNotice('warn',`外參找到但未成功載入：${ref.path}`);
  if(meta.recoveries?.length)addNotice('warn','此圖使用 ODA 修復讀取，原始檔未更動；請核對圖元是否完整。');
  if(meta.fontSubstitutions?.length)addNotice('warn',`${meta.fontSubstitutions.length} 個文字樣式使用中文字型替代；文字內容保留，字形與字寬可能和原始字型不同。`);

  renderBadges(item, drawing, engine);
  renderLayers(drawing);
  renderStatus(drawing, engine);

  // 標註層：2D 用圖面座標（viewBox 跟著縮放跑）
  if (state.cadLayout) {
    addNotice('info', '目前顯示完整紙空間版面；列印會輸出整張圖紙。釋疑、量測與對位請切回模型空間。');
    state.ann?.setVisible(false);
    sync3DTools();
  } else {
    attachAnnLayer();
    loadAnnotations(item, engine);
    loadDrawingRfi(item).catch(e=>flashHint(e.message));
  }
}

function renderBadges(item, drawing, engine) {
  const m = drawing.meta ?? {};
  const bits = [];
  bits.push(`<span class="badge hl">${engineLabel(engine)}</span>`);
  const st = item.engines?.[engine];
  if (st?.status === 'ready' && st.ms != null) bits.push(`<span class="badge">${(st.ms / 1000).toFixed(2)}s</span>`);
  if (m.entityCount != null) bits.push(`<span class="badge">實體 ${m.entityCount}</span>`);
  const nLayers = (drawing.layers ?? []).length;
  if (nLayers) bits.push(`<span class="badge">圖層 ${nLayers}</span>`);
  const warns = drawing.warnings ?? [];
  if (warns.length) bits.push(`<span class="badge warn">警告 ${warns.length}</span>`);
  const index = m.geometry?.indexLimitations;
  if (index?.count) bits.push(`<span class="badge" title="${escapeHtml(index.types.map(row => `${row.label} (${row.type}) ×${row.count}`).join('；'))}">索引限制 ${index.count}</span>`);
  bits.push(`<span class="badge">${fmtBytes(item.size)}</span>`);
  $('#infoBadges').innerHTML = bits.join('');
}

function renderStatus(drawing, engine) {
  const m = drawing.meta ?? {};
  const bits = [`引擎 ${engineLabel(engine)}`];
  if (m.entityTypes?.length) bits.push(m.entityTypes.slice(0, 5).map((t) => `${t.type}×${t.count}`).join('、'));
  if (viewer.hasContent) bits.push(`縮放 ${Math.round(viewer.zoom() * 100)}%`);
  $('#statusLeft').textContent = bits.join(' · ');
  $('#zoomLabel').textContent = `${Math.round(viewer.zoom() * 100)}%`;
}

viewer.onViewChange = ({ zoom }) => {
  if(documentRfi?.drawingRfi)documentRfi.render();
  if (state.mode3d) return;
  $('#zoomLabel').textContent = `${Math.round(zoom * 100)}%`;
};

/* ── 圖層 / 顏色面板 ────────────────────────────────────────── */
function renderLayers(drawing) {
  const layers = drawing.layers ?? [];
  $('#layerCount').textContent = String(layers.length);
  const ul = $('#layerList');
  ul.innerHTML = '';
  const note = $('#layerNote');

  const hasHandleMap = (drawing.meta?.handleToLayer?.length ?? 0) > 0;
  if (note) {
    note.hidden = hasHandleMap || !layers.length;
    note.textContent = hasHandleMap
      ? ''
      : 'ODA 產生的 SVG 依「顏色」分組，不含圖層/handle 資訊，因此點色塊是切換顏色，不是切換圖層。';
  }

  if (!layers.length) {
    ul.innerHTML = '<li class="layer-item"><span class="layer-name">（無圖層資訊）</span></li>';
    return;
  }
  const used = layers.filter((l) => (l.count ?? 0) > 0);
  for (const l of (used.length ? used : layers)) {
    const li = document.createElement('li');
    li.className = 'layer-item' + ((l.off || l.frozen) ? ' off' : '');
    const color = l.color || '#8b9bb4';
    const hidden = state.hiddenColors.has(color.toLowerCase());
    li.innerHTML = `
      <span class="layer-swatch is-toggle" style="background:${color}" title="切換此顏色"></span>
      <span class="layer-name" title="${escapeHtml(l.name)}">${escapeHtml(l.name)}</span>
      ${l.locked ? '<span class="layer-flag" title="鎖定">🔒</span>' : ''}
      ${l.off ? '<span class="layer-flag" title="關閉">off</span>' : ''}
      ${l.frozen ? '<span class="layer-flag" title="凍結">❄</span>' : ''}
      <span class="layer-count">${l.count ?? 0}</span>`;
    if (hidden) li.classList.add('off');
    li.addEventListener('click', () => {
      const key = color.toLowerCase();
      if (state.hiddenColors.has(key)) state.hiddenColors.delete(key);
      else state.hiddenColors.add(key);
      li.classList.toggle('off', state.hiddenColors.has(key));
      applyColorVisibility();
    });
    ul.appendChild(li);
  }
}

function rgbToHex(v) {
  const m = /rgb\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*\)/i.exec(v);
  if (!m) return v.startsWith('#') ? v.toLowerCase() : null;
  const [r, g, b] = m.slice(1).map(Number);
  return `#${[r, g, b].map((n) => n.toString(16).padStart(2, '0')).join('')}`;
}

function applyColorVisibility() {
  const svg = viewer.svg;
  if (!svg) return;
  for (const g of svg.querySelectorAll('g[stroke]')) {
    const rgb = rgbToHex((g.getAttribute('stroke') || '').toLowerCase());
    g.style.display = (rgb && state.hiddenColors.has(rgb)) ? 'none' : '';
  }
}

function resetLayers() {
  state.hiddenColors.clear();
  applyColorVisibility();
  for (const li of $$('#layerList .layer-item')) li.classList.remove('off');
}

/** Viewer-only appearance: CAD follows the chosen background; document pages keep their colors. */
function applyCanvasAppearance() {
  const dark = state.viewerBackground === 'dark';
  const baseIsPaper = state.currentEngine !== 'libredwg';
  const needInvert = state.invertOverride ?? (dark ? baseIsPaper : !baseIsPaper);
  for (const id of ['canvas', 'linkedCanvas']) {
    const el = $('#' + id);
    if (!el) continue;
    el.classList.toggle('paper', baseIsPaper);
    el.classList.toggle('invert', needInvert);
  }
  $('#modal').querySelector('.canvas-wrap').dataset.viewerBackground = state.viewerBackground;
  state.invert = needInvert;
  if (state.viewer3d) {
    state.viewer3d.setBackground(dark ? '#171c24' : '#f3f5f8');
    state.viewer3d.renderOnce();
  }
  syncToolbarState();
}

/** 更新工具列的作用中狀態與無障礙屬性。 */
function syncCadLayoutControl() {
  const control = $('#cadLayoutControl'), select = $('#cadLayoutSelect');
  control.hidden = !state.cadLayouts.length || state.mode3d || Boolean(state.docMode);
  select.replaceChildren(new Option('模型空間', ''));
  for (const row of state.cadLayouts.filter(row => !row.model)) select.add(new Option(row.name, row.name));
  select.value = state.cadLayout?.name || '';
  $('#cadPaperSize').textContent = state.cadLayout
    ? `${Math.round(state.cadLayout.paperWidthMm)} × ${Math.round(state.cadLayout.paperHeightMm)} mm` : '';
  $('#btnPrint').textContent = state.cadLayout && !state.mode3d ? '出圖／PDF' : '列印';
  $('#btnPrint').title = state.cadLayout && !state.mode3d
    ? '列印完整圖紙或另存 PDF；依版面紙張尺寸出圖，列印對話框請選 100% 比例並關閉頁首頁尾' : '列印目前檢視，附浮水印與紙本版本查詢 QR Code';
}
$('#cadLayoutSelect').addEventListener('change', () => {
  const item = activeItem();
  if (item) void openItem(item, { layout: $('#cadLayoutSelect').value });
});

function syncToolbarState() {
  syncCadLayoutControl();
  $('#sideAnnotToggle').disabled = Boolean(state.cadLayout && !state.mode3d) || state.docMode === 'sheetset';
  for (const button of $$('[data-review="measure2d"], [data-review="linked"], [data-review="annotations"]')) button.disabled = Boolean(state.cadLayout && !state.mode3d);
  const setPressed = (el, value) => {
    if (!el) return;
    el.setAttribute('aria-pressed', String(Boolean(value)));
    el.classList.toggle('is-active', Boolean(value));
  };
  setPressed($('#btnInvert'), state.invert);
  const backgroundButton = $('#btnViewerBackground');
  backgroundButton.textContent = state.viewerBackground === 'dark' ? '背景：深色' : '背景：淺色';
  backgroundButton.title = state.viewerBackground === 'dark' ? '切換為淺色背景' : '切換為深色背景';
  setPressed(backgroundButton, state.viewerBackground === 'dark');
  $('#sideAnnotToggle').textContent = state.annMode ? '結束標註' : '新增標註';
  const doc = isDocPreview();
  const pdf = state.docMode === 'pdf';
  const image = state.docMode === 'image';
  $('#rotateLeft').hidden = !doc;
  $('#rotateRight').hidden = !doc;
  $('#modal').dataset.mode = state.docMode || (state.mode3d ? '3d' : '2d');
  $('#btnActual').disabled = state.mode3d && !doc;
  $('#btnFitAll').hidden = state.mode3d && !doc;
  $('#btnFit').textContent = doc ? '寬度' : state.mode3d ? '全模型' : '主要';
  $('#btnFitAll').textContent = doc ? '整頁' : '全圖';
  $('#btnActual').textContent = doc ? '100%' : '1:1';
  locationNavigation?.sync();
  $('#btnFit').title = pdf ? '符合頁寬 (F)' : image ? '符合圖寬 (F)' : '主要範圍：2D 略過遠處零散物件；3D 為全模型 (F)';
  $('#btnFitAll').title = pdf ? '整頁顯示目前頁面 (0)' : image ? '整圖顯示 (0)' : '全圖：包含所有物件 (0)';
  $('#btnActual').title = pdf ? 'PDF 原始尺寸 (1)' : image ? '原始像素 (1)' : state.mode3d ? '1:1 僅適用於 2D 圖面' : '原始比例 (1)';
}

/* ── 3D ─────────────────────────────────────────────────────── */

/**
 * 讀完一個回應，並回報進度。
 *
 * 大 mesh 的二進位可以到 200 MB 以上，`res.arrayBuffer()` 中間沒有任何回饋，
 * 使用者只會盯著「取得 3D 幾何…」發呆。這裡用 ReadableStream 邊收邊報，而且
 * **先按 Content-Length 配好一塊**，不要一直重新配置記憶體。
 */
async function readBodyWithProgress(res, onProgress) {
  const total = Number(res.headers.get('content-length')) || 0;
  if (!res.body?.getReader) {
    const buf = await res.arrayBuffer();
    onProgress?.(buf.byteLength, buf.byteLength);
    return buf;
  }
  const reader = res.body.getReader();
  const out = total ? new Uint8Array(total) : null;
  const chunks = out ? null : [];
  let got = 0;
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    if (out) out.set(value, got); else chunks.push(value);
    got += value.byteLength;
    onProgress?.(got, total);
  }
  if (out) return out.buffer;
  const merged = new Uint8Array(got);
  let off = 0;
  for (const c of chunks) { merged.set(c, off); off += c.byteLength; }
  return merged.buffer;
}

async function loadViewer3DModule() {
  if (!state.viewer3dMod) {
    state.viewer3dMod = await import(new URL('assets/viewer3d.js', document.baseURI).href);
  }
  return state.viewer3dMod;
}

/**
 * 進入 3D。
 *
 */
const manualViewRequests = new Map();
let viewChoiceSeq = 0;
async function prepareViewOutput(item, mode) {
  const key = `${item.id}:${mode}`;
  if (!manualViewRequests.has(key)) {
    flashHint(`正在準備 ${mode.toUpperCase()} 檢視，首次切換可能需要轉檔…`);
    const button = $(mode === '2d' ? '#viewMode2D' : '#viewMode3D');
    button.setAttribute('aria-busy', 'true');
    button.textContent = `${mode.toUpperCase()}…`;
    const request = (async () => {
      const response = await fetch(`/api/library/${item.id}/convert?mode=${mode}`, { method: 'POST' });
      const data = await response.json();
      if (!response.ok || !data.ok) throw new Error(data.error || '檢視轉檔失敗');
      const index = state.items.findIndex(i => i.id === item.id);
      if (index >= 0) state.items[index] = data.item;
      if (state.activeId === item.id) state.activeDetail = data.item;
      return data.item;
    })().finally(() => {
      manualViewRequests.delete(key);
      if (![...manualViewRequests.keys()].some(k => k.endsWith(`:${mode}`))) {
        button.removeAttribute('aria-busy');
        button.textContent = mode.toUpperCase();
      }
    });
    manualViewRequests.set(key, request);
  }
  return manualViewRequests.get(key);
}

async function enter2D() {
  const item = activeItem(), seq = openSeq, choice = ++viewChoiceSeq;
  if (!item || state.docMode) return;
  if (state.has2d) { exit3D(); return; }
  try {
    const ready = await prepareViewOutput(item, '2d');
    if (state.activeId !== item.id || seq !== openSeq || choice !== viewChoiceSeq) return;
    await flushAnnSave();
    if (state.activeId !== item.id || seq !== openSeq || choice !== viewChoiceSeq) return;
    await openItem(ready);
  } catch (e) { if (state.activeId === item.id && seq === openSeq && choice === viewChoiceSeq) flashHint(e.message); }
}

async function enter3D({ manual = false } = {}) {
  const choice = manual ? ++viewChoiceSeq : viewChoiceSeq;
  let item = activeItem();
  if (!item || state.mode3d) return;
  if (manual) {
    const opening = openSeq;
    try { item = await prepareViewOutput(item, '3d'); }
    catch (e) { if (state.activeId === item.id && opening === openSeq && choice === viewChoiceSeq) flashHint(e.message); return; }
    if (state.activeId !== item.id || opening !== openSeq || choice !== viewChoiceSeq) return;
  }
  item = await hydrateItemDetail(item);
  await flushAnnSave();        // 2D 的標註先落地，免得被 3D 的載入蓋掉
  const seq = ++meshSeq;
  const ctrl = beginRequest('mesh');   // 取消上一個還在飛的 3D 請求
  const engine = resolvedEngine();
  documentRfi?.surfaceTools.setMode(null);
  state.mode3d = true;
  $('#canvas').hidden = true;
  $('#view3d').hidden = false;
  $('#v3dLoading').hidden = false;
  $('#v3dLoadingText').textContent = `取得 3D 幾何…（引擎 ${engineLabel(engine)}）`;
  $('#v3dInfo').textContent = '—';
  syncToolbarState();
  try {
    const mod = await loadViewer3DModule();
    if (!state.viewer3d) state.viewer3d = new mod.Viewer3D($('#view3d'));
    applyCanvasAppearance();
    state.viewer3d.canNavigate = () => state.workspace === 'viewer' && state.mode3d && !state.annMode && !$('#modal').hidden && Boolean(state.viewer3d.bbox);
    state.viewer3d.onNavigationChange = sync3DTools;
    state.viewer3d.surfaceTools.onStatus = text => {
      $('#v3dPickStatus').textContent = text; $('#v3dPickStatus').hidden = !text;
    };
    state.viewer3d.surfaceTools.onChange = syncSurfaceTools;
    if (seq !== meshSeq) return;

    // 先把上一個檔案的幾何清掉。這樣即使接下來請求失敗，也絕不會露出舊模型。
    state.viewer3d.setMesh(null);
    resetBoxInputs();
    $('#v3dPickStatus').hidden = true;
    sync3DTools();

    // closeModal() 會 stop() 掉 render loop 以省 CPU，所以每次進來都要重新
    // 啟動。少了這行，setMesh() 換了資料也不會重畫 —— canvas 用
    // preserveDrawingBuffer，會一直停在最後一次畫的畫面（＝上一張圖的 3D）。
    state.viewer3d.start();

    // 先試**二進位**（大圖唯一送得出去的形式：沒有 512 MB 單一字串上限）。
    // 舊伺服器／舊圖庫可能沒有 .bin 或直接回 JSON，這裡看 Content-Type 判斷，
    // 兩種都吃得下；真的沒有 3D 才退回 JSON 端點拿錯誤訊息。
    let meshForInfo = null, workspaceBytes = null;
    // cache-buster：mesh 會在同一個 URL 上被重新轉檔改掉，瀏覽器可能還留著舊的
    // （尤其先前用過 immutable 快取）。帶上產物 mtime 就一定是新的。
    const meshVersion = item.engines?.[engine]?.meshVersion;
    const binUrl = `/api/library/${item.id}/mesh?engine=${engine}&format=bin`
      + (meshVersion ? `&v=${meshVersion}` : '');
    const binRes = await fetch(binUrl, { signal: ctrl.ac.signal });
    if (seq !== meshSeq) return;
    const isBinary = binRes.ok && !(binRes.headers.get('content-type') || '').includes('json');
    if (isBinary) {
      const buf = await readBodyWithProgress(binRes, (got, total) => {
        const mb = (got / 1048576).toFixed(0);
        $('#v3dLoadingText').textContent = `取得 3D 幾何…（引擎 ${engineLabel(engine)}`
          + (total ? `，${Math.round((got / total) * 100)}%` : `，${mb} MB`) + '）';
      });
      if (seq !== meshSeq) return;
      state.viewer3d.setWireframe($('#v3dWire').checked);
      state.viewer3d.resize();
      workspaceBytes = new Uint8Array(buf);
      meshForInfo = state.viewer3d.setMeshBinary(workspaceBytes);
    } else {
      // 舊伺服器把 ?format=bin 當一般請求回了 JSON → 直接吃；否則再打一次 JSON 端點
      let j = binRes.ok ? await binRes.json().catch(() => null) : null;
      if (!j?.ok) {
        const r = await fetch(`/api/library/${item.id}/mesh?engine=${engine}`, {
          signal: ctrl.ac.signal,
        });
        j = await r.json().catch(() => null);
        if (seq !== meshSeq) return;
        if (!r.ok || !j?.ok) throw new Error(j?.error || `HTTP ${r.status}`);
      }
      state.viewer3d.setWireframe($('#v3dWire').checked);
      state.viewer3d.resize();
      state.viewer3d.setMesh(j.mesh3d);
      meshForInfo = j.mesh3d;
      workspaceBytes = mesh3dToBinary(j.mesh3d);
    }
    if (seq !== meshSeq) return;
    state.viewer3d.frameAll();
    $('#v3dEyeOffset').value = String(Math.max((state.viewer3d.bbox.max[2] - state.viewer3d.bbox.min[2]) * .02, state.viewer3d.modelSpan() * .001));
    applyClip(); applyDisplay(); sync3DTools();
    loadViewpoints(item, engine);
    await reviewTools.load(state.viewer3d,item,engine);
    if(seq!==meshSeq)return;
    await linkedViewer.ready3D(workspaceBytes,item,engine);
    if(seq!==meshSeq)return;
    for (const button of document.querySelectorAll('.vv')) {
      const active = button.dataset.view === 'iso';
      button.classList.toggle('on', active);
      button.setAttribute('aria-pressed', String(active));
    }
    // 3D 標註：畫面座標（0..1）＋ 視點
    attachAnnLayer();
    state.viewer3d.onCameraChange = () => {
      linkedViewer.cameraChanged();
      syncCameraHeight();
      if (state.mode3d) $('#zoomLabel').textContent = `${Math.round(state.viewer3d.zoom() * 100)}%`;
      const layer = state.ann;
      if (!layer || layer.space !== 'screen') return;
      layer.setCurrentViewpoint(state.viewer3d.getViewpoint());
      // 只有在「哪些標註算過期」真的變了才重畫清單，避免拖曳時每 frame 重建 DOM
      const key = layer.staleIds().sort().join(',');
      if (key !== state.annStaleKey) { state.annStaleKey = key; renderAnnList(); }
    };
    state.ann.setCurrentViewpoint(state.viewer3d.getViewpoint());
    $('#zoomLabel').textContent = '100%';
    loadAnnotations(item, engine);
    render3DBadges(item, engine, meshForInfo);
    update3DInfo(meshForInfo);
    // 「為什麼只有 3D」「有沒有被取樣」「有沒有 3D 幾何」全部走同一個提醒出口
    const mst = meshForInfo?.stats ?? {};
    for(const ref of mst.xrefLoads||[])if(!ref.loaded&&!ref.unloaded)addNotice('warn',`外參未成功載入：${ref.path}`);
    if(mst.recoveries?.length)addNotice('warn','此模型使用 ODA 修復讀取，原始檔未更動；請核對圖元是否完整。');
    if(mst.fontSubstitutions?.length)addNotice('warn',`${mst.fontSubstitutions.length} 個文字樣式使用中文字型替代；文字內容保留，字形與字寬可能和原始字型不同。`);
    const faces = mst.faces ?? state.viewer3d.info().faces ?? 0;
    if (mst.truncated) {
      addNotice('limit', mst.truncatedBy === 'stride'
        ? `3D 已達顯示上限：均勻取樣約 ${Math.round((mst.coverage ?? 0) * 100)}% 的元件`
          + `（原圖約 ${fmtBytes(mst.estFullBytes ?? 0)}，上限 ${fmtBytes(mst.byteLimit ?? 0)}）`
        : '3D 已達顯示上限（部分圖元未收進來）');
    }
    const skipped = mst.skippedByType ?? {};
    const skipBits = Object.entries(skipped).sort((a, b) => b[1] - a[1]).slice(0, 3)
      .map(([k, v]) => `${k}×${v}`).join('、');
    if (skipBits) addNotice('warn', `有些圖元型別未支援、已略過：${skipBits}`);
    if (faces === 0) {
      addNotice('warn', mst.lines
        ? '此圖沒有可三角化的曲面——只找到線段。LibreDWG 沒有 ACIS 曲面細分器，切到 ODA 通常就有面。'
        : '此圖沒有 3D 幾何。');
    }
  } catch (e) {
    if (seq !== meshSeq) return;
    state.mode3d = false;
    sync3DTools();
    syncToolbarState();
    $('#view3d').hidden = true;
    $('#canvas').hidden = false;
    // 失敗時幾何已經被清空，不會留下上一個檔案的内容
    showError(`3D 取得失敗：${String(e?.message ?? e)}`);
    if(state.has2d)loadDrawingRfi(activeItem()).catch(e=>flashHint(e.message));
  } finally {
    endRequest('mesh', ctrl);
    if (seq === meshSeq) $('#v3dLoading').hidden = true;
  }
}

function exit3D() {
  linkedViewer.cancel();linkedViewer.setSplit(false);
  if (!state.has2d) return;  // 這個檔沒有 2D，退回去只會看到空白
  meshSeq++;                 // 讓還在飛的 3D 請求失效
  abortRequest('mesh');      // 而且真的取消掉
  state.mode3d = false;
  state.viewer3d?.setNavigationMode('orbit');
  sync3DTools();
  state.viewer3d?.stop?.();  // 看不見時不要空轉
  $('#view3d').hidden = true;
  $('#canvas').hidden = false;
  applyCanvasAppearance();
  if (openGroupTab()) { setGroupTab(null); syncDrawer(); }
  syncToolbarState();
  state.cadLayout ? viewer.fit() : viewer.fitContent();
  if (state.cadLayout) state.ann?.setVisible(false);
  state.viewer3d?.surfaceTools.setMode(null);
  loadDrawingRfi(activeItem()).catch(e=>flashHint(e.message));
}

function update3DInfo(mesh) {
  const info = state.viewer3d?.info();
  if (!info) return;
  // 統計行**只放數字**；「有沒有被取樣」「哪些型別未支援」都走 addNotice()。
  $('#v3dInfo').textContent =
    `3D：面 ${info.faces}（三角 ${info.triangles}）· 線 ${info.lines} · 點 ${info.points} · 頂點 ${info.vertices}`;
}

/* ── 標註 / 議題（圖形溝通）────────────────────────────────────
   設計要點（見 lib/annotations.js 的說明）：
   - **原圖完全不會被改**：標註是 sidecar JSON（`<id>.<engine>.annotations.json`），
     要交換時才匯出（PDF 標註／DWG 註解層）。
   - 座標系有兩種：2D 用**圖面座標**（跟 viewBox 同步），3D 用**正規化畫面座標**
     ＋當時的視點。兩者不相容，所以分開存、清單只顯示當前這一種。
   - 存檔要**合併另一個座標系**的標註，不然在 3D 按一下就會把 2D 的洗掉。 */

/** 取得（必要時建立）標註層。 */
function annLayer() {
  if (!state.ann) {
    const host = document.querySelector('.canvas-wrap');
    state.ann = new AnnotationLayer({ host, space: 'drawing' });
    state.ann.onchange = () => scheduleAnnSave();
    state.ann.onselect = () => renderAnnList();
    // 3D 標註存的是畫面座標，所以相機一動就要重算「哪些是別的視角畫的」
    state.ann.onstalechange = () => renderAnnList();
    state.ann.onstatus = (t) => { const el = $('#annStatus'); if (el) el.textContent = t; };
    // 3D 的標註要記住當下的視點（重開才對得回同一個畫面）
    state.ann.viewpointProvider = () => state.viewer3d?.getViewpoint?.() ?? null;
  }
  return state.ann;
}

/** 把標註層掛到正確的宿主與座標系（2D：圖面座標；3D：畫面座標）。 */
function attachAnnLayer() {
  const layer = annLayer();
  const wrap = document.querySelector('.canvas-wrap');
  const annNote = $('#annNote');
  if (state.mode3d) {
    state.annViewObs?.disconnect();
    state.annViewObs = null;
    layer.attach(linkedViewer.split ? $('#view3d') : wrap, 'screen');
    if (annNote) annNote.innerHTML = '3D 的雲狀圖、箭頭、手繪、文字與方框請用工具列「釋疑」，圖形會釘在模型上並保留編號。這裡的標註仍貼在畫面上。';
  } else {
    if (annNote) annNote.innerHTML = '在圖面上加註解：雲狀圖、箭頭、文字。<b>原圖不會被修改</b>，要交換時再匯出。';
    layer.attach(wrap, 'drawing');
    const svg = $('#canvas svg');
    if (svg) {
      const sync = () => {
        const vb = svg.getAttribute('viewBox');
        if (!vb) return;
        const [x, y, w, h] = vb.split(/[\s,]+/).map(Number);
        if (Number.isFinite(w) && w > 0) layer.setViewBox({ x, y, w, h });
      };
      sync();
      // 縮放／平移是**改 viewBox**，所以用 MutationObserver 跟著跑（不必動 svg-viewer）
      state.annViewObs?.disconnect();
      state.annViewObs = new MutationObserver(sync);
      state.annViewObs.observe(svg, { attributes: true, attributeFilter: ['viewBox'] });
    }
  }
  layer.setVisible(true);
}

/** 載入這個檔（這顆引擎）的標註。 */
async function loadAnnotations(item, engine) {
  const layer = annLayer();
  state.annOther = [];
  layer.setItems([]);
  renderAnnList();
  try {
    const r = await fetch(`/api/library/${item.id}/annotations?engine=${engine}`);
    if (!r.ok) return;                                  // 舊伺服器或沒有標註 → 空的
    const j = await r.json();
    if (activeItem()?.id !== item.id) return;           // 已經換檔了
    const all = Array.isArray(j.items) ? j.items : [];
    // 只顯示與目前座標系相符的；另一種留著，存檔時要一起寫回去
    state.annOther = all.filter((a) => a.space !== layer.space);
    layer.setItems(all.filter((a) => a.space === layer.space));
    if (layer.space === 'screen' && state.viewer3d) layer.setCurrentViewpoint(state.viewer3d.getViewpoint());
  } catch { /* 讀不到就當作空的 */ }
  renderAnnList();
}

/** 變更後排程存檔（去抖：連續拖曳只送一次）。 */
function scheduleAnnSave() {
  if(window.dcmsAuth&&!['annotation.create','annotation.update','annotation.delete','annotation.resolve','annotation.reply'].some(action=>window.dcmsAuth.can(action,activeItem(),window.dcmsAuth.user.id)))return;
  state.annDirty = true;
  updateAnnCount();
  renderAnnList();
  clearTimeout(state.annSaveTimer);
  state.annSaveTimer = setTimeout(() => { saveAnnotations(); }, 700);
}

async function saveAnnotations() {
  const item = activeItem();
  const layer = state.ann;
  if (!item || !layer) return;
  const engine = resolvedEngine();
  const items = [...layer.getItems(), ...state.annOther];
  try {
    const r = await fetch(`/api/library/${item.id}/annotations?engine=${engine}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items }),
    });
    if (!r.ok) { flashHint(`標註儲存失敗（HTTP ${r.status}）——伺服器是舊版嗎？`); return; }
    state.annDirty = false;
    const j = await r.json().catch(() => null);
    if(j?.items)for(const annotation of [...layer.items,...state.annOther]){const saved=j.items.find(value=>value.id===annotation.id);if(saved)for(const key of ['createdBy','createdAt','updatedBy','updatedAt','author'])annotation[key]=saved[key];}
    updateAnnCount(j?.items?.length ?? items.length);
  } catch (e) {
    flashHint(`標註儲存失敗：${String(e?.message ?? e)}`);
  }
}

/** 切換檢視或關閉前把還沒送出的標註寫回去（不然會被下一次載入蓋掉）。 */
async function flushAnnSave() {
  if (!state.annDirty) return;
  clearTimeout(state.annSaveTimer);
  await saveAnnotations();
}

function updateAnnCount(n) {
  const el = $('#annCount');
  if (el) el.textContent = String(n ?? (state.ann?.items.length ?? 0));
}

/** 議題清單（編號與畫面上的徽章一致）。 */
function renderAnnList() {
  const ul = $('#annList');
  if (!ul) return;
  const items = state.ann?.items ?? [];
  updateAnnCount(items.length);
  if (!items.length) {
    ul.innerHTML = '<li class="an-empty">還沒有標註。按工具列「標註」，再選雲狀圖／箭頭／文字。</li>';
    return;
  }
  const staleCount = items.filter((it) => !state.ann.viewpointMatches(it)).length;
  const hint = staleCount
    ? `<li class="ann-hint">有 <b>${staleCount}</b> 則標註是在<b>其他視角</b>畫的（畫面上以虛線顯示）。
       點「回視角」可以回到當初的視角。</li>`
    : '';
  ul.innerHTML = hint + items.map((it, i) => {
    const t = (it.text ?? '').trim();
    const stale = !state.ann.viewpointMatches(it);
    return `<li class="ann-item${state.ann.selected === it.id ? ' sel' : ''}${it.status === 'closed' ? ' closed' : ''}${stale ? ' stale' : ''}" data-id="${escapeHtml(it.id)}">
      <span class="an-no">${i + 1}</span>
      <span class="an-body">
        <span class="an-meta">${TYPE_LABEL[it.type] ?? it.type}${it.space === 'screen' ? ' · 3D' : ''}${it.status === 'closed' ? ' · 已結案' : ''}${stale ? ' · <b>視點不同</b>' : ''}</span>
        <span class="an-text${t ? '' : ' empty'}">${t ? escapeHtml(t) : '（沒有說明）'}</span>
      </span>
      <span class="an-ops">
        ${stale ? '<button data-act="vp" title="回到這則標註的視角">回視角</button>' : ''}
        <button data-act="edit" title="編輯說明" ${window.dcmsAuth&&!window.dcmsAuth.can('annotation.update',activeItem(),it.createdBy)?'disabled':''}>改</button>
        <button data-act="toggle" title="切換結案" ${window.dcmsAuth&&!window.dcmsAuth.can('annotation.resolve',activeItem(),it.createdBy)?'disabled':''}>${it.status === 'closed' ? '重開' : '結案'}</button>
        <button data-act="del" title="刪除" ${window.dcmsAuth&&!window.dcmsAuth.can('annotation.delete',activeItem(),it.createdBy)?'disabled':''}>刪</button>
      </span>
    </li>`;
  }).join('');
}

function setAnnMode(on) {
  if (on && state.cadLayout && !state.mode3d) { flashHint('請切回模型空間新增標註'); return; }
  if (on) {linkedViewer.cancel();documentRfi?.surfaceTools.setMode(null);state.viewer3d?.surfaceTools.setMode(null);}
  if (on && state.viewer3d?.navigationMode !== 'orbit') state.viewer3d?.setNavigationMode('orbit', { restore: false });
  state.annMode = Boolean(on);
  if (state.annMode) showReviewPanel('annotations');
  $('#sideAnnotToggle').textContent = state.annMode ? '結束標註' : '新增標註';
  const tools = $('#annTools');
  if (tools) tools.hidden = !state.annMode;
  const layer = annLayer();
  // 非標註模式要讓整層「穿透」，否則會蓋住 3D 的視角按鈕（實測按不到）
  layer.setActive(state.annMode);
  if (!state.annMode) { state.annTool = null; layer.setTool(null); }
  else { attachAnnLayer(); layer.setVisible(true); }
  syncAnnToolButtons();
  $('#btnAnnot')?.classList.toggle('on', state.annMode);
  syncToolbarState();
  renderAnnList();
}

function setAnnTool(tool) {
  const layer = annLayer();
  state.annTool = state.annTool === tool ? null : tool;
  layer.setTool(state.annTool);
  syncAnnToolButtons();
}

function syncAnnToolButtons() {
  for (const b of document.querySelectorAll('#annTools .at-btn')) {
    b.classList.toggle('on', b.dataset.ann === state.annTool);
  }
}

/* ── 提醒資訊（單一出口）────────────────────────────────────── */

/**
 * 把一則提醒放進 modal 上方那一條。
 *
 * 為什麼要收攏：以前「為什麼只有 3D」、「有沒有取樣」、「幾何有多少警告」分散在
 * 標題下的 badge、3D 畫面裡的黃色說明、底部狀態列三個地方，同一個原因會重複出現
 * 或整個漏掉；使用者得自己拼。現在規則很簡單：
 *   - **badge／統計行只放數字**（引擎、耗時、實體、圖層、面／線／點／頂點）
 *   - **這一條只放「為什麼」與「有什麼限制」**
 *
 * @param {'info'|'warn'|'limit'} kind
 * @param {string} text
 */
function addNotice(kind, text, scope = null) {
  if (!text) return;
  if (state.notices.some((n) => n.text === text)) return;   // 同一個原因不重複列
  state.notices.push({ kind, text, scope });
  renderNotices();
}
function clearNotices() {
  state.notices = [];
  state.bannerDismissed = false;
  renderNotices();
}
function renderNotices() {
  const ul = $('#mNotice');
  if (!ul) return;
  ul.hidden = state.notices.length === 0;
  $('#messagePanel').hidden = ul.hidden;
  if (ul.hidden) $('#messagePanel').open = false;
  $('#messagePanel').dataset.level = state.notices.some(n => n.kind !== 'info') ? 'warn' : 'info';
  $('#messageCount').textContent = String(state.notices.length);
  ul.innerHTML = state.notices
    .map((n) => `<li class="nt nt-${n.kind}"><span class="nt-dot"></span><span>${escapeHtml(n.text)}</span></li>`)
    .join('');
  renderViewerBanner();
}

/** 會讓人誤判圖面的提醒（warn／limit）浮在畫布頂部；info 只留在「訊息」。 */
function renderViewerBanner() {
  const banner = $('#viewerBanner');
  if (!banner) return;
  const important = state.notices.filter(n => n.kind !== 'info');
  banner.hidden = !important.length || state.bannerDismissed;
  if (banner.hidden) return;
  banner.dataset.level = important[0].kind;
  $('#viewerBannerText').textContent = important[0].text;
  const more = $('#viewerBannerMore');
  more.hidden = state.notices.length < 2;
  more.textContent = `查看全部 ${state.notices.length} 則`;
}
$('#viewerBannerClose').addEventListener('click', () => {
  state.bannerDismissed = true;
  renderViewerBanner();
});
$('#viewerBannerMore').addEventListener('click', () => {
  const panel = $('#messagePanel');
  panel.open = true;
  panel.querySelector('summary')?.focus();
});

/**
 * 3D 的 badge（只放數字，跟 2D 的 renderBadges 對稱）。
 *
 * 以前只有 2D 會設 badge，所以「只有 3D」的檔案標題下是空的 —— 同一個檔案用
 * 2D 開有數字、用 3D 開沒有，就是不一致。耗時不放進來：3D-only 的檔案那個
 * `ms` 包含 2D 逾時的 15 分鐘，放上去會誤導。
 */
function render3DBadges(item, engine, mesh) {
  const st = mesh?.stats ?? {};
  const faces = mesh?.counts?.tris ?? st.faces ?? 0;
  const lines = mesh?.counts?.lines ?? st.lines ?? 0;
  const points = mesh?.counts?.points ?? st.points ?? 0;
  const bits = [`<span class="badge hl">${engineLabel(engine)}</span>`,
    `<span class="badge">3D 面 ${faces.toLocaleString()}</span>`];
  if (lines) bits.push(`<span class="badge">線 ${lines.toLocaleString()}</span>`);
  if (points) bits.push(`<span class="badge">點 ${points.toLocaleString()}</span>`);
  bits.push(`<span class="badge">${fmtBytes(item.size)}</span>`);
  $('#infoBadges').innerHTML = bits.join('');
}

/** 把一大串幾何警告收成一句：最常見的兩類 + 總數。 */
function warningSummary(warns) {
  const counts = new Map();
  for (const w of warns) {
    const k = String(w).replace(/（.*$/, '').trim() || '其他';
    counts.set(k, (counts.get(k) ?? 0) + 1);
  }
  const top = [...counts.entries()].sort((a, b) => b[1] - a[1]).slice(0, 2)
    .map(([k, n]) => `${k} ×${n.toLocaleString()}`).join('、');
  return `2D 有 ${warns.length.toLocaleString()} 筆需確認：${top}`
    + (counts.size > 2 ? `（共 ${counts.size} 類）` : '');
}

/* ── 覆蓋層 ─────────────────────────────────────────────────── */
const showLoading = (t) => {
  $('#loadingText').textContent = t; $('#mLoading').hidden = false;
  $('#workspaceBusy').textContent = t; $('#workspaceBusy').hidden = false;
};
const hideLoading = () => { $('#mLoading').hidden = true; $('#workspaceBusy').hidden = true; };
function showError(msg) { $('#errorMsg').textContent = msg; $('#mError').hidden = false; }
const hideError = () => { $('#mError').hidden = true; };

/* ── 事件 ───────────────────────────────────────────────────── */
$('#mClose').addEventListener('click', closeModal);
$('#modal').querySelectorAll('[data-close]').forEach((el) => el.addEventListener('click', closeModal));
const CLEAR_PHRASE = '清空圖庫';
const CLEAR_ALL = '*';

/** 清空範圍選項：整個圖庫／未分類／各頂層目錄與子目錄。 */
function clearScopeOptions() {
  const out = [new Option('整個圖庫', CLEAR_ALL), ...destinationOptions()];
  return out;
}

/** 依清空範圍篩選項目（含子目錄；專案根＝該專案全部）。 */
function itemsInClearScope(items, scopeValue) {
  if (scopeValue === CLEAR_ALL) return items;
  const { projectId, folderId } = parseDestination(scopeValue);
  if (!projectId) return items.filter((i) => !i.projectId);
  if (!folderId) return items.filter((i) => i.projectId === projectId);
  const subtree = folderSubtree(state.folders, folderId);
  return items.filter((i) => i.projectId === projectId && subtree.has(i.folderId));
}

function clearScopeLabel(scopeValue) {
  const opt = [...$('#clearScope').options].find((o) => o.value === scopeValue);
  return opt?.textContent?.trim() || '選定範圍';
}

/** 清空範圍會一併移除的目錄數量（整個圖庫＝全部專案；頂層＝該專案；子目錄＝子樹）。 */
function clearScopeFolderCount(scopeValue) {
  if (scopeValue === CLEAR_ALL) return state.projects.length + state.folders.length;
  const { projectId, folderId } = parseDestination(scopeValue);
  if (!projectId) return 0;
  if (!folderId) return 1 + state.folders.filter((f) => f.projectId === projectId).length;
  return folderSubtree(state.folders, folderId).size;
}

function updateClearSummary() {
  const scope = $('#clearScope').value;
  const items = itemsInClearScope(state.items, scope);
  const label = clearScopeLabel(scope);
  const bytes = items.reduce((sum, it) => sum + (it.size || 0), 0);
  const sizeHint = bytes ? `（約 ${fmtBytes(bytes)}）` : '';
  const folderCount = clearScopeFolderCount(scope);
  const canClear = items.length > 0 || folderCount > 0;
  if (!canClear) {
    $('#clearSummary').textContent = `「${label}」目前沒有可刪除的檔案或目錄。`;
    $('#clearSubmit').disabled = true;
    return;
  }
  const parts = [];
  if (items.length) parts.push(`${items.length} 個項目${sizeHint}（原始檔、轉檔結果、標註與書籤）`);
  if (folderCount) {
    parts.push(scope === CLEAR_ALL ? `全部 ${folderCount} 個目錄／專案`
      : parseDestination(scope).folderId ? `該目錄及其 ${folderCount - 1} 個子目錄`
        : `該頂層目錄與其下 ${folderCount - 1} 個子目錄`);
  }
  $('#clearSummary').textContent = `將永久刪除「${label}」：${parts.join('，以及')}。此動作無法復原。`;
  $('#clearSubmit').disabled = $('#clearConfirm').value.trim() !== CLEAR_PHRASE;
}

archivePanel = new ArchivePanel($('#archiveCard'), {
  getTree: () => ({ projects: state.projects, folders: state.folders }),
  fmtBytes, escapeHtml, toast: flashHint,
  onChanged: () => refreshLibrary(),
});
$('#engineJump')?.addEventListener('click', () => showSettingsPane('engine'));
$('#archiveJump').addEventListener('click', () => showSettingsPane('archive'));
$('#uploadReviewJump').addEventListener('click', () => showSettingsPane('uploadReview'));
$('#uploadWorkflowsJump')?.addEventListener('click', () => showSettingsPane('uploadWorkflows'));
$('#accountsJump')?.addEventListener('click', () => showSettingsPane('accounts'));
$('#auditJump')?.addEventListener('click', () => showSettingsPane('systemAudit'));
$('#projectFoldersJump')?.addEventListener('click', () => showSettingsPane('projectFolders'));
$('#projectFoldersForm')?.addEventListener('submit', async (event) => {
  event.preventDefault();
  const form = event.currentTarget;
  const status = $('#projectFolderStatus');
  const name = $('#projectFolderName').value.trim();
  const file = $('#projectFolderFile').files?.[0];
  if (!name) { status.textContent = '請輸入專案名稱'; return; }
  if (!file) { status.textContent = '請上傳專案資料夾結構檔'; return; }
  const submit = form.querySelector('button[type="submit"]');
  submit.disabled = true;
  status.textContent = '正在建立專案目錄…';
  try {
    const response = await fetch(`/api/library/projects/from-template?name=${encodeURIComponent(name)}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' },
      body: await file.arrayBuffer(),
    });
    const result = await response.json();
    if (!response.ok) throw new Error(result.error || `HTTP ${response.status}`);
    await refreshLibrary({ silent: true });
    status.textContent = `已建立專案「${result.project.name}」，共 ${result.folderCount} 個資料夾。`;
    form.reset();
  } catch (error) {
    status.textContent = error.message;
  } finally {
    submit.disabled = false;
  }
});

function reconvertPlace(item) {
  const project = item.projectId ? state.projects.find(p => p.id === item.projectId)?.name : '';
  const folder = item.projectId ? folderPath(state.folders, item.folderId) : '';
  return [project, folder].filter(Boolean).join(' / ') || (item.projectId ? '根目錄' : '未分類');
}

function reconvertCandidates(name) {
  const query = name.trim().normalize('NFC').toLowerCase();
  if (!query) return [];
  return state.items.filter(item => !item.missing && item.isCurrent !== false && CONVERTIBLE_KINDS.has(item.kind) && item.name.normalize('NFC').toLowerCase() === query);
}

function reconvertBusy(item) {
  const id = item.id;
  return item.engines?.oda?.status === 'converting'
    || state.conversion?.running?.some(job => job.id === id)
    || state.conversion?.queue?.some(job => job.id === id);
}

async function queueReconvert(item) {
  const status = $('#reconvertStatus');
  if (reconvertBusy(item)) {
    status.textContent = `「${item.name}」已在轉檔排程中。`;
    return;
  }
  const response = await fetch(`/api/library/${item.id}/convert`, { method: 'POST' });
  const data = await response.json().catch(() => ({}));
  if (!response.ok || data.ok === false) throw new Error(data.error || '無法排入重轉');
  status.textContent = `已重新排入「${reconvertPlace(item)}」的 ${item.name}。`;
  await refreshLibrary();
}

function renderReconvertMatches(matches) {
  const host = $('#reconvertMatches');
  host.replaceChildren();
  if (matches.length < 2) return;
  const list = document.createElement('div');
  list.className = 'reconvert-matches';
  for (const item of matches) {
    const row = document.createElement('div');
    row.className = 'reconvert-match';
    const label = document.createElement('span');
    label.textContent = `${reconvertPlace(item)} · ${item.name}`;
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'btn btn-sm';
    button.textContent = '重轉這個檔案';
    button.addEventListener('click', () => queueReconvert(item).catch(e => { $('#reconvertStatus').textContent = e.message; }));
    row.append(label, button);
    list.append(row);
  }
  host.append(list);
}

$('#reconvertForm')?.addEventListener('submit', async (event) => {
  event.preventDefault();
  const status = $('#reconvertStatus');
  const name = $('#reconvertName').value;
  $('#reconvertMatches').replaceChildren();
  const matches = reconvertCandidates(name);
  if (!name.trim()) { status.textContent = '請輸入完整檔名。'; return; }
  if (!matches.length) { status.textContent = `找不到檔名「${name.trim()}」的可轉檔圖面。`; return; }
  if (matches.length > 1) {
    status.textContent = `有 ${matches.length} 個同名檔案，請選擇要重轉的路徑。`;
    renderReconvertMatches(matches);
    return;
  }
  try { await queueReconvert(matches[0]); }
  catch (e) { status.textContent = e.message; }
});

$('#clearBtn').addEventListener('click', () => {
  if (!state.items.length && !state.projects.length) return;
  const sel = $('#clearScope');
  sel.replaceChildren(...clearScopeOptions());
  // 預設對齊文件庫目前選取的目錄（可改）
  const pref = state.selectedProject === 'all' ? CLEAR_ALL
    : state.selectedProject === 'unassigned' ? '|'
      : `${state.selectedProject}|${state.selectedFolder || ''}`;
  if ([...sel.options].some((o) => o.value === pref)) sel.value = pref;
  else sel.value = CLEAR_ALL;
  $('#clearConfirm').value = '';
  updateClearSummary();
  $('#clearDialog').showModal();
  $('#clearScope').focus();
});
$('#clearScope').addEventListener('change', updateClearSummary);
$('#clearConfirm').addEventListener('input', updateClearSummary);
$('#clearDialog').addEventListener('close', async () => {
  if ($('#clearDialog').returnValue !== 'clear' || $('#clearConfirm').value.trim() !== CLEAR_PHRASE) return;
  const scope = $('#clearScope').value;
  const label = clearScopeLabel(scope);
  const { projectId, folderId } = parseDestination(scope);
  const parentAfter = folderId
    ? (state.folders.find((f) => f.id === folderId)?.parentId ?? null)
    : null;
  let allItems = state.items;
  try {
    const data = await (await fetch('/api/library?revisions=all&deleted=1', { cache: 'no-store' })).json();
    if (Array.isArray(data.items)) allItems = data.items;
  } catch { /* 退回現行清單 */ }
  const items = itemsInClearScope(allItems, scope);
  const folderCount = clearScopeFolderCount(scope);
  if (!items.length && !folderCount) { flashHint(`「${label}」沒有可刪除的內容`); return; }
  try {
    const target=scope===CLEAR_ALL?{type:'global'}:folderId?{type:'folder',id:folderId}:projectId?{type:'project',id:projectId}:{type:'unassigned'};
    await fetch('/api/library/clear',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({scope:target})});
  } catch(e) {flashHint(`清空失敗：${e.message}`);return;}
  closeModal();
  if (scope === CLEAR_ALL || (projectId && !folderId)) selectBrowse({ project: 'all' });
  else if (folderId) selectBrowse({ project: projectId, folder: parentAfter });
  await refreshLibrary();
  const bits = [];
  if (items.length) bits.push(`${items.length} 項`);
  if (folderCount) bits.push(`${folderCount} 個目錄`);
  flashHint(`已清空「${label}」（${bits.join('、')}）`);
});
$('#pickBtn').addEventListener('click', () => $('#fileInput').click());
$('#fileInput').addEventListener('change', (e) => { (reviewUi.enabled?reviewUi.enqueue(e.target.files):ingest(e.target.files)).catch(err=>flashHint(err.message)); e.target.value = ''; });

const dz = $('#dropzone');
dz.addEventListener('click', (e) => {
  if (e.target === dz || e.target.closest('.dz-icon, .dz-title, .dz-hint')) $('#fileInput').click();
});
/* ── 3D 視圖控制（標準視角／正交／剖面）───────────────────────── */

function sync3DTools() {
  const v = state.viewer3d;
  const ready = state.mode3d && Boolean(v?.bbox);
  $('#sceneToolFields').disabled = !ready;
  // 2D 時仍可按：量測開 2D 量距，其他群組會先切換到 3D；3D 載入中才停用。
  for (const button of $$('.v3d-group-tab')) button.disabled = state.docMode === 'sheetset' || Boolean(state.cadLayout && !state.mode3d) || !activeItem() || (isDocPreview()?button.id!=='v3dNotesGroup'||!documentRfi?.rfiLoaded:state.mode3d&&!ready);
  for (const id of ['v3dPivot','v3dPivotReset','v3dMeasureStart','v3dBoxEnabled']) $(`#${id}`).disabled = !ready;
  syncSurfaceTools();
  $('#viewpointAdd').disabled = !ready;
  $('#sceneEnter3D').hidden = state.mode3d;
  $('#v3dNav').value = v?.navigationMode || 'orbit';
  $('#v3dSpeed').value = String(v?.navigationSpeed || 1);
  $('#v3dOrtho').disabled = ready && v.navigationMode !== 'orbit';
  if (v) $('#v3dOrtho').checked = v.ortho;
  $('#navHint').hidden = !ready || v.navigationMode === 'orbit';
  syncCameraHeight();
  locationNavigation?.sync();
}

function syncCameraHeight() {
  const v = state.viewer3d, ready = state.mode3d && Boolean(v?.bbox);
  for (const id of ['v3dCameraHeight','v3dHeightDown','v3dHeightUp','v3dHeightRange','v3dLookHorizontal','v3dWalkHere','v3dEyeOffset']) $(`#${id}`).disabled = !ready;
  if (!ready) return;
  const height = v.camera.position.z;
  if (document.activeElement !== $('#v3dCameraHeight')) $('#v3dCameraHeight').value = String(Math.round(height * 1e6) / 1e6);
  const min = v.bbox.min[2], max = Math.max(v.bbox.max[2], min + v.modelSpan() * .01);
  $('#v3dHeightRange').value = String(Math.min(100, Math.max(0, (height - min) / (max - min) * 100)));
}
$('#v3dCameraHeight').addEventListener('change', () => {
  const value = $('#v3dCameraHeight').valueAsNumber;
  if (!Number.isFinite(value)) { flashHint('請輸入有效的觀看高度'); return; }
  state.viewer3d?.setCameraHeight(value);
});
$('#v3dHeightRange').addEventListener('input', () => {
  const v = state.viewer3d; if (!v?.bbox) return;
  const min = v.bbox.min[2], max = Math.max(v.bbox.max[2], min + v.modelSpan() * .01);
  v.setCameraHeight(min + (max - min) * Number($('#v3dHeightRange').value) / 100);
});
for (const [id, sign] of [['v3dHeightDown',-1],['v3dHeightUp',1]]) $(`#${id}`).addEventListener('click', () => {
  const v = state.viewer3d; if (v?.bbox) v.setCameraHeight(v.camera.position.z + sign * v.heightStep());
});
$('#v3dLookHorizontal').addEventListener('click', () => state.viewer3d?.lookHorizontal());
$('#v3dWalkHere').addEventListener('click', () => {
  const offset = $('#v3dEyeOffset').valueAsNumber;
  if (!Number.isFinite(offset) || offset < 0) { flashHint('離表面高度需為有效的非負數'); return; }
  if (state.viewer3d) state.viewer3d.surfaceTools.eyeOffset = offset;
  beginSurfaceTool('walk-start');
});

function syncSurfaceTools() {
  const tools = isPageRfi()?documentRfi?.surfaceTools:state.viewer3d?.surfaceTools;
  const ready = isPageRfi()?Boolean(documentRfi?.rfiLoaded):state.mode3d && Boolean(state.viewer3d?.bbox);
  $('#v3dPickCancel').disabled = !ready || !tools?.mode;
  $('#v3dMeasureClear').disabled = !ready || !tools?.measurements.length;
  $('#v3dMeasureUnit').disabled = !ready;
  $('#v3dPivot').classList.toggle('on', tools?.mode === 'pivot');
  $('#v3dWalkHere').classList.toggle('on', tools?.mode === 'walk-start');
  $('#v3dMeasureStart').classList.toggle('on', tools?.mode === 'measure');
  $('#v3dNoteAdd')?.setAttribute('aria-pressed', String(tools?.mode === 'note'));
  for (const button of document.querySelectorAll('[data-rfi-tool]')) button.setAttribute('aria-pressed', String(tools?.mode === 'markup' && tools.markupType === button.dataset.rfiTool));
  $('#v3dMeasureUnit').value = tools?.unit || '';
  const list = $('#v3dMeasurementList'); list.replaceChildren();
  for (const record of tools?.measurements || []) {
    const li = document.createElement('li'), text = document.createElement('span'), remove = document.createElement('button');
    if(record.angle!==undefined)text.textContent=`#${record.id} 角度 ${record.angle.toFixed(2)}°`;
    else text.textContent = `#${record.id} 距離 ${tools.format(record.distance)} · 水平 ${tools.format(record.horizontal)} · 高差 ${tools.format(record.height)}`;
    remove.className = 'btn btn-sm'; remove.textContent = '刪除'; remove.onclick = () => tools.remove(record.id);
    li.append(text, remove); list.append(li);
  }
}
function beginSurfaceTool(mode) {
  if (state.cadLayout && !state.mode3d) { flashHint('請切回模型空間使用此工具'); return; }
  if(isPageRfi()){
   if(state.annMode)setAnnMode(false);linkedViewer.cancel();
   const tools=documentRfi?.surfaceTools;
   if(!tools||!documentRfi.rfiLoaded||!window.dcmsAuth?.can('rfi.create',activeItem()))return;
   if(String(mode).startsWith('markup:')){const type=mode.slice(7);tools.mode==='markup'&&tools.markupType===type?tools.setMode(null):tools.activateMarkup(type);}
   else if(mode==='note')tools.mode==='note'?tools.setMode(null):tools.activate('note');
   return;
  }
  if (!state.mode3d || !state.viewer3d?.bbox) return;
  if (state.annMode) setAnnMode(false);
  const tools = state.viewer3d.surfaceTools;
  if (String(mode).startsWith('markup:')) {
    const type = mode.slice(7);
    if (tools.mode === 'markup' && tools.markupType === type) { tools.setMode(null); $('#v3dPickStatus').textContent = '已結束點選'; return; }
    tools.activateMarkup(type);
    return;
  }
  if (tools.mode === mode) { tools.setMode(null); $('#v3dPickStatus').textContent = '已結束點選'; return; }
  tools.activate(mode);
}
$('#v3dPivot').addEventListener('click', () => beginSurfaceTool('pivot'));
$('#v3dMeasureStart').addEventListener('click', () => beginSurfaceTool('measure'));
$('#v3dPickCancel').addEventListener('click', () => { (isPageRfi()?documentRfi:state.viewer3d)?.surfaceTools.setMode(null); $('#v3dPickStatus').textContent = '已結束點選'; });
$('#v3dMeasureClear').addEventListener('click', () => state.viewer3d?.surfaceTools.clear());
$('#v3dMeasureUnit').addEventListener('change', () => {
  if (!state.viewer3d) return;
  state.viewer3d.surfaceTools.unit = $('#v3dMeasureUnit').value;
  reviewTools.mutate(c=>{c.sourceUnit=$('#v3dMeasureUnit').value;c.scaleMeters=null;c.calibration=null;});
  syncSurfaceTools(); state.viewer3d.renderOnce();
});
$('#v3dPivotReset').addEventListener('click', () => {
  const v = state.viewer3d; if (!v?.bbox) return;
  v.surfaceTools.setMode(null);
  if (v.navigationMode !== 'orbit') v.setNavigationMode('orbit', { restore: false });
  v._newOrbitControls(); v.controls.target.set(...v.bbox.min.map((n, i) => (n + v.bbox.max[i]) / 2));
  v.camera.lookAt(v.controls.target); v.renderOnce();
  $('#v3dPickStatus').textContent = '旋轉中心已回到模型中心'; $('#v3dPickStatus').hidden = false;
});

function applyDisplay() {
  state.viewer3d?.setDisplayStyle($('#v3dStyle').value, Number($('#v3dOpacity').value));
}
$('#sceneEnter3D').addEventListener('click', () => enter3D({ manual: true }));
$('#v3dNav').addEventListener('change', () => {
  state.viewer3d?.surfaceTools.setMode(null);
  if (state.annMode) setAnnMode(false);
  state.viewer3d?.setNavigationMode($('#v3dNav').value);
  sync3DTools();
});
$('#v3dSpeed').addEventListener('change', () => {
  if (state.viewer3d) state.viewer3d.navigationSpeed = Number($('#v3dSpeed').value) || 1;
  state.viewer3d?.renderer.domElement.focus({ preventScroll: true });
});
$('#v3dStyle').addEventListener('change', applyDisplay);
$('#v3dOpacity').addEventListener('input', applyDisplay);
$('#v3dSnapshot').addEventListener('click', async () => {
  const item = activeItem(), v = state.viewer3d;
  if (!item || !state.mode3d || !v?.bbox) return;
  const button = $('#v3dSnapshot'); button.disabled = true;
  try {
    const blob = await v.snapshot();
    const url = URL.createObjectURL(blob), a = document.createElement('a');
    a.href = url; a.download = item.name.replace(/\.[^.]+$/, '') + '.png'; a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  } catch (e) { flashHint(e.message); }
  finally { button.disabled = false; }
});

let viewpointSeq = 0;
async function loadViewpoints(item, engine) {
  const seq = ++viewpointSeq;
  state.viewpoints = []; renderViewpoints();
  try {
    const r = await fetch(`/api/library/${item.id}/viewpoints?engine=${engine}`, { cache: 'no-store' });
    const data = await r.json();
    if (seq !== viewpointSeq || activeItem()?.id !== item.id || resolvedEngine() !== engine) return;
    if (!r.ok) throw new Error(data.error || '無法取得視點');
    state.viewpoints = data.items; renderViewpoints();
  } catch (e) { if (seq === viewpointSeq && activeItem()?.id === item.id) $('#viewpointList').textContent = e.message; }
}

async function changeViewpoint(method, key, input) {
  const item = activeItem(), engine = resolvedEngine();
  if (!item || !state.mode3d || !state.viewer3d?.bbox) return;
  const seq = ++viewpointSeq;
  const r = await fetch(`/api/library/${item.id}/viewpoints?engine=${engine}${key ? `&key=${key}` : ''}`, {
    method, headers: { 'Content-Type': 'application/json' }, ...(input ? { body: JSON.stringify(input) } : {}),
  });
  const data = await r.json();
  if (!r.ok) throw new Error(data.error || '視點操作失敗');
  if (seq === viewpointSeq && activeItem()?.id === item.id && resolvedEngine() === engine) { state.viewpoints = data.items; renderViewpoints(); }
}

function renderViewpoints() {
  const list = $('#viewpointList');
  list.replaceChildren();
  if (!state.viewpoints.length) { list.textContent = '尚無視點書籤。開啟 3D 後可儲存目前視角。'; return; }
  for (const book of state.viewpoints) {
    const li = document.createElement('li');
    const open = document.createElement('button'); open.className = 'side-action'; open.textContent = book.name; open.title = book.name;
    open.onclick = () => {
      if (!state.mode3d || !state.viewer3d?.bbox) return;
      state.viewer3d.setViewpoint(book.viewpoint);
      resetBoxInputs();
      if (book.section) {
        $('#v3dClipAxis').value = book.section.axis || ''; $('#v3dClipPos').value = String(book.section.t);
        $('#v3dClipReverse').checked = book.section.reverse; $('#v3dClipSlab').checked = book.section.upper !== null;
        $('#v3dClipEnd').min = '0'; $('#v3dClipEnd').value = String(book.section.upper ?? 1);
        if (book.section.box) {
          $('#v3dBoxEnabled').checked = true;
          for (const [i, axis] of ['X','Y','Z'].entries()) for (const end of ['Min','Max']) $(`#v3dBox${axis}${end}`).value = String(book.section.box[end.toLowerCase()][i] * 100);
        }
        applyClip();
        if(book.section.floor)state.viewer3d.advanced.setFloor(book.section.floor);
        if(book.section.oblique){const o=book.section.oblique;$('#v3dOblique').checked=true;$('#v3dAzimuth').value=o.azimuth;$('#v3dTilt').value=o.tilt;$('#v3dObliquePos').value=o.position;reviewTools.oblique();}
      }
      sync3DTools();
    };
    const rename = document.createElement('button'); rename.className = 'btn btn-sm'; rename.textContent = '改名';
    rename.onclick = async () => {
      const name = prompt('視點名稱：', book.name); if (name === null) return;
      try { await changeViewpoint('PATCH', book.id, { name }); } catch (e) { flashHint(e.message); }
    };
    const remove = document.createElement('button'); remove.className = 'btn btn-sm'; remove.textContent = '刪除';
    remove.onclick = async () => {
      if (!confirm(`刪除視點「${book.name}」？`)) return;
      try { await changeViewpoint('DELETE', book.id); } catch (e) { flashHint(e.message); }
    };
    li.append(open, rename, remove); list.append(li);
  }
}
$('#viewpointAdd').addEventListener('click', async () => {
  if (!state.mode3d || !state.viewer3d?.bbox) return;
  const name = prompt('視點名稱：'); if (name === null) return;
  try { await changeViewpoint('POST', null, { name, viewpoint: state.viewer3d.getViewpoint(), section: state.viewer3d.sectionSettings }); }
  catch (e) { flashHint(e.message); }
});

const GROUP_KEYS = { v3dObjectsGroup: 'objects', v3dSectionGroup: 'section', v3dMeasureGroup: 'measure', v3dNotesGroup: 'notes', v3dViewGroup: 'view' };
for (const tab of $$('.v3d-group-tab')) {
  tab.addEventListener('click', () => {
    if(isDocPreview()||!state.mode3d&&state.has2d&&tab.id==='v3dNotesGroup'){if(tab.id!=='v3dNotesGroup')return;const expand=tab.getAttribute('aria-expanded')!=='true';if(expand&&state.reviewPanel!=='tools')showReviewPanel('tools');setGroupTab(expand?tab:null);syncDrawer();return;}
    if (!state.mode3d) {
      // 2D 的「量測」是圖面兩點量距；其他群組需要 3D，先切換再展開。
      if (tab.id === 'v3dMeasureGroup' && state.has2d) { showReviewPanel(state.reviewPanel === 'measure2d' ? 'tools' : 'measure2d'); return; }
      locationNavigation.openGroup(GROUP_KEYS[tab.id]).catch(e => flashHint(e.message));
      return;
    }
    const expand = tab.getAttribute('aria-expanded') !== 'true';
    if (expand && state.reviewPanel !== 'tools') showReviewPanel('tools');
    setGroupTab(expand ? tab : null);
    syncDrawer();
  });
}

/* 工具列右側的彈出框（訊息／資訊／說明）與「更多」選單：一次只開一個，點外面收起。 */
const popovers = () => $$('.bar-pop, .tb-more');
for (const pop of popovers()) {
  pop.addEventListener('toggle', () => {
    pop.querySelector('summary').setAttribute('aria-expanded', String(pop.open));
    if (pop.open) for (const other of popovers()) if (other !== pop) other.open = false;
  });
}
document.addEventListener('pointerdown', (e) => {
  for (const pop of popovers()) if (pop.open && !pop.contains(e.target)) pop.open = false;
  for (const pop of document.querySelectorAll('#fileList details.row-more[open]')) {
    if (!pop.contains(e.target)) pop.open = false;
  }
});
// 捲動／縮放視窗時關掉列選單，避免 fixed 位置與按鈕脫節。
document.addEventListener('scroll', () => { if (document.querySelector('#fileList details.row-more[open]')) closeOpenRowMores(); }, true);
window.addEventListener('resize', () => { if (document.querySelector('#fileList details.row-more[open]')) closeOpenRowMores(); });

function applyClip() {
  $('#v3dOblique').checked=false;
  const axis = $('#v3dClipAxis')?.value || null;
  const slider = $('#v3dClipPos');
  const value = Number(slider.value);
  slider.disabled = !axis;
  $('#v3dClipMinus').disabled = !axis || value <= Number(slider.min);
  $('#v3dClipPlus').disabled = !axis || value >= Number(slider.max);
  const slab = $('#v3dClipSlab').checked;
  const end = $('#v3dClipEnd');
  if (Number(end.value) < value) end.value = String(value);
  end.min = String(value);
  $('#v3dClipEndLabel').hidden = !slab;
  $('#v3dClipPercent').value = String(Math.round(value * 1000) / 10);
  for (const id of ['v3dClipPercent', 'v3dClipSlab', 'v3dClipEnd']) $(`#${id}`).disabled = !axis;
  $('#v3dClipReverse').disabled = !axis || slab;
  $('#v3dBoxFields').hidden = !$('#v3dBoxEnabled').checked;
  if ($('#v3dBoxEnabled').checked) { applyBox(); return; }
  state.viewer3d?.setSection(axis, value, { reverse: $('#v3dClipReverse').checked, upper: slab ? Number(end.value) : null });
}
function resetBoxInputs() {
  $('#v3dBoxEnabled').checked = false; $('#v3dBoxFields').hidden = true;
  for (const axis of ['X','Y','Z']) { $(`#v3dBox${axis}Min`).value = '0'; $(`#v3dBox${axis}Max`).value = '100'; }
}
function applyBox(event) {
  if (!$('#v3dBoxEnabled').checked) return;
  const min = [], max = [];
  for (const axis of ['X','Y','Z']) {
    const lo = $(`#v3dBox${axis}Min`), hi = $(`#v3dBox${axis}Max`);
    if (!Number.isFinite(lo.valueAsNumber) || !Number.isFinite(hi.valueAsNumber)) { flashHint('剖切盒範圍請輸入有效百分比'); return; }
    let a = Math.min(100, Math.max(0, lo.valueAsNumber)), b = Math.min(100, Math.max(0, hi.valueAsNumber));
    if (a > b) { if (event?.target === hi) a = b; else b = a; }
    lo.value = String(a); hi.value = String(b); min.push(a / 100); max.push(b / 100);
  }
  state.viewer3d?.setClipBox({ min, max });
}
$('#v3dBoxEnabled').addEventListener('change', () => { if ($('#v3dBoxEnabled').checked) $('#v3dClipAxis').value = ''; applyClip(); });
for (const axis of ['X','Y','Z']) for (const end of ['Min','Max']) $(`#v3dBox${axis}${end}`).addEventListener('change', applyBox);
$('#v3dBoxReset').addEventListener('click', () => { resetBoxInputs(); $('#v3dBoxEnabled').checked = true; applyClip(); });
$('#v3dClipMinus').addEventListener('click', () => { $('#v3dClipPos').stepDown(); applyClip(); });
$('#v3dClipPlus').addEventListener('click', () => { $('#v3dClipPos').stepUp(); applyClip(); });
for (const id of ['v3dClipReverse', 'v3dClipSlab', 'v3dClipEnd']) $(`#${id}`).addEventListener('input', applyClip);
$('#v3dClipPercent').addEventListener('change', () => {
  const value = $('#v3dClipPercent').valueAsNumber;
  if (Number.isFinite(value)) $('#v3dClipPos').value = String(Math.min(Math.max(value, 0), 100) / 100);
  applyClip();
});
$('#v3dClipReset').addEventListener('click', () => {
  resetBoxInputs();
  $('#v3dClipAxis').value = ''; $('#v3dClipPos').value = '0.5'; $('#v3dClipEnd').min = '0'; $('#v3dClipEnd').value = '1';
  $('#v3dClipReverse').checked = false; $('#v3dClipSlab').checked = false; applyClip();
});

for (const b of document.querySelectorAll('.vv')) {
  b.addEventListener('click', () => {
    if (!state.viewer3d) return;
    state.viewer3d.setStandardView(b.dataset.view);
    for (const x of document.querySelectorAll('.vv')) {
      x.classList.toggle('on', x === b);
      x.setAttribute('aria-pressed', String(x === b));
    }
  });
}
$('#v3dOrtho')?.addEventListener('change', (e) => state.viewer3d?.setOrthographic(e.target.checked));
$('#v3dGrid')?.addEventListener('change', e => state.viewer3d?.setGridVisible(e.target.checked));
$('#v3dClipAxis')?.addEventListener('change', () => { if ($('#v3dClipAxis').value) $('#v3dBoxEnabled').checked = false; applyClip(); });
$('#v3dClipPos')?.addEventListener('input', applyClip);

/* ── 標註的事件 ─────────────────────────────────────────────── */

$('#annDone').addEventListener('click', () => setAnnMode(false));
$('#sideAnnotToggle').addEventListener('click', () => setAnnMode(!state.annMode));
for (const b of document.querySelectorAll('#annTools .at-btn')) {
  b.addEventListener('click', () => setAnnTool(b.dataset.ann));
}
$('#btnAnnAll').addEventListener('click', (e) => {
  const layer = annLayer();
  layer.setVisible(!layer.visible);
  e.target.textContent = layer.visible ? '隱藏' : '顯示';
});
$('#annList').addEventListener('click', (e) => {
  const li = e.target.closest('.ann-item');
  if (!li) return;
  const id = li.dataset.id;
  const layer = annLayer();
  const act = e.target.closest('button')?.dataset.act;
  if (act === 'del') { layer.delete(id); renderAnnList(); return; }
  if (act === 'vp') {
    const it = layer.items.find((x) => x.id === id);
    if (it?.viewpoint && state.viewer3d?.setViewpoint(it.viewpoint)) {
      layer.select(id);
      layer.setCurrentViewpoint(state.viewer3d.getViewpoint());
      state.annStaleKey = layer.staleIds().sort().join(',');
      renderAnnList();
      flashHint('已回到這則標註的視角');
    }
    return;
  }
  if (act === 'toggle') {
    const it = layer.items.find((x) => x.id === id);
    layer.update(id, { status: it?.status === 'closed' ? 'open' : 'closed' });
    renderAnnList();
    return;
  }
  if (act === 'edit') {
    const it = layer.items.find((x) => x.id === id);
    const t = window.prompt('標註說明', it?.text ?? '');
    if (t != null) { layer.update(id, { text: t }); renderAnnList(); }
    return;
  }
  // 點列本身：選取（畫面上會 highlight）
  layer.select(id);
  renderAnnList();
});

dz.addEventListener('keydown', (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); $('#fileInput').click(); } });
for (const ev of ['dragenter', 'dragover']) dz.addEventListener(ev, (e) => { e.preventDefault(); dz.classList.add('dragging'); });
for (const ev of ['dragleave', 'drop']) dz.addEventListener(ev, (e) => { e.preventDefault(); dz.classList.remove('dragging'); });
dz.addEventListener('drop', (e) => { if (e.dataTransfer?.files?.length) (reviewUi.enabled?reviewUi.enqueue(e.dataTransfer.files):ingest(e.dataTransfer.files)).catch(err=>flashHint(err.message)); });

const pdfActive = () => state.docMode === 'pdf' && pdfViewer?.pageCount > 0;
const imageActive = () => state.docMode === 'image' && imageViewer?.pageCount > 0;
function zoomCurrent(factor) {
  if (state.docMode === 'pdf') { if (pdfActive()) pdfViewer.zoomBy(factor); }
  else if (state.docMode === 'image') { if (imageActive()) imageViewer.zoomBy(factor); }
  else if (state.mode3d) state.viewer3d?.zoomBy(factor);
  else viewer.zoomBy(factor);
}
function fitCurrent() {
  if (state.docMode === 'pdf') { if (pdfActive()) pdfViewer.fitWidth(); }
  else if (state.docMode === 'image') { if (imageActive()) imageViewer.fitWidth(); }
  else if (state.mode3d) state.viewer3d?.frameAll(true);
  else state.cadLayout ? viewer.fit() : viewer.fitContent();
}
function fitAllCurrent() {
  if (state.docMode === 'pdf') { if (pdfActive()) pdfViewer.fitPage(); }
  else if (state.docMode === 'image') { if (imageActive()) imageViewer.fitPage(); }
  else if (state.mode3d) state.viewer3d?.frameAll(true);
  else viewer.fit();
}
function actualCurrent() {
  if (state.docMode === 'pdf') { if (pdfActive()) pdfViewer.actual(); }
  else if (state.docMode === 'image') { if (imageActive()) imageViewer.actual(); }
  else if (!state.mode3d) viewer.zoomToActual();
}
function rotateCurrent(delta) {
  if (state.docMode === 'pdf') { if (pdfActive()) pdfViewer.rotateBy(delta); }
  else if (state.docMode === 'image' && imageActive()) imageViewer.rotateBy(delta);
}
$('#zoomIn').addEventListener('click', () => zoomCurrent(1.35));
$('#zoomOut').addEventListener('click', () => zoomCurrent(1 / 1.35));
$('#rotateLeft').addEventListener('click', () => rotateCurrent(-90));
$('#rotateRight').addEventListener('click', () => rotateCurrent(90));
$('#btnFit').addEventListener('click', fitCurrent);
$('#btnFitAll').addEventListener('click', fitAllCurrent);
$('#btnActual').addEventListener('click', actualCurrent);
let viewerMax = false;
function syncViewerMaxButton() {
  const button = $('#btnFullscreen');
  if (!button) return;
  button.setAttribute('aria-pressed', String(viewerMax));
  button.textContent = viewerMax ? '還原' : '全螢幕';
  button.title = viewerMax ? '結束全螢幕，回到原本的檢視畫面' : '全螢幕：只保留此工具列';
  button.setAttribute('aria-label', viewerMax ? '結束全螢幕' : '全螢幕');
}
function layoutViewerMax() {
  requestAnimationFrame(() => { state.viewer3d?.resize(); linkedViewer?.draw?.(); });
}
function applyViewerMax(on) {
  viewerMax = on;
  document.body.classList.toggle('is-viewer-max', on);
  syncViewerMaxButton();
  layoutViewerMax();
}
function exitViewerMax() {
  if (!viewerMax && !document.fullscreenElement) return;
  applyViewerMax(false);
  if (document.fullscreenElement) document.exitFullscreen().catch(() => {});
}
async function enterViewerMax() {
  applyViewerMax(true);
  try { await document.documentElement.requestFullscreen(); }
  catch { /* 瀏覽器不提供全螢幕時，仍以視窗內最大顯示區呈現。 */ }
}
$('#btnFullscreen').addEventListener('click', () => { if (viewerMax) exitViewerMax(); else void enterViewerMax(); });
document.addEventListener('fullscreenchange', () => {
  if (viewerMax && !document.fullscreenElement) applyViewerMax(false);
  else layoutViewerMax();
});
$('#btnAllLayers').addEventListener('click', resetLayers);
$('#btnViewerBackground').addEventListener('click', () => {
  state.viewerBackground = state.viewerBackground === 'dark' ? 'light' : 'dark';
  state.invertOverride = null;
  try { localStorage.setItem('dcms.viewerBackground', state.viewerBackground); } catch { /* Keep the selection for this page if storage is unavailable. */ }
  applyCanvasAppearance();
});
$('#btnInvert').addEventListener('click', () => {
  // CAD 個別反白；切換檢視背景時恢復跟隨背景設定。
  state.invertOverride = !state.invert;
  applyCanvasAppearance();
  syncToolbarState();
});
$('#v3dFit').addEventListener('click', fitCurrent);
$('#v3dWire').addEventListener('change', (e) => state.viewer3d?.setWireframe(e.target.checked));

let printPreparing = false;
async function printCurrentView() {
  const item = activeItem();
  if (!item || $('#btnPrint').disabled || printPreparing) return;
  const requestSequence = openSeq;
  printPreparing = true;
  $('#btnPrint').disabled = true;
  $('#printQr').removeAttribute('src');
  const finish = () => {
    document.body.classList.remove('is-viewer-print', 'is-cad-layout-print');
    $('#cadPrintSheet').replaceChildren();
    document.getElementById('cadPrintPageStyle')?.remove();
    window.removeEventListener('afterprint', finish);
    printPreparing = false;
    syncToolbarState();
  };
  try {
    const response = await fetch(`/api/library/${item.id}/print`, {method: 'POST', headers: {'Content-Type': 'application/json'}, body: '{}'});
    const record = await response.json();
    if (!response.ok) throw new Error(record.error || '無法產生紙本版本查詢 QR Code');
    if (activeItem()?.id !== item.id || state.workspace !== 'viewer') throw new Error('檔案已切換，請重新列印');
    if (state.docMode === 'pdf' && pdfViewer) {
      const viewer = pdfViewer;
      for (const page of [...viewer.pages]) {
        await viewer.renderPage(page);
        if (!page.el.querySelector('canvas')) throw new Error(`PDF 第 ${page.n} 頁尚未準備好，請稍後再列印`);
      }
    }
    $('#printQr').src = record.qrDataUrl;
    await $('#printQr').decode();
    $('#printRevision').textContent = `${record.fileName} · 版號 ${record.revision}`;
    $('#printTime').textContent = `列印：${new Date(record.printedAt).toLocaleString('zh-TW', {hour12: false})}`;
    for (const page of document.querySelectorAll('#pdfView .pdf-page, #imageView .img-page')) {
      const rect = page.getBoundingClientRect();
      if (rect.width > 0 && rect.height > 0) page.style.setProperty('--print-page-ratio', String(rect.width / rect.height));
    }
    if (activeItem()?.id !== item.id || openSeq !== requestSequence || state.workspace !== 'viewer') throw new Error('檔案已切換，請重新列印');
    if (state.cadLayout && !state.mode3d && state.cadSvg) {
      const {paperWidthMm: width, paperHeightMm: height} = state.cadLayout;
      if (!(width > 0 && height > 0 && Number.isFinite(width) && Number.isFinite(height))) throw new Error('版面紙張尺寸無效');
      const doc = new DOMParser().parseFromString(state.cadSvg, 'image/svg+xml');
      if (doc.querySelector('parsererror')) throw new Error('圖紙 SVG 無法解析');
      const sheet = document.importNode(doc.documentElement, true);
      sheet.setAttribute('width', '100%'); sheet.setAttribute('height', '100%');
      sheet.setAttribute('preserveAspectRatio', 'xMidYMid meet');
      const watermark = $('#printWatermark').cloneNode(true); watermark.id = 'cadPrintWatermark';
      watermark.querySelector('pattern').id = 'cadPrintWatermarkPattern';
      watermark.querySelector('rect').setAttribute('fill', 'url(#cadPrintWatermarkPattern)');
      $('#cadPrintSheet').replaceChildren(sheet, watermark);
      $('#cadPrintSheet').style.width = `${width}mm`; $('#cadPrintSheet').style.height = `${height}mm`;
      const style = document.createElement('style'); style.id = 'cadPrintPageStyle';
      style.textContent = `@page { size: ${width}mm ${height}mm; margin: 0; }`;
      document.head.append(style);
      document.body.classList.add('is-cad-layout-print');
      $('#printRevision').textContent += ` · ${state.cadLayout.name}`;
    }
    document.body.classList.add('is-viewer-print');
    window.addEventListener('afterprint', finish);
    window.print();
  } catch (error) {
    finish();
    flashHint(error.message);
  }
}
$('#btnPrint').addEventListener('click', printCurrentView);
window.addEventListener('keydown', event => {
  if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'p' && state.workspace === 'viewer' && activeItem()) {
    event.preventDefault();
    void printCurrentView();
  }
});
$('#btnDownload').addEventListener('click', () => {
  const item = activeItem();
  if (!item) return;
  const a = document.createElement('a');
  a.href = `/api/library/${item.id}/svg?engine=${resolvedEngine()}${state.cadLayout ? `&layout=${encodeURIComponent(state.cadLayout.name)}` : ''}`;
  a.download = item.name.replace(/\.[^.]+$/, '') + (state.cadLayout ? `-${state.cadLayout.name}` : '') + '.svg';
  a.click();
});

for (const b of document.querySelectorAll('.tb-menu-item')) {
  b.addEventListener('click', () => b.closest('details')?.removeAttribute('open'));
}

document.addEventListener('keydown', (e) => {
  if (e.key === 'Escape') {
    if(isPageRfi()&&documentRfi?.surfaceTools.mode){e.preventDefault();documentRfi.surfaceTools.setMode(null);return;}
    if (!$('#modal').hidden && state.viewer3d?.surfaceTools.mode) { e.preventDefault(); state.viewer3d.surfaceTools.setMode(null); $('#v3dPickStatus').textContent = '已結束點選'; return; }
    if (!$('#modal').hidden && state.viewer3d?.navigationMode && state.viewer3d.navigationMode !== 'orbit') { e.preventDefault(); state.viewer3d.setNavigationMode('orbit'); return; }
    if (!$('#modal').hidden && state.annMode) { e.preventDefault(); setAnnMode(false); return; }
    if (viewerMax) { e.preventDefault(); exitViewerMax(); return; }
    if (!$('#modal').hidden) closeModal();
    return;
  }
  if ($('#modal').hidden) return;
  if (editableTarget(e.target) || e.ctrlKey || e.metaKey || e.altKey) return;
  const k = e.key.toLowerCase();
  if (state.docMode === 'sheetset') return;
  if (isDocPreview()) {
    // PDF／圖片：縮放、取景、左右旋轉與翻頁；2D／3D 與標註快捷鍵不適用。
    if (k === '+' || k === '=') zoomCurrent(1.35);
    else if (k === '-' || k === '_') zoomCurrent(1 / 1.35);
    else if (k === '[') rotateCurrent(-90);
    else if (k === ']') rotateCurrent(90);
    else if (k === 'f') fitCurrent();
    else if (k === '0') fitAllCurrent();
    else if (k === '1') actualCurrent();
    else if (pdfActive() && ['pagedown', 'pageup', 'home', 'end'].includes(k) && e.target !== $('#pdfView')) {
      e.preventDefault();
      const page = pdfViewer.currentPage();
      pdfViewer.goToPage(k === 'pagedown' ? page + 1 : k === 'pageup' ? page - 1 : k === 'home' ? 1 : pdfViewer.pageCount);
    } else if (imageActive() && imageViewer.pageCount > 1 && ['pagedown', 'pageup', 'home', 'end'].includes(k) && e.target !== $('#imageView')) {
      e.preventDefault();
      const page = imageViewer.currentPage();
      imageViewer.goToPage(k === 'pagedown' ? page + 1 : k === 'pageup' ? page - 1 : k === 'home' ? 1 : imageViewer.pageCount);
    }
    return;
  }
  if (state.mode3d && (state.viewer3d?.navigationMode !== 'orbit' || ['w','s','a','d','q','e'].includes(k)) && NAV_KEYS.has(k)) return;
  if (k === '3') { state.mode3d ? enter2D() : enter3D({ manual: true }); }
  else if (k === 'm') setAnnMode(!state.annMode);
  else if (k === '+' || k === '=') zoomCurrent(1.35);
  else if (k === '-' || k === '_') zoomCurrent(1 / 1.35);
  else if (k === 'f') fitCurrent();
  else if (k === '0') fitAllCurrent();
  else if (k === '1' && !state.mode3d) viewer.zoomToActual();
  else if (k.startsWith('arrow')) {
    e.preventDefault();
    if (state.mode3d) {
      // 方向鍵轉視角用較小步距（5°）；WASD 移動另有較快固定速度。
      const angle = 5 * Math.PI / 180;
      state.viewer3d?.rotateView(k === 'arrowleft' ? angle : k === 'arrowright' ? -angle : 0,
        k === 'arrowup' ? angle : k === 'arrowdown' ? -angle : 0);
      for (const button of document.querySelectorAll('.vv')) {
        button.classList.remove('on'); button.setAttribute('aria-pressed', 'false');
      }
    } else {
      const step = 40;
      viewer.panBy(k === 'arrowleft' ? step : k === 'arrowright' ? -step : 0,
        k === 'arrowup' ? step : k === 'arrowdown' ? -step : 0);
    }
  }
});

/* 主題：**刻意固定淺色**，不提供切換。
   理由：這個工具是拿來看圖的，深色底會讓黑白線稿的對比判斷變得不確定；
   兩套主題也讓「引擎原生底色相反」這件事的組合數翻倍，容易看錯。
   ◑（反白圖面）保留，因為它調的是**畫布**而不是介面。 */

const reviewTools=new ReviewToolsUI({viewer:()=>isPageRfi()?documentRfi:state.viewer3d,item:activeItem,engine:resolvedEngine,flash:flashHint,begin:beginSurfaceTool});

linkedViewer=new LinkedViewer({state,primary:viewer,enter3D,flash:flashHint,begin:beginSurfaceTool,reviewTools,
  prepareInteraction:()=>{setAnnMode(false);documentRfi?.surfaceTools.setMode(null);state.viewer3d?.surfaceTools.setMode(null);},
  refresh3D:()=>{applyDisplay();sync3DTools();update3DInfo();}});

locationNavigation=new LocationNavigation({state,activeItem,openItem,enter2D,enter3D,exit3D,linked:()=>linkedViewer,showReviewPanel,flash:flashHint,engine:resolvedEngine,selectFolder:selectBrowse});
reviewTools.onSync=()=>locationNavigation.sync();
linkedViewer.onLayoutChange=()=>locationNavigation.sync();
locationNavigation.renderCatalog();locationNavigation.sync();

/* ── 除錯把手（給 e2e 測試用；與 v1/v2 的慣例一致）────────────── */
window.__dwgv3 = {
  get state() { return state; },
  get linkedViewer(){return linkedViewer;},
  get locationNavigation(){return locationNavigation;},
  get reviewTools(){return reviewTools;},
  get svgViewer(){return viewer;},
  get pdfViewer(){return pdfViewer;},
  get imageViewer(){return imageViewer;},
  openItem,
  closeModal,
  setSidebarPct,
  sidebarPct,
  setEngine,
  applyCanvasAppearance,
  enter3D,
  exit3D,
  setAnnMode,
  setAnnTool,
  saveAnnotations,
  annLayer,
  /** 'yes' | 'no' | 'unknown'：只讀圖庫狀態，不發請求。 */
  artifactState,
  /** 目前畫面上 SVG 的指紋，用來斷言「顯示的是哪一個檔案」。 */
  canvasFingerprint() {
    const svg = viewer.svg;
    if (!svg) return null;
    const html = svg.outerHTML;
    let h = 0;
    for (let i = 0; i < html.length; i++) h = (h * 31 + html.charCodeAt(i)) | 0;
    return { len: html.length, hash: (h >>> 0).toString(16), title: $('#mTitle').textContent,
             engine: state.currentEngine, invert: state.invert,
             paper: $('#canvas').classList.contains('paper') };
  },
};

function syncPermissionUI(){
  const auth=window.dcmsAuth;if(!auth)return;
  const item=activeItem(),location={projectId:state.selectedProject==='all'||state.selectedProject==='unassigned'?null:state.selectedProject,folderId:state.selectedFolder};
  const map={folderAdd:[location.projectId?'folder.create':'project.create',location],crumbFolderAdd:[location.projectId?'folder.create':'project.create',location],folderRename:[state.selectedFolder?'folder.rename':'project.rename',location],folderMove:['folder.move',location],folderDelete:[state.selectedFolder?'folder.purge':'project.purge',location],clearBtn:['library.clear',location],crumbConvert:['conversion.batch',location],convertRetryAllFailed:['conversion.batch',location],v3dNoteAdd:['rfi.create',item],v3dCalibrate:['review.calibration.edit',item],v3dCalibrationReset:['review.calibration.edit',item],v3dFloorAdd:['review.floors.edit',item],v3dFloorDelete:['review.floors.edit',item],v3dObjectsRebuild:['conversion.request',item],viewpointAdd:['viewpoint.create',item]};
  for(const [id,[action,resource]]of Object.entries(map)){const node=document.getElementById(id);if(node&&!auth.can(action,resource))node.disabled=true;}
}
const permissionObserver=new MutationObserver(syncPermissionUI);
permissionObserver.observe(document.querySelector('.app-shell'),{childList:true,subtree:true});

/* ── 啟動 ───────────────────────────────────────────────────── */
(async function boot() {
  await loadEngines();
  await refreshLibrary();
})();
